import * as Sentry from '@sentry/react';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useDragControls, animate } from 'motion/react';
import { 
  Home as HomeIcon, 
  Compass, 
  Heart, 
  User, 
  Scan, 

  ArrowLeft, 
  Share2, 
  Star, 
  MapPin, 
  Calendar, 
  ChevronRight,
  ChevronLeft,
  Rocket,
  Headphones,
  Pause,
  Play,
  BookOpen,
  Bookmark,
  Search,
  Gift,
  CheckCircle2,
  X,
  Check,
  Edit2,
  Send,
  Navigation,
  ChevronDown,
  Box,
  Zap,
  Sparkles,
  Smartphone,
  Eye,
  EyeOff,
  Loader2
} from 'lucide-react';
import comeniusLogo from './assets/images/comenius-mobile-logo-white.png';
import comeniusIcon from './assets/images/comenius-mobile-icon-white.png';
import { VisitorDesktopLanding } from './components/VisitorDesktopLanding';
import PrelaunchScreen from './components/PrelaunchScreen';
import { isPrelaunchAdminEmail, isPrelaunchAdminUser, isQrEntry } from './lib/prelaunch';
import { db, auth, functions } from './lib/firebase';
import { collection, getDocs, doc, getDoc, query, where, documentId, setDoc, addDoc, serverTimestamp, deleteDoc, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInAnonymously,
  linkWithPopup,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  deleteUser,
  updateProfile
} from 'firebase/auth';
import { getOrderedNodes } from './lib/storyboard';
import { useExperienceReward } from './hooks/useExperienceReward';
import ExpandableRewardCard from './components/ExpandableRewardCard';
import { getRank, getNextRank, getRankProgress } from './lib/rankCalculator';
import RankCard from './components/RankCard';
import RankDetailSheet from './components/RankDetailSheet';
const AIGuideScreen = React.lazy(() => import('./components/AIGuideScreen'));

function SentryErrorFallback({ error, resetError }: { error: Error; componentStack: string | null; resetError(): void }) {
  return (
    <div className="min-h-screen bg-bg-dark text-red-400 p-10 font-mono flex flex-col justify-center items-center">
      <div className="max-w-md bg-bg-card border border-red-500/20 p-8 rounded-2xl shadow-2xl">
        <h1 className="text-xl font-bold mb-4 flex items-center gap-2">⚠️ Something went wrong</h1>
        <p className="text-sm text-on-surface-variant mb-6">An unexpected error occurred. This has been reported.</p>
        <pre className="bg-black/50 p-4 rounded-lg overflow-x-auto text-xs text-red-300 border border-white/5 mb-6 whitespace-pre-wrap">
          {error?.toString()}
        </pre>
        <div className="flex gap-3">
          <button onClick={() => window.location.reload()} className="flex-1 px-6 py-3 bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-500/30 rounded-xl text-sm font-bold transition-all">Reload App</button>
          <button onClick={resetError} className="flex-1 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-200 border border-slate-600 rounded-xl text-sm font-bold transition-all">Try Again</button>
        </div>
      </div>
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

import { proxyTTS } from './lib/geminiProxy';

// --- Types ---
type Screen = 'splash' | 'auth' | 'prelaunch' | 'home' | 'museum_detail' | 'experience' | 'scanner' | 'mylist' | 'profile' | 'itinerary' | 'tour_object' | 'discover' | 'reward_redeem' | 'ai_guide' | 'audio_player';

type Museum = {
  id: string;
  name: string;
  location: string;
  city: string;
  country: string;
  image: string;
  coverImageUrl?: string;
  rating: number;
  counts: { ar: number; route: number; audio: number };
  isFavorite: boolean;
  createdAt: string;
  countryCode: string;
  lat: number;
  lon: number;
  distance?: number;
};
// --- Helpers ---
const renderMuseumName = (name: string) => name;

// --- Firestore-based Data (loaded from Firebase) ---
let MUSEUMS: Museum[] = [];
let MUSEUM_EXPERIENCES: Record<string, any[]> = {};
let EXPERIENCES: any[] = [];

// --- Components ---

const SplashScreen = ({ onFinish }: { onFinish: () => void }) => {
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFading(true);
      setTimeout(onFinish, 400);
    }, 2500);
    return () => clearTimeout(timer);
  }, [onFinish]);

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: fading ? 0 : 1 }}
      transition={{ duration: 0.4 }}
      className="absolute inset-0 bg-bg-dark flex flex-col items-center justify-center z-[9999]"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1, ease: "easeOut" }}
        className="flex flex-col items-center"
      >
        <img 
          src={comeniusIcon} 
          alt="Comenius Icon" 
          className="h-[120px] w-auto object-contain"
          referrerPolicy="no-referrer"
          fetchPriority="high"
        />
      </motion.div>
    </motion.div>
  );
};

const AuthScreen = ({
  onGoogleSuccess,
}: {
  onGoogleSuccess?: (user: any) => Promise<void>;
}) => {
  const [isSignIn, setIsSignIn] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (isSignIn) {
        // Sign In using Firebase Auth
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        // Sign Up using Firebase Auth
        if (!firstName || !lastName || !country) {
          throw new Error("Please fill in all fields.");
        }
        
        // Save pending first name for onAuthStateChanged listener
        try {
          localStorage.setItem('pending_firstName', firstName);
        } catch (e) {
          console.warn("Could not save pending_firstName to localStorage:", e);
        }

        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        
        // Pre-launch: only allowlisted admins keep an account. The auth
        // listener signs out any non-admin session right after auth; skip
        // the profile doc for blocked sign-ups so no junk user docs land.
        if (!isPrelaunchAdminEmail(email)) {
          return;
        }
        // Save user details to Firestore
        await setDoc(doc(db, 'users', user.uid), {
          firstName,
          lastName,
          email,
          country,
          createdAt: new Date().toISOString()
        });
      }
    } catch (err: any) {
      // Remove pending first name on error
      try {
        localStorage.removeItem('pending_firstName');
      } catch (e) {}
      
      console.error("Auth error:", err);
      let msg = err.message;
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        msg = "Invalid email or password.";
      } else if (err.code === 'auth/email-already-in-use') {
        msg = "This email is already registered.";
      } else if (err.code === 'auth/weak-password') {
        msg = "Password should be at least 6 characters.";
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError("");
    setLoading(true);
    localStorage.setItem('oauth_in_flight', '1');
    try {
      const provider = new GoogleAuthProvider();

      let user = auth.currentUser;
      const prevAnonUid = user?.isAnonymous ? user.uid : localStorage.getItem('analytics_user_id') || undefined;

      try {
        // Popup-primary on all platforms — most reliable across iOS Safari, Chrome, Arc
        if (user && user.isAnonymous) {
          const res = await linkWithPopup(user, provider);
          user = res.user;
        } else {
          const res = await signInWithPopup(auth, provider);
          user = res.user;
        }
      } catch (popupErr: any) {
        if (popupErr.code === 'auth/credential-already-in-use') {
          const res = await signInWithPopup(auth, provider);
          user = res.user;
        } else if (popupErr.code === 'auth/popup-blocked' || popupErr.code === 'auth/cancelled-popup-request') {
          // Fallback to redirect only when popup is explicitly blocked
          localStorage.setItem('oauth_in_flight', '1');
          await signInWithRedirect(auth, provider);
          return;
        } else {
          throw popupErr;
        }
      }

      if (user) {
        // Handled by parent (App scope): AuthScreen is module-scope and cannot
        // touch App state directly (that was a runtime crash + tsc error).
        await onGoogleSuccess?.(user);
      }
    } catch (err: any) {
      console.error("Google login error:", err);
      let msg = err.message;
      if (err.code === 'auth/operation-not-allowed') {
        msg = "Google login is not enabled in Firebase Console. Please enable it in Authentication > Sign-in method.";
      }
      setError(msg);
    } finally {
      setTimeout(() => localStorage.removeItem('oauth_in_flight'), 3000);
      setLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-bg-dark px-6 pt-8 pb-10 overflow-y-auto no-scrollbar">
      <div className="flex justify-center mb-6">
        <img 
          src={comeniusLogo} 
          alt="Comenius Logo" 
          className="h-[250px] w-auto object-contain"
          referrerPolicy="no-referrer"
          fetchPriority="high"
        />
      </div>

      <div className="flex border-b border-border-dark mb-6">
        <button 
          onClick={() => {
            setIsSignIn(true);
            setError("");
          }}
          className={`flex-1 pb-4 text-sm font-bold uppercase tracking-widest transition-colors ${isSignIn ? 'text-slate-100 border-b-2 border-primary' : 'text-slate-500'}`}
        >
          Sign In
        </button>
        <button 
          onClick={() => {
            setIsSignIn(false);
            setError("");
          }}
          className={`flex-1 pb-4 text-sm font-bold uppercase tracking-widest transition-colors ${!isSignIn ? 'text-slate-100 border-b-2 border-primary' : 'text-slate-500'}`}
        >
          Sign Up
        </button>
      </div>

      <form onSubmit={handleAuth} className="flex-1 flex flex-col">
        <div className="space-y-4">
          {error && (
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-xs font-semibold">
              {error}
            </div>
          )}

          {!isSignIn && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">First Name</label>
                  <input 
                    type="text" 
                    placeholder="John" 
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="w-full h-14 bg-surface-dark border border-border-dark rounded-xl px-4 text-slate-100 focus:outline-none focus:border-primary transition-colors text-sm font-medium" 
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Last Name</label>
                  <input 
                    type="text" 
                    placeholder="Doe" 
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className="w-full h-14 bg-surface-dark border border-border-dark rounded-xl px-4 text-slate-100 focus:outline-none focus:border-primary transition-colors text-sm font-medium" 
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Country / Region</label>
                <div className="relative">
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    required
                    className="w-full h-14 bg-surface-dark border border-border-dark rounded-xl px-4 pr-10 text-slate-100 focus:outline-none focus:border-primary transition-colors appearance-none text-sm font-medium"
                  >
                    <option value="">Select Country</option>
                    <option value="UK">United Kingdom (UK)</option>
                    <option value="USA">United States (USA)</option>
                    <option value="EU">European Union (EU)</option>
                    <option value="Asia">Asia (Turkey, etc.)</option>
                  </select>
                  <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                </div>
              </div>
            </>
          )}

          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
            <input 
              type="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john.doe@gmail.com" 
              required
              className="w-full h-14 bg-surface-dark border border-border-dark rounded-xl px-4 text-slate-100 focus:outline-none focus:border-primary transition-colors text-sm font-medium" 
            />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Password</label>
            <div className="relative">
              <input 
                type={showPassword ? "text" : "password"} 
                placeholder="••••••••" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full h-14 bg-surface-dark border border-border-dark rounded-xl px-4 pr-12 text-slate-100 focus:outline-none focus:border-primary transition-colors text-sm font-medium" 
              />
              <button 
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500"
              >
                {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
              </button>
            </div>
            {!isSignIn && (
              <p className="text-[10px] text-slate-500 mt-1 ml-1">
                Minimum 6 characters
              </p>
            )}
          </div>

          {isSignIn && (
            <div className="flex justify-end">
              <button type="button" className="text-primary text-sm font-medium">Forgot password?</button>
            </div>
          )}
        </div>

        <div className="mt-auto pt-8 space-y-6">
          <button 
            type="submit"
            disabled={loading}
            className="w-full h-14 bg-primary text-bg-dark font-bold rounded-xl shadow-lg shadow-primary/20 active:scale-[0.98] transition-all uppercase tracking-widest disabled:opacity-50 flex items-center justify-center text-sm"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-bg-dark border-t-transparent rounded-full animate-spin"></div>
            ) : (
              isSignIn ? 'Enter the Museum' : 'Join to Discover'
            )}
          </button>

          <div className="flex items-center">
            <div className="flex-1 h-[1px] bg-border-dark"></div>
            <span className="px-4 text-xs text-slate-500 font-medium uppercase tracking-widest">Or continue with</span>
            <div className="flex-1 h-[1px] bg-border-dark"></div>
          </div>

          <div className="flex justify-center w-full">
            <button 
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full h-14 glassmorphism rounded-xl flex items-center justify-center gap-3 font-bold text-xs disabled:opacity-50 uppercase tracking-widest"
            >
              <img src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-5 h-5" alt="Google" referrerPolicy="no-referrer" />
              GOOGLE
            </button>
          </div>
        </div>
      </form>

      <p className="mt-8 text-center text-[10px] text-slate-500 leading-relaxed">
        By continuing, you agree to our <br />
        <span className="text-primary underline">Terms of Service</span> and <span className="text-primary underline">Privacy Policy</span>
      </p>
    </div>
  );
};

const Navbar = ({ current, setScreen }: { current: Screen, setScreen: (s: Screen) => void, key?: string }) => {
  const items = [
    { id: 'home', icon: HomeIcon, label: 'HOME' },
    { id: 'discover', icon: Compass, label: 'DISCOVER' },
    { id: 'scanner', icon: Scan, label: 'SCAN QR', isCenter: true },
    { id: 'mylist', icon: Heart, label: 'MY LIST' },
    { id: 'profile', icon: User, label: 'PROFILE' },
  ].filter(item => !item.isCenter);

  return (
    <motion.nav 
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className="absolute bottom-0 left-0 right-0 z-40 bg-[#13213b] border-t border-white/10 rounded-t-3xl flex flex-col justify-center shadow-[0_-10px_30px_rgba(0,0,0,0.5)]"
      style={{ 
        paddingTop: '12px',
        paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))'
      }}
    >
      <div className="flex justify-around items-center px-4 h-16">
        {items.map((item) => (
          item.isCenter ? (
            <button
              key={item.id}
              onClick={() => setScreen('scanner')}
              aria-label="Scan QR Code"
              aria-current={current === 'scanner' ? 'page' : undefined}
              className="flex flex-col items-center gap-1 transition-all duration-300 group focus-ring touch-target rounded-full"
            >
              <div className="h-9 flex items-center justify-center">
                <div className={`w-11 h-11 rounded-full bg-gradient-to-tr from-primary to-[#ecd4a1] text-bg-dark flex items-center justify-center shadow-lg transition-transform group-active:scale-90 ${current === 'scanner' ? 'ring-2 ring-primary ring-offset-2 ring-offset-[#13213b]' : ''}`}>
                  <Scan size={26} />
                </div>
              </div>
              <span className={`text-xs font-bold uppercase tracking-wider ${current === 'scanner' ? 'text-primary' : 'text-slate-300'}`}>SCAN QR</span>
            </button>
          ) : (
            <button
              key={item.id}
              onClick={() => setScreen(item.id as Screen)}
              aria-label={item.label}
              aria-current={current === item.id ? 'page' : undefined}
              className={`flex flex-col items-center gap-1 transition-all duration-300 focus-ring touch-target rounded-xl ${current === item.id ? 'text-primary' : 'text-slate-300 hover:text-white'}`}
            >
              <div className="h-6 flex items-center justify-center">
                <item.icon size={20} />
              </div>
              <span className="text-xs font-bold uppercase tracking-wider">{item.label}</span>
            </button>
          )
        ))}
      </div>
    </motion.nav>
  );
};


const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371; // Radius of the earth in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c; // Distance in km
};


const HomeScreen = ({ 
  onSelectMuseum, 
  favorites, 
  toggleFavorite, 
  onProfileClick, 
  avatarUrl, 
  userName,
  activeTab,
  setActiveTab,
  userCoords,
  setUserCoords,
  userCountry,
  setUserCountry,
  isLocating,
  setIsLocating,
  locationError,
  setLocationError,
  triggerGeolocation,
  userRank
}: { 
  onSelectMuseum: (id: string) => void, 
  favorites: string[], 
  toggleFavorite: (id: string) => void, 
  onProfileClick: () => void, 
  avatarUrl: string, 
  userName: string,
  activeTab: 'newly' | 'popular' | 'nearby',
  setActiveTab: (tab: 'newly' | 'popular' | 'nearby') => void,
  userCoords: { lat: number; lon: number } | null,
  setUserCoords: (coords: { lat: number; lon: number } | null) => void,
  userCountry: string | null,
  setUserCountry: (country: string | null) => void,
  isLocating: boolean,
  setIsLocating: (val: boolean) => void,
  locationError: string | null,
  setLocationError: (val: string | null) => void,
  triggerGeolocation: (onSuccess?: (coords: { lat: number; lon: number }, countryCode: string) => void) => void,
  userRank?: { title: string; emoji: string; color: string }
}) => {
  const rank = userRank || { title: 'Explorer', emoji: '🔍', color: '#8B8B8B' };

  const getMuseumRating = (museumId: string, baseRating: number) => {
    const exps = MUSEUM_EXPERIENCES[museumId] || [];
    if (exps.length === 0) return baseRating;
    const sum = exps.reduce((acc, exp) => acc + (exp.rating || 4.5), 0);
    return sum / exps.length;
  };

  const handleNearbyClick = () => {
    setActiveTab('nearby');
    triggerGeolocation();
  };

  let sortedMuseums = [...MUSEUMS];
  let showingFallback = false;

  if (activeTab === 'newly') {
    sortedMuseums.sort((a, b) => {
      const dateA = new Date(a.createdAt || '2026-01-01').getTime();
      const dateB = new Date(b.createdAt || '2026-01-01').getTime();
      return dateB - dateA; // newest first
    });
  } else if (activeTab === 'popular') {
    sortedMuseums.sort((a, b) => {
      const ratingA = getMuseumRating(a.id, a.rating || 0);
      const ratingB = getMuseumRating(b.id, b.rating || 0);
      return ratingB - ratingA; // highest avg exhibition rating first
    });
  } else if (activeTab === 'nearby') {
    if (userCoords) {
      const museumsWithDist = [...MUSEUMS].map(m => ({
        ...m,
        distance: calculateDistance(userCoords.lat, userCoords.lon, m.lat || 0, m.lon || 0)
      }));

      const countryFiltered = museumsWithDist.filter(m => m.countryCode === userCountry);
      if (countryFiltered.length > 0) {
        sortedMuseums = countryFiltered.sort((a, b) => a.distance - b.distance);
      } else {
        sortedMuseums = museumsWithDist.sort((a, b) => a.distance - b.distance);
        showingFallback = true;
      }
    }
  }

  // Publication gating (A+B): hide unpublished museums and museums with no live content.
  // Legacy museum docs without isPublished default to visible (backwards compatible).
  sortedMuseums = sortedMuseums.filter(m => {
    if ((m as any).isPublished === false) return false;
    const c = (m as any).counts || { ar: 0, route: 0, audio: 0 };
    return (c.ar || 0) + (c.route || 0) + (c.audio || 0) > 0;
  });

  return (
    <div className="bg-[#13213b] h-full flex flex-col overflow-hidden">
      <div className="relative h-[320px] overflow-hidden flex-shrink-0">
        <img 
          src="https://upload.wikimedia.org/wikipedia/commons/1/1a/William_Blake_-_Isaac_Newton_-_WGA02217.jpg" 
          alt="Hero" 
          className="w-full h-full object-cover opacity-60"
          referrerPolicy="no-referrer"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#13213b]/90 via-[#13213b]/45 to-[#13213b]"></div>
        <div className="absolute top-16 left-8 right-8 flex justify-between items-start">
          <div>
            <p className="text-primary text-[10px] font-bold uppercase tracking-[0.3em] mb-1">Welcome back</p>
            <h2 className="text-3xl font-serif font-bold text-white">{userName}</h2>
            <div className="flex items-center gap-1.5 mt-1">
              <span className="text-xs">{rank.emoji}</span>
              <span className="text-primary font-bold text-xs uppercase tracking-widest">{rank.title}</span>
            </div>
          </div>
          <div 
            onClick={onProfileClick}
            className="w-20 h-20 rounded-full border-2 border-primary/30 p-1 bg-[#13213b]/40 backdrop-blur-md cursor-pointer active:scale-95 transition-transform"
          >
            <img 
              src={avatarUrl} 
              className="w-full h-full rounded-full object-cover" 
              alt="Profile"
              referrerPolicy="no-referrer"
            />
          </div>
        </div>
        <div className="absolute bottom-16 left-8">
          <h3 className="text-white text-2xl font-serif font-bold tracking-tight">Your next journey</h3>
        </div>
      </div>

      <div className="px-8 -mt-8 relative z-10 flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between border-b border-white/10 mb-0 relative">
          <button 
            onClick={() => setActiveTab('newly')}
            className={`relative flex-1 pb-1 text-xs font-bold uppercase tracking-[0.2em] flex flex-col items-center leading-tight transition-colors duration-300 ${activeTab === 'newly' ? 'text-primary font-bold' : 'text-slate-500 hover:text-slate-300'}`}
          >
            <span>New</span>
            <span>&nbsp;</span>
            {activeTab === 'newly' && (
              <motion.div 
                layoutId="activeTabUnderline"
                className="absolute bottom-0 left-0 right-0 h-[3px] bg-primary rounded-t-full z-10"
                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
              />
            )}
          </button>
          <button 
            onClick={() => setActiveTab('popular')}
            className={`relative flex-1 pb-1 text-xs font-bold uppercase tracking-[0.2em] flex flex-col items-center leading-tight transition-colors duration-300 ${activeTab === 'popular' ? 'text-primary font-bold' : 'text-slate-500 hover:text-slate-300'}`}
          >
            <span>Popular</span>
            <span>&nbsp;</span>
            {activeTab === 'popular' && (
              <motion.div 
                layoutId="activeTabUnderline"
                className="absolute bottom-0 left-0 right-0 h-[3px] bg-primary rounded-t-full z-10"
                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
              />
            )}
          </button>
          <button 
            onClick={handleNearbyClick}
            className={`relative flex-1 pb-1 text-xs font-bold uppercase tracking-[0.2em] flex flex-col items-center leading-tight transition-colors duration-300 ${activeTab === 'nearby' ? 'text-primary font-bold' : 'text-slate-500 hover:text-slate-300'}`}
          >
            <span>Nearby</span>
            <span>&nbsp;</span>
            {activeTab === 'nearby' && (
              <motion.div 
                layoutId="activeTabUnderline"
                className="absolute bottom-0 left-0 right-0 h-[3px] bg-primary rounded-t-full z-10"
                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
              />
            )}
          </button>
        </div>

        <div className="relative flex-1 min-h-0">
          {/* Scrollable Container */}
          <div className="space-y-[18px] h-full overflow-y-auto no-scrollbar pb-32 pt-[18px]">
            {activeTab === 'nearby' && isLocating && (
              <div className="flex flex-col items-center justify-center py-20 text-center bg-[#15233D]/30 backdrop-blur-xl border border-white/5 rounded-[32px]">
                <div className="w-10 h-10 rounded-full border-2 border-primary border-t-transparent animate-spin mb-4"></div>
                <p className="text-white font-medium text-sm">Determining your location...</p>
                <p className="text-slate-500 text-xs mt-1">Please authorize location access when prompted.</p>
              </div>
            )}

            {activeTab === 'nearby' && locationError && !isLocating && (
              <div className="flex items-start gap-3 p-4 bg-orange-500/10 border border-orange-500/20 rounded-2xl mb-6">
                <MapPin size={18} className="text-primary shrink-0 mt-0.5" />
                <div className="text-left">
                  <p className="text-xs text-white/95 font-bold">Location Services Offline</p>
                  <p className="text-xs text-slate-300 leading-normal mt-0.5">
                    {locationError}
                  </p>
                  <button 
                    onClick={() => {
                      setUserCoords(null);
                      setLocationError(null);
                      handleNearbyClick();
                    }}
                    className="mt-2 text-[10px] font-bold text-primary uppercase tracking-wider hover:underline"
                  >
                    Retry Permission
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'nearby' && userCoords && !isLocating && !locationError && (
              showingFallback ? (
                <div className="flex items-center gap-3 p-4 bg-primary/10 border border-primary/20 rounded-2xl mb-6">
                  <Navigation size={18} className="text-primary shrink-0 animate-pulse" />
                  <p className="text-xs text-white/90 leading-normal text-left">
                    No museums found in <strong>{userCountry}</strong>. Showing closest institutions by distance instead.
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-4 bg-primary/10 border border-primary/25 rounded-2xl mb-6">
                  <CheckCircle2 size={18} className="text-primary shrink-0" />
                  <p className="text-xs text-white/90 leading-normal text-left">
                    Showing museums in <strong>{userCountry}</strong> sorted by distance.
                  </p>
                </div>
              )
            )}

            {!(activeTab === 'nearby' && isLocating) && sortedMuseums.map((museum) => {
              const isFavorited = favorites.includes(museum.id);
              return (
                <div 
                  key={museum.id} 
                  onClick={() => onSelectMuseum(museum.id)}
                  data-testid="museum-card"
                  className="group relative rounded-[32px] overflow-hidden bg-[#15233D]/50 backdrop-blur-xl border border-white/5 transition-all duration-500 hover:bg-[#15233D]/70 cursor-pointer"
                >
                  <div className="aspect-[16/11] w-full overflow-hidden relative">
                    <img src={museum.coverImageUrl || museum.image} alt={museum.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" referrerPolicy="no-referrer" loading="lazy" />
                    <div className="absolute top-5 left-5 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#13213b]/80 backdrop-blur-xl border border-white/10 shadow-2xl">
                      {museum.rating && museum.rating > 0 ? (
                        <>
                          <Star size={14} className="text-primary fill-primary" />
                          <span className="text-primary text-xs font-bold">{museum.rating}</span>
                        </>
                      ) : (
                        <>
                          <Sparkles size={14} className="text-primary" />
                          <span className="text-primary text-xs font-black tracking-wider uppercase">NEW</span>
                        </>
                      )}
                    </div>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(museum.id);
                      }}
                      className={`absolute top-5 right-5 size-12 rounded-full backdrop-blur-md flex items-center justify-center border border-white/10 transition-all duration-300 ${
                        isFavorited 
                          ? 'bg-primary/20 text-primary border-primary/30' 
                          : 'bg-[#13213b]/40 text-white hover:bg-primary/20'
                      }`}
                    >
                      <Heart size={24} className={isFavorited ? 'fill-primary' : ''} />
                    </button>
                    <div className="absolute bottom-5 left-5 flex flex-row gap-2">
                      {(museum.counts?.ar ?? 0) > 0 && (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#13213b]/80 backdrop-blur-xl border border-white/10 shadow-2xl">
                          <Smartphone size={11} className="text-primary" />
                          <span className="text-[10px] font-bold text-white uppercase tracking-wider">{museum.counts.ar} AR</span>
                        </div>
                      )}
                      {(museum.counts?.route ?? 0) > 0 && (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#13213b]/80 backdrop-blur-xl border border-white/10 shadow-2xl">
                          <MapPin size={11} className="text-primary" />
                          <span className="text-[10px] font-bold text-white uppercase tracking-wider">{museum.counts.route} ROUTE</span>
                        </div>
                      )}
                      {(museum.counts?.audio ?? 0) > 0 && (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#13213b]/80 backdrop-blur-xl border border-white/10 shadow-2xl">
                          <Headphones size={11} className="text-primary" />
                          <span className="text-[10px] font-bold text-white uppercase tracking-wider">{museum.counts.audio} AUDIO</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="p-7">
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <h4 className="text-white text-lg font-serif font-bold mb-2 leading-tight">{renderMuseumName(museum.name)}</h4>
                        <div className="flex items-center gap-2 text-slate-400 text-sm flex-wrap">
                          <MapPin size={14} className="text-primary/60" />
                          <span className="tracking-tight">{museum.city && museum.country ? `${museum.city}, ${museum.country}` : museum.location}</span>
                          {museum.distance !== undefined && (
                            <>
                              <span className="text-primary/40">•</span>
                              <span className="text-primary font-bold text-xs uppercase tracking-wider">{museum.distance.toFixed(1)} km away</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const MuseumDetailScreen = ({ museumId, onBack, onSelectExperience }: { museumId: string, onBack: () => void, onSelectExperience: (s: Screen, expId?: string) => void }) => {
  const [selectedTab, setSelectedTab] = useState<string>('all');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const foundMuseum = MUSEUMS.find(m => m.id === museumId);
  const museum = foundMuseum || (museumId ? { id: museumId, name: '', image: '', rating: 0, counts: { ar: 0, route: 0, audio: 0 }, isFavorite: false, location: '', city: '', country: '', createdAt: '', countryCode: '', lat: 0, lon: 0 } : (MUSEUMS[0] || { id: '', name: 'Museum', image: '', rating: 0, counts: { ar: 0, route: 0, audio: 0 }, isFavorite: false, location: '', city: '', country: '', createdAt: '', countryCode: '', lat: 0, lon: 0 }));

  if (museumId && !foundMuseum && MUSEUMS.length === 0) {
    return (
      <div className="h-full bg-[#13213b] flex flex-col justify-center items-center">
        <Loader2 size={32} className="text-primary animate-spin mb-3" />
        <p className="text-white/60 text-xs font-bold uppercase tracking-widest">Loading Museum…</p>
      </div>
    );
  }

  const experiences = MUSEUM_EXPERIENCES[museum.id] || [];

  const realExps = experiences.filter(exp => !exp.isDemo);
  const demoExps = experiences.filter(exp => exp.isDemo);

  const categories = Array.from(new Set(realExps.map(exp => exp.type).filter(Boolean))).sort();
  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'featured', label: 'Featured' },
    ...categories.map(type => ({
      id: type,
      label: type === 'immersive' ? 'AR Artefacts' : type === 'ar' ? 'AR Artefacts' : type === 'archive' ? 'Archive' : type === 'audio' ? 'Audio Guides' : type === 'route' ? 'Routes' : type.charAt(0).toUpperCase() + type.slice(1)
    }))
  ];

  const filteredExperiences = [...realExps, ...demoExps].filter(exp => {
    if (exp.status === 'unlisted') return false;
    if (selectedTab === 'featured') {
      return exp.isNew || exp.featured;
    }
    if (selectedTab === 'all') {
      return true;
    }
    return exp.type === selectedTab;
  });

  return (
    <div className="h-full bg-[#13213b] flex flex-col overflow-hidden relative">
      <div className="absolute top-0 left-0 w-full px-6 py-4 pt-10 z-50 flex justify-between items-center bg-transparent">
        <button onClick={onBack} className="flex items-center gap-2 text-white/90 hover:text-white active:scale-95 transition-all cursor-pointer">
          <ArrowLeft size={20} />
          <span className="text-xs font-bold tracking-widest uppercase">Back to home</span>
        </button>
      </div>

      <div className="relative h-[42vh] w-full bg-slate-900 overflow-hidden shrink-0">
        <div className="absolute inset-0 w-full h-full">
          <img 
            alt={museum.name} 
            className="w-full h-full object-cover object-center animate-none" 
            src={museum.coverImageUrl || museum.image}
            referrerPolicy="no-referrer"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-[#13213b]/20 mix-blend-multiply"></div>
        </div>

        <div className="absolute top-20 sm:top-24 left-0 w-full px-6 z-20">
          <div className="mt-4">
            <p className="text-primary text-[10px] font-bold tracking-[0.4em] uppercase mb-1">Welcome to</p>
            <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl text-white leading-[1.1] max-w-[90%]">{renderMuseumName(museum.name)}</h1>
            <p className="text-white/80 text-xs font-sans mt-2 text-left flex items-center gap-1.5 opacity-90"><MapPin size={12} className="text-primary shrink-0" /> {museum.city && museum.country ? `${museum.city}, ${museum.country}` : museum.location}</p>
          </div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#13213b] from-[20%] via-[#13213b]/70 to-[#13213b]/85 z-10"></div>
        {/* Bottom melt — solid below the panel edge (-mt-32 = 128px ≈ 57% of h-56), fading above it */}
        <div
          className="absolute bottom-0 left-0 right-0 h-56 z-[15] pointer-events-none"
          style={{ background: 'linear-gradient(to top, #13213b 0%, #13213b 62%, transparent 100%)' }}
        ></div>
      </div>

      <div className="flex-1 bg-[#13213b] -mt-32 relative z-30 flex flex-col overflow-hidden">
        {/* Fixed Title Section */}
        <div className="px-6 pt-2 pb-4 flex justify-between items-end shrink-0">
          <div>
            <h2 className="font-serif text-3xl text-white tracking-tight">Exhibitions</h2>
            <p className="text-white/40 text-xs mt-1 font-medium tracking-wide uppercase">CURATED AR ROUTES &amp; AUDIO TOURS</p>
          </div>
        </div>

        {/* Fixed Category Chips */}
        <div className="flex gap-3 px-6 overflow-x-auto no-scrollbar pb-4 relative z-10 shrink-0">
          {tabs.map(tab => {
            const isActive = selectedTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id)}
                className="relative whitespace-nowrap px-6 py-2.5 rounded-full text-xs focus:outline-none cursor-pointer"
              >
                {isActive && (
                  <motion.div
                    layoutId="activeDetailCategoryTab"
                    className="absolute inset-0 bg-primary rounded-full"
                    transition={{ type: 'spring', stiffness: 380, damping: 35 }}
                  />
                )}
                <div className={`absolute inset-0 rounded-full border transition-all duration-300 pointer-events-none ${
                  isActive 
                    ? 'bg-transparent border-transparent' 
                    : 'bg-white/5 border-white/10'
                }`} />
                <span className={`relative z-10 font-bold transition-colors duration-300 ${isActive ? 'text-[#13213b]' : 'text-slate-300'}`}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Scrollable Experiences Cards Wrapper */}
        <div className="relative flex-1 min-h-0">
          <div ref={scrollContainerRef} className="h-full overflow-y-auto no-scrollbar pb-32 min-h-0 pt-4">
            <div className="flex flex-col gap-5 px-6">
              {filteredExperiences.length === 0 ? (
                <div className="text-center py-16 text-slate-500 text-sm font-semibold tracking-wide uppercase">
                  No experiences found in this category
                </div>
              ) : (
                filteredExperiences.map((exp, idx) => (
                  <div 
                    key={exp.id} 
                    data-testid="experience-card"
                    onClick={() => {
                      onSelectExperience(
                        exp.status !== 'live' ? 'experience' : 'itinerary',
                        exp.id
                      );
                    }}
                    className="relative group rounded-3xl overflow-hidden bg-[#112240]/40 border border-white/[0.08] shadow-2xl cursor-pointer active:scale-[0.98] transition-transform focus-ring"
                  >
                    <div className={`flex h-[190px] ${idx % 2 === 1 ? 'flex-row-reverse' : ''}`}>
                      <div className="w-[38%] bg-slate-900 relative overflow-hidden shrink-0">
                        <img src={exp.image} className="w-full h-full object-cover" alt={exp.title} referrerPolicy="no-referrer" loading="lazy" />
                        {exp.isDemo && (
                          <div className="absolute top-3 left-3">
                            <div className="bg-slate-600/90 text-white px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider">Demo</div>
                          </div>
                        )}
                        {exp.isNew && !exp.isDemo && (
                          <div className="absolute top-3 left-3">
                            <div className="bg-primary/90 text-[#13213b] px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider">New</div>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 p-5 flex flex-col justify-between min-w-0">
                        <div>
                          <h3 className="font-serif text-lg text-white mb-1.5 leading-tight">{exp.title}</h3>
                          <p className="text-slate-300 text-xs leading-relaxed line-clamp-4">{exp.desc || exp.description}</p>
                        </div>
                        <div className="flex items-center gap-2 text-primary text-xs font-bold uppercase tracking-wider mt-3">
                          <span>{exp.type === 'audio' ? 'Listen' : exp.type === 'ar' || exp.type === 'immersive' ? 'Discover AR' : exp.type === 'archive' ? 'Browse' : 'Start Tour'}</span>
                          {exp.type === 'audio' ? <Headphones size={14} /> : exp.type === 'ar' ? <Smartphone size={14} /> : exp.type === 'archive' ? <BookOpen size={14} /> : <Rocket size={14} />}
                        </div>
                      </div>
                    </div>
                  </div>
                )))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ExperienceDetail = ({ experience, museumName, onBack, onStart, isCompleted, isInProgress }: { experience?: any, museumName: string, onBack: () => void, onStart: () => void, isCompleted?: boolean, isInProgress?: boolean }) => {
  const expType = experience?.type || 'immersive';
  const isLive = experience?.status === 'live';
  const ctaLabel = isCompleted
    ? 'START AGAIN'
    : isInProgress
      ? 'CONTINUE'
      : expType === 'audio' ? 'Listen' : expType === 'ar' || expType === 'immersive' ? 'Discover AR' : expType === 'archive' ? 'Browse' : 'Start Tour';
  const CtaIcon = expType === 'audio' ? Headphones : expType === 'ar' || expType === 'immersive' ? Smartphone : expType === 'archive' ? BookOpen : Rocket;
  const typeLabel = expType === 'audio' ? 'Audio Guide' : expType === 'ar' || expType === 'immersive' ? 'AR Artefact' : expType === 'route' ? 'Route' : expType === 'archive' ? 'Archive' : 'Exhibition';
  const title = experience?.title || 'Untitled Experience';
  const desc = experience?.desc || experience?.description || '';
  const image = experience?.image || '';

  return (
    <div className="h-full bg-[#0d1a2e] flex flex-col relative overflow-hidden">
      {/* Ambient glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[360px] h-[360px] rounded-full bg-[#faa31b]/8 blur-[80px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[280px] h-[280px] rounded-full bg-[#faa31b]/5 blur-[60px] pointer-events-none" />

      {/* Back button */}
      <div className="w-full px-6 pt-14 flex items-center z-10">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-white/60 hover:text-white active:scale-95 transition-all cursor-pointer"
        >
          <ArrowLeft size={18} />
          <span className="text-xs font-bold tracking-widest uppercase">Back</span>
        </button>
      </div>

      {/* Cover image */}
      <div className="relative flex-1 min-h-0 flex flex-col items-center justify-center px-8 text-center z-10">
        <div className="relative w-full max-w-[280px]">
          {image ? (
            <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl aspect-[3/4] bg-slate-900">
              <img src={image} alt={title} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d1a2e]/80 via-transparent to-transparent" />
            </div>
          ) : (
            <div className="relative w-24 h-24 mx-auto rounded-3xl bg-[#faa31b]/10 border border-[#faa31b]/20 flex items-center justify-center">
              <CtaIcon size={48} className="text-[#faa31b]" />
              <div className="absolute inset-0 rounded-3xl border border-[#faa31b]/20 animate-ping opacity-30" />
            </div>
          )}

          {/* Type badge */}
          <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/10">
            <CtaIcon size={12} className="text-[#faa31b]" />
            <span className="text-white text-xs font-bold uppercase tracking-wider">{typeLabel}</span>
          </div>
          {!isLive && (
            <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-700/90 backdrop-blur-md">
              <span className="text-white text-xs font-bold uppercase tracking-wider">Preview</span>
            </div>
          )}
        </div>

        {/* Museum label */}
        <p className="text-[#faa31b] text-[10px] font-bold uppercase tracking-[0.35em] mt-8 mb-3">
          {museumName || 'Museum'}
        </p>

        {/* Title */}
        <h1 className="font-serif text-4xl text-white leading-tight mb-5 tracking-tight">
          {title}
        </h1>

        {/* Divider */}
        <div className="w-12 h-px bg-[#faa31b]/30 mb-5" />

        {/* Description */}
        {desc ? (
          <p className="text-white/50 text-xs leading-relaxed max-w-[300px] font-medium line-clamp-6">{desc}</p>
        ) : (
          <p className="text-white/30 text-xs leading-relaxed max-w-[280px] font-medium">
            This experience has no description yet.
          </p>
        )}
      </div>

      {/* Bottom action */}
      <div className="w-full px-8 pb-16 z-10">
        <button
          onClick={onStart}
          data-testid="experience-start"
          className="w-full py-4 bg-[#faa31b] text-[#0d1a2e] font-bold text-xs uppercase tracking-[0.25em] rounded-2xl active:scale-95 transition-transform flex items-center justify-center gap-2"
        >
          <CtaIcon size={16} />
          {ctaLabel}
        </button>
      </div>
    </div>
  );
};

const AudioPlayerScreen = ({ experience, onBack, onComplete }: { experience?: any, onBack: () => void, onComplete?: () => void }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const fullBufferRef = useRef<AudioBuffer | null>(null);
  const playbackOffsetRef = useRef(0);
  const lastStartTimeRef = useRef(0);
  const animationFrameRef = useRef<number | null>(null);
  const isPlayingRef = useRef(false);

  const title = experience?.title || 'Audio Guide';
  const image = experience?.image || '';
  const desc = experience?.desc || experience?.description || '';
  const audioText = desc || title;
  const preloadedUrl = experience?.audioUrl || null;

  const updateProgress = () => {
    if (isPlayingRef.current && !isDragging && audioContextRef.current && fullBufferRef.current) {
      const elapsed = audioContextRef.current.currentTime - lastStartTimeRef.current;
      const currentPos = playbackOffsetRef.current + elapsed;
      const total = fullBufferRef.current.duration;

      if (currentPos >= total) {
        setIsPlaying(false);
        isPlayingRef.current = false;
        playbackOffsetRef.current = 0;
        setProgress(0);
        setElapsedTime(0);
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        if (onComplete) onComplete();
        return;
      }

      setProgress((currentPos / total) * 100);
      setElapsedTime(currentPos);
      animationFrameRef.current = requestAnimationFrame(updateProgress);
    }
  };

  useEffect(() => {
    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(updateProgress);
    } else {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    }
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [isPlaying]);

  const stopAudio = (resetOffset = true) => {
    if (sourceRef.current) {
      sourceRef.current.stop();
      sourceRef.current = null;
    }
    isPlayingRef.current = false;
    setIsPlaying(false);
    if (resetOffset) playbackOffsetRef.current = 0;
  };

  const playFromOffset = (offset: number) => {
    if (!fullBufferRef.current || !audioContextRef.current) return;
    const source = audioContextRef.current.createBufferSource();
    source.buffer = fullBufferRef.current;
    source.connect(audioContextRef.current.destination);
    source.start(0, offset);
    sourceRef.current = source;
    lastStartTimeRef.current = audioContextRef.current.currentTime;
    playbackOffsetRef.current = offset;
    isPlayingRef.current = true;
    setIsPlaying(true);
    source.onended = () => {
      if (isPlayingRef.current) {
        stopAudio(false);
      }
    };
  };

  const handleTTS = async () => {
    if (isPlaying) {
      stopAudio();
      return;
    }

    if (sourceRef.current && fullBufferRef.current) {
      playFromOffset(playbackOffsetRef.current);
      return;
    }

    if (preloadedUrl) {
      try {
        setIsGenerating(true);
        const response = await fetch(preloadedUrl);
        const arrayBuffer = await response.arrayBuffer();
        const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        audioContextRef.current = audioCtx;
        const decodedBuffer = await audioCtx.decodeAudioData(arrayBuffer);
        fullBufferRef.current = decodedBuffer;
        setDuration(decodedBuffer.duration);
        setIsGenerating(false);
        playFromOffset(playbackOffsetRef.current);
      } catch (err) {
        console.error("Error loading pre-recorded audio:", err);
        setIsGenerating(false);
        setIsPlaying(false);
      }
      return;
    }

    if (!audioText.trim()) {
      alert("This audio guide has no narration yet.");
      return;
    }

    if (isGenerating) return;

    try {
      setIsGenerating(true);
      const data = await proxyTTS(audioText, 'Charon');
      const base64Audio = data?.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
      setIsGenerating(false);

      if (base64Audio) {
        const binaryString = window.atob(base64Audio);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const pcmData = new Int16Array(bytes.buffer);

        const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        audioContextRef.current = audioCtx;
        const buffer = audioCtx.createBuffer(1, pcmData.length, 24000);
        const channelData = buffer.getChannelData(0);
        for (let i = 0; i < pcmData.length; i++) {
          channelData[i] = pcmData[i] / 32768;
        }
        fullBufferRef.current = buffer;
        setDuration(buffer.duration);
        playFromOffset(playbackOffsetRef.current);
      }
    } catch (error) {
      console.error("TTS Error:", error);
      setIsGenerating(false);
      setIsPlaying(false);
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    let clientX;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
    } else {
      clientX = e.clientX;
    }
    const x = clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));

    setProgress(percent * 100);
    setElapsedTime(fullBufferRef.current ? percent * fullBufferRef.current.duration : 0);

    if (fullBufferRef.current) {
      const newOffset = percent * fullBufferRef.current.duration;
      playbackOffsetRef.current = newOffset;
      playFromOffset(newOffset);
    } else {
      if (!isGenerating && audioText.trim()) {
        handleTTS();
      }
    }
  };

  useEffect(() => {
    return () => stopAudio();
  }, []);

  const formatTime = (sec: number) => {
    if (!fullBufferRef.current) return '0:00';
    return `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;
  };

  return (
    <div className="bg-[#13213b] flex flex-col overflow-hidden overscroll-none" style={{ height: 'calc(100dvh - env(safe-area-inset-top, 0px))' }}>
      <div className="flex flex-col shrink-0 overflow-hidden" style={{ height: '30dvh' }}>
        <header className="shrink-0 z-10 px-6 pt-6 pb-4 flex justify-between items-center bg-[#13213b] border-b border-white/5 touch-none">
          <button onClick={onBack} className="text-white/80 hover:text-white transition-colors cursor-pointer">
            <ChevronLeft size={28} />
          </button>
          <h2 className="text-primary text-[10px] font-bold uppercase tracking-[0.4em]">Audio Guide</h2>
          <button onClick={onBack} className="text-white/80 hover:text-white transition-colors cursor-pointer">
            <X size={28} />
          </button>
        </header>

        <div className="flex-1 min-h-0 bg-[#13213b] flex items-center justify-center px-12 py-4 touch-none">
          <div className="relative w-full h-full flex items-center justify-center">
            <div className="absolute inset-0 bg-primary/5 blur-[100px] rounded-full" />
            {image ? (
              <img
                src={image}
                alt={title}
                onClick={() => setIsImageModalOpen(true)}
                className="max-w-full max-h-full object-contain relative z-10 drop-shadow-[0_20px_50px_rgba(0,0,0,0.8)] cursor-pointer active:scale-95 transition-transform"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-24 h-24 rounded-3xl bg-primary/10 border border-primary/20 flex items-center justify-center relative z-10">
                <Headphones size={48} className="text-primary" />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden bg-gradient-to-b from-[#1A2B4D] to-[#15233D] rounded-t-[40px] border-t border-white/10 shadow-[0_-30px_60px_rgba(0,0,0,0.6)]">
        <div className="shrink-0 px-8 pt-8 pb-3 touch-none">
          <div className="flex justify-between items-start mb-5">
            <div className="flex-1 pr-4">
              <h3 className="font-serif italic text-3xl text-primary mb-1 tracking-tight">{title}</h3>
              <p className="text-primary/40 text-[10px] font-bold uppercase tracking-[0.2em]">Audio Guide</p>
            </div>
            <button
              onClick={handleTTS}
              disabled={isGenerating}
              className={`w-12 h-12 rounded-full border border-white/10 flex items-center justify-center transition-all active:scale-90 relative overflow-hidden shrink-0 cursor-pointer ${isPlaying ? 'bg-primary text-[#13213b]' : 'text-primary bg-white/5'} ${isGenerating ? 'opacity-50 cursor-wait' : ''}`}
            >
              {isGenerating && (
                <motion.div
                  initial={{ rotate: 0 }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0 border-2 border-primary/30 border-t-primary rounded-full"
                />
              )}
              {isGenerating ? (
                <Headphones size={20} className="opacity-20" />
              ) : isPlaying ? (
                <Pause size={20} fill="currentColor" />
              ) : (
                <Play size={20} fill="currentColor" className="ml-0.5" />
              )}
            </button>
          </div>

          <div>
            <div
              id="audio-seekbar"
              className="relative h-1.5 w-full bg-white/10 rounded-full cursor-pointer touch-none"
              onMouseDown={handleSeek}
              onTouchStart={handleSeek}
            >
              <div className="absolute top-0 left-0 h-full bg-primary rounded-full" style={{ width: `${progress}%` }} />
              <div
                className="absolute top-1/2 -translate-y-1/2 size-4 bg-primary rounded-full shadow-[0_0_10px_rgba(250,163,27,0.5)]"
                style={{ left: `calc(${progress}% - 8px)` }}
              />
            </div>
            <div className="flex justify-between mt-2">
              <span className="text-[10px] text-white/30 font-bold">{fullBufferRef.current ? formatTime(elapsedTime) : '0:00'}</span>
              <span className="text-[10px] text-white/30 font-bold">{fullBufferRef.current ? formatTime(duration) : '0:00'}</span>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-8 pb-10 no-scrollbar overscroll-none">
          {desc && (
            <p className="text-white/80 leading-relaxed text-sm">{desc}</p>
          )}
          {!desc && (
            <p className="text-white/40 text-sm leading-relaxed">This audio guide has no description yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};



const ItineraryScreen = ({ museumId, experienceId, dynamicItinerary, aboutText, onBack, onStartTour, toggleMyList, isInMyList, isTourInProgress, isCompleted, isLoading, detailOverride, linkedArSceneId, onRewardClick, progressInfo, isGuest, onRequireSignIn }: { museumId: string, experienceId: string, dynamicItinerary: any[], aboutText?: string, onBack: () => void, onStartTour: () => void, toggleMyList: () => void, isInMyList: boolean, isTourInProgress?: boolean, isCompleted?: boolean, isLoading?: boolean, detailOverride?: any, linkedArSceneId?: string | null, onRewardClick?: (reward: any) => void, progressInfo?: { currentItem: number; totalItems: number; completed: boolean; completedStops?: number[] }, isGuest?: boolean, onRequireSignIn?: () => void }) => {
  const exp = EXPERIENCES.find(e => e.id === experienceId);
  const expType = exp?.type || 'immersive';
  const isRoute = expType === 'route';
  const details = detailOverride || { title: '', description: '', image: '', minutes: '', objectsCount: '', about: '', itinerary: [] };
  const aboutContent = aboutText || details.about;
  const itinerary = dynamicItinerary && dynamicItinerary.length > 0 ? dynamicItinerary : details.itinerary;
  const objectsCountText = dynamicItinerary && dynamicItinerary.length > 0
    ? `${itinerary.length} Stop${itinerary.length !== 1 ? 's' : ''}`
    : details.objectsCount || (isRoute ? '...' : '');
  const badgeLabel = isRoute ? 'Route' : expType === 'audio' ? 'Audio Guide' : 'AR Artefact';
  const { rewardRule, isClaimed, userRewardDoc } = useExperienceReward(experienceId, museumId);
  const startLabel = isTourInProgress
    ? (isRoute ? 'CONTINUE THE TOUR' : 'CONTINUE EXPERIENCE')
    : isCompleted
      ? 'START AGAIN'
      : (isRoute ? 'START THE TOUR' : 'START EXPERIENCE');

  const completedStopsSet = useMemo(() => {
    const set = new Set<number>(progressInfo?.completedStops || []);
    if (isCompleted && itinerary) {
      itinerary.forEach((_, idx) => set.add(idx + 1));
    }
    return set;
  }, [progressInfo?.completedStops, isCompleted, itinerary]);

  // Warm the 8th Wall engine into the SW cache so Start Experience is instant
  useEffect(() => {
    const warm = (url: string) => fetch(url).catch(() => {});
    warm('/8thwall/xr.js');
    warm('/8thwall/xr-slam.js');
  }, []);

  return (
    <div data-testid="itinerary-screen" className="h-full bg-[#13213b] flex flex-col relative overflow-hidden">
      <main className="flex-1 flex flex-col relative overflow-hidden pb-[80px]">
        <div className="relative h-[350px] w-full shrink-0">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url("${details.image}")` }}>
            <div className="absolute inset-0 bg-gradient-to-t from-[#13213b] via-[#13213b]/40 to-[#13213b]/90"></div>
            <div className="absolute inset-0 bg-black/10"></div>
          </div>
          <div className="absolute top-0 left-0 w-full p-6 flex justify-between items-center z-50">
            <button 
              onClick={onBack} 
              className="w-10 h-10 rounded-full bg-[#13213b]/75 backdrop-blur-md border border-white/10 flex items-center justify-center text-white active:scale-90 hover:bg-white/10 transition-all cursor-pointer"
            >
              <ArrowLeft size={20} />
            </button>
            <button
              onClick={async () => {
                const url = `${window.location.origin}/marketplace?experience=${experienceId}&museum=${museumId}`;
                const shareData = { title: details.title, text: details.description, url };
                if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
                  try { await navigator.share(shareData); } catch {}
                } else {
                  try { await navigator.clipboard.writeText(url); } catch {}
                }
              }}
              className="w-10 h-10 rounded-full bg-[#13213b]/75 backdrop-blur-md border border-white/10 flex items-center justify-center text-white active:scale-90 hover:bg-white/10 transition-all cursor-pointer"
            >
              <Share2 size={20} />
            </button>
          </div>
          <div className="absolute bottom-0 left-0 w-full px-6 pb-[18px] pt-[20px] z-40 bg-gradient-to-t from-[#13213b] via-[#13213b]/80 to-transparent">
            <h1 className="font-serif text-5xl text-white leading-[1.1] mb-4 drop-shadow-2xl max-w-[300px]">{details.title}</h1>
            <div className="flex flex-nowrap items-center gap-1.5 overflow-x-auto no-scrollbar">
              <span className="bg-primary px-2.5 py-1 rounded text-[10px] text-[#13213b] uppercase font-bold tracking-wider whitespace-nowrap">{badgeLabel}</span>
              <span className="bg-black/60 backdrop-blur-md px-2.5 py-1 rounded text-[10px] text-white border border-white/20 uppercase font-medium tracking-wide whitespace-nowrap">{details.minutes}</span>
              <span className="bg-black/60 backdrop-blur-md px-2.5 py-1 rounded text-[10px] text-white border border-white/20 uppercase font-medium tracking-wide whitespace-nowrap">{objectsCountText}</span>
            </div>
          </div>
        </div>

        {/* Scrollable Container Wrapper */}
        <div className="flex-grow -mt-[48px] relative z-30 flex flex-col overflow-hidden bg-[#13213b] rounded-t-[32px] min-h-0">
          {/* Top Gradient Fade Overlay at the red line transition */}
          <div className="absolute top-0 left-0 right-0 h-[48px] bg-gradient-to-b from-[#13213b] via-[#13213b] via-[78px] to-transparent z-20 pointer-events-none" />

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto no-scrollbar px-6 pt-[48px] pb-[140px]">
            <section className="mb-6">
              <h2 className="font-serif text-2xl font-bold text-primary mb-5 italic">About this Exhibition</h2>
              <p className="text-white/90 leading-relaxed text-base whitespace-pre-line">
                {aboutContent}
              </p>
            </section>

            {rewardRule && (
              <ExpandableRewardCard
                rewardRule={rewardRule}
                isClaimed={isClaimed}
                userRewardDoc={userRewardDoc}
                onViewReward={onRewardClick}
              />
            )}

            {isGuest && onRequireSignIn && (
              <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-amber-500/15 via-primary/10 to-amber-500/5 border border-primary/30 flex items-center justify-between gap-3 shadow-lg">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-primary/20 flex items-center justify-center text-primary shrink-0">
                    <Bookmark size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-xs font-bold truncate">Save Progress & Badges</p>
                    <p className="text-white/70 text-[10px] leading-tight">Create a free account so your completed stops & rewards are never lost.</p>
                  </div>
                </div>
                <button
                  onClick={onRequireSignIn}
                  className="px-3 py-1.5 bg-primary text-[#13213b] text-[10px] font-black uppercase tracking-wider rounded-xl hover:scale-105 active:scale-95 transition-all shrink-0 cursor-pointer"
                >
                  Sign In
                </button>
              </div>
            )}

            {isRoute && (
              <section className="relative mb-8">
                <h2 className="font-serif text-2xl text-white mb-8">Tour Itinerary</h2>
                {isLoading ? (
                  <div className="space-y-8">
                    {[1, 2].map(i => (
                      <div key={i} className="flex gap-6 animate-pulse">
                        <div className="w-10 h-10 rounded-full bg-white/10 shrink-0" />
                        <div className="flex-1 space-y-3 pt-1">
                          <div className="h-4 bg-white/10 rounded w-2/3" />
                          <div className="flex gap-4">
                            <div className="w-20 h-20 rounded-lg bg-white/10 shrink-0" />
                            <div className="flex-1 space-y-2 pt-1">
                              <div className="h-3 bg-white/10 rounded w-full" />
                              <div className="h-3 bg-white/10 rounded w-5/6" />
                              <div className="h-3 bg-white/10 rounded w-4/6" />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-8 relative">
                    <div className="absolute left-[19px] top-10 bottom-0 w-[2px] bg-gradient-to-b from-primary via-primary/40 to-primary/10"></div>
                    {itinerary.map((step, idx) => {
                      const stepNo = typeof step.id === 'number' ? step.id : (idx + 1);
                      const isStepCompleted = isCompleted || completedStopsSet.has(stepNo) || (progressInfo?.currentItem ? stepNo < progressInfo.currentItem : false);
                      const isCurrentStep = !isCompleted && isTourInProgress && progressInfo?.currentItem === stepNo;
                      return (
                        <div key={step.id || idx} className="relative flex gap-6">
                          <div className="relative z-10 flex-shrink-0">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold border-4 border-[#13213b] transition-all duration-300 ${
                              isStepCompleted
                                ? 'bg-primary text-[#13213b] shadow-[0_0_15px_rgba(250,163,27,0.4)]'
                                : isCurrentStep
                                  ? 'bg-[#112240] border-primary text-primary animate-pulse'
                                  : 'bg-[#112240] border-primary/40 text-primary/70'
                            }`}>
                              {isStepCompleted ? <Check size={18} strokeWidth={3} /> : stepNo}
                            </div>
                          </div>
                          <div className="pb-2 w-full">
                            <div className="flex items-center gap-2 mb-3">
                              <h3 className="text-white font-bold text-xl">{step.title}</h3>
                              {isStepCompleted ? (
                                <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full flex items-center gap-1 shrink-0">
                                  <Check size={10} strokeWidth={3} /> COMPLETED
                                </span>
                              ) : isCurrentStep ? (
                                <span className="bg-primary/20 text-primary border border-primary/30 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0">
                                  CURRENT STOP
                                </span>
                              ) : null}
                            </div>
                            <div className="flex gap-4 items-start">
                              <div className="w-20 h-20 rounded-lg overflow-hidden shrink-0 border border-white/10 bg-slate-800/50">
                                <img alt={step.title} className="w-full h-full object-cover" src={step.img} referrerPolicy="no-referrer" loading="lazy" />
                              </div>
                              <p className="text-white/80 text-sm leading-relaxed mt-1">{
                                step.desc && step.desc.length > 150
                                  ? step.desc.slice(0, 150).trimEnd().replace(/\s\S*$/, '') + '…'
                                  : step.desc
                              }</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
      </main>

      <div className="absolute bottom-0 left-0 w-full z-40">
        <div className="absolute bottom-[80px] left-0 right-0 h-[120px] bg-gradient-to-t from-[#13213b] to-transparent pointer-events-none" />
        <button 
          onClick={onStartTour}
          disabled={isLoading}
          className="absolute bottom-[76px] left-6 right-6 h-14 bg-primary text-[#13213b] font-bold rounded-xl shadow-[0_4px_25px_rgba(250,163,27,0.3)] flex items-center justify-center gap-3 active:scale-[0.98] transition-all uppercase tracking-widest z-50 disabled:opacity-50 disabled:pointer-events-none"
        >
          <span className="tracking-widest uppercase text-base">
            {startLabel}
          </span>
        </button>
        <div className="absolute bottom-[40px] left-0 right-0 flex justify-center z-50">
          <button 
            onClick={toggleMyList} 
            className={`text-sm font-bold uppercase tracking-widest transition-colors flex items-center gap-2 ${isInMyList ? 'text-primary' : 'text-white/70 hover:text-white'}`}
          >
            {isInMyList ? <Check size={16} /> : null}
            {isInMyList ? 'SAVED TO LIST' : 'SAVE FOR LATER'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DiscoverScreen = ({ 
  onSelectExperience, 
  myListExhibitions, 
  toggleMyListExhibition,
  userCoords,
  userCountry,
  isLocating,
  triggerGeolocation
}: { 
  onSelectExperience: (museumId: string, experienceId: string) => void, 
  myListExhibitions: string[], 
  toggleMyListExhibition: (id: string) => void,
  userCoords: { lat: number; lon: number } | null,
  userCountry: string | null,
  isLocating: boolean,
  triggerGeolocation: (onSuccess?: (coords: { lat: number; lon: number }, countryCode: string) => void) => void
}) => {
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [isCountryListOpen, setIsCountryListOpen] = useState(false);
  const [isTypeListOpen, setIsTypeListOpen] = useState(false);
  const [isTimeListOpen, setIsTimeListOpen] = useState(false);
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const countries = ['UK', 'USA', 'EU', 'Asia'];
  const types = ['AR Artefact', 'Route', 'Audio Guide'];
  const times = ['< 30 MIN', '1 HOUR', '> 1 HOUR'];

  const mapCountryCodeToFilter = (code: string | null): string | null => {
    if (!code) return null;
    const upper = code.toUpperCase();
    if (upper === 'GB' || upper === 'UK') return 'UK';
    if (upper === 'US' || upper === 'USA') return 'USA';
    const euCodes = ['NL', 'FR', 'DE', 'IT', 'ES', 'BE', 'AT', 'CH', 'SE', 'NO', 'DK', 'FI', 'IE', 'PT', 'GR', 'PL'];
    if (euCodes.includes(upper)) return 'EU';
    const asiaCodes = ['TR', 'JP', 'CN', 'IN', 'KR', 'SG', 'MY', 'ID', 'TH', 'VN', 'PH'];
    if (asiaCodes.includes(upper)) return 'Asia';
    return null;
  };

  const handleCountrySelect = (country: string) => {
    setActiveTags(prev => {
      const filtered = selectedCountry ? prev.filter(t => t !== selectedCountry) : prev;
      return [country, ...filtered];
    });
    setSelectedCountry(country);
    setIsCountryListOpen(false);
  };

  const handleFindAroundMeClick = () => {
    triggerGeolocation((coords, countryCode) => {
      const filterCountry = mapCountryCodeToFilter(countryCode);
      if (filterCountry) {
        handleCountrySelect(filterCountry);
      }
    });
  };

  const handleTypeSelect = (type: string) => {
    setActiveTags(prev => {
      const filtered = selectedType ? prev.filter(t => t !== selectedType) : prev;
      return [...filtered, type];
    });
    setSelectedType(type);
    setIsTypeListOpen(false);
  };

  const handleTimeSelect = (time: string) => {
    setActiveTags(prev => {
      const filtered = selectedTime ? prev.filter(t => t !== selectedTime) : prev;
      return [...filtered, time];
    });
    setSelectedTime(time);
    setIsTimeListOpen(false);
  };

  const removeTag = (tagToRemove: string) => {
    setActiveTags(prev => prev.filter(tag => tag !== tagToRemove));
    if (tagToRemove === selectedCountry) setSelectedCountry(null);
    if (tagToRemove === selectedType) setSelectedType(null);
    if (tagToRemove === selectedTime) setSelectedTime(null);
  };

  // Filter Experiences (Exhibitions)
  const filteredExperiences = EXPERIENCES.filter(exp => {
    const museumId = exp.museumId || '';
    const museum = MUSEUMS.find(m => m.id === museumId);
    const museumName = museum?.name || '';
    
    // Search query matching
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const titleMatch = exp.title.toLowerCase().includes(q);
      const descMatch = (exp.description || exp.desc || '').toLowerCase().includes(q);
      const museumMatch = museumName.toLowerCase().includes(q);
      if (!titleMatch && !descMatch && !museumMatch) {
        return false;
      }
    }
    
    // Country / Region filter
    if (selectedCountry) {
      const location = (museum?.location || '').toLowerCase();
      const country = (museum?.country || '').toLowerCase();
      if (selectedCountry === 'UK') {
        if (!location.includes('uk') && !location.includes('london') && !location.includes('england') && !country.includes('uk') && !country.includes('united kingdom') && !country.includes('england')) return false;
      } else if (selectedCountry === 'USA') {
        if (!location.includes('usa') && !location.includes('us ') && !location.includes('united states') && !location.includes('america') && !country.includes('usa') && !country.includes('united states') && !country.includes('america')) return false;
      } else if (selectedCountry === 'EU') {
        const euCountries = ['netherlands', 'france', 'germany', 'italy', 'spain', 'belgium', 'amsterdam', 'paris', 'rome', 'berlin'];
        if (!euCountries.some(c => location.includes(c) || country.includes(c))) return false;
      } else if (selectedCountry === 'Asia') {
        const asiaCountries = ['turkey', 'japan', 'china', 'india', 'korea', 'istanbul', 'tokyo', 'asia'];
        if (!asiaCountries.some(c => location.includes(c) || country.includes(c))) return false;
      } else {
        if (!location.includes(selectedCountry.toLowerCase()) && !country.includes(selectedCountry.toLowerCase())) return false;
      }
    }
    
    // Type filter
    if (selectedType) {
      if (selectedType === 'AR Artefact') {
        if (exp.type !== 'immersive' && exp.type !== 'ar') return false;
      } else if (selectedType === 'Route') {
        if (exp.type !== 'route') return false;
      } else if (selectedType === 'Audio Guide') {
        if (exp.type !== 'audio') return false;
      }
    }
    
    // Time filter
    if (selectedTime) {
      let mins = 15;
      if (exp.id === 'hermetic_heritage') mins = 20;
      else if (exp.id === 'the_grand_library') mins = 60;
      else if (exp.id === 'founding_fathers') mins = 30;
      else if (exp.id === 'source_library') mins = 45;
      else if (exp.id === 'voices_of_free_thinkers') mins = 25;
      else if (exp.id === 'the_1900s_living_town') mins = 90;
      else if (exp.id === 'old_colliery_railway') mins = 40;
      else if (exp.id === 'agricultural_heritage_1950s') mins = 35;

      if (selectedTime === '< 30 MIN') {
        if (mins >= 30) return false;
      } else if (selectedTime === '1 HOUR') {
        if (mins < 30 || mins > 60) return false;
      } else if (selectedTime === '> 1 HOUR') {
        if (mins <= 60) return false;
      }
    }
    
    return true;
  });

  const displayExperiences = [...filteredExperiences];
  if (userCoords) {
    displayExperiences.sort((a, b) => {
      const musA = MUSEUMS.find(m => m.id === (a.museumId || ''));
      const musB = MUSEUMS.find(m => m.id === (b.museumId || ''));
      const distA = musA ? calculateDistance(userCoords.lat, userCoords.lon, musA.lat || 0, musA.lon || 0) : Infinity;
      const distB = musB ? calculateDistance(userCoords.lat, userCoords.lon, musB.lat || 0, musB.lon || 0) : Infinity;
      return distA - distB;
    });
  }

  return (
    <div className="h-full bg-[#13213b] flex flex-col overflow-hidden">
      {/* Click overlay to close filters when open */}
      {(isCountryListOpen || isTypeListOpen || isTimeListOpen) && (
        <div 
          className="fixed inset-0 z-30" 
          onClick={() => {
            setIsCountryListOpen(false);
            setIsTypeListOpen(false);
            setIsTimeListOpen(false);
          }}
        />
      )}

      {/* Hero Section */}
      <div className="relative h-[280px] w-full flex-shrink-0">
        <div 
          className="absolute inset-0 bg-cover bg-center" 
          style={{ backgroundImage: 'url("https://www.britishmuseumshoponline.org/media/catalog/product/3/8/38787001_europe_a_prophecy.jpg")' }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-[#13213b] via-[#13213b]/45 to-[#13213b]/90"></div>
        </div>
        
        <div className="absolute inset-0 flex flex-col justify-end px-8 pb-10">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl font-serif font-bold text-white mb-4"
          >
            Discover
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-white/80 text-lg max-w-[280px] leading-relaxed"
          >
            Uncover the rich history, and timeless wisdom of human knowledge.
          </motion.p>
        </div>
      </div>

      {/* Fixed Header Controls */}
      <div className="px-6 -mt-4 relative z-10 flex-shrink-0">
        
        {/* Search Row: 1 Input + 1 Button with 4px gap */}
        <div className="flex items-center gap-[4px] mb-4 w-full">
          {/* Search Input Container */}
          <div className="flex-1 relative h-16 transition-all duration-300">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-white/40" size={20} />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => {
                setTimeout(() => {
                  setIsSearchFocused(false);
                }, 200);
              }}
              className="w-full h-full bg-[#15233D]/50 border border-white/10 rounded-2xl pl-12 pr-10 text-white placeholder-white/40 focus:outline-none focus:border-primary transition-all duration-300 text-sm font-medium"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
              >
                <X size={18} />
              </button>
            )}
          </div>

          {/* Find Around Me Button */}
          <button 
            onClick={handleFindAroundMeClick}
            disabled={isLocating}
            className={`h-16 bg-primary rounded-2xl flex items-center justify-center shadow-[0_4px_20px_rgba(250,163,27,0.3)] active:scale-[0.98] transition-all duration-300 overflow-hidden shrink-0 ${
              isSearchFocused ? 'w-16' : 'w-[200px]'
            } ${isLocating ? 'opacity-80' : ''}`}
          >
            <div className="flex items-center justify-center gap-2">
              {isLocating ? (
                <div className="w-5 h-5 border-2 border-[#13213b] border-t-transparent rounded-full animate-spin shrink-0" />
              ) : (
                <Navigation size={20} className="text-[#13213b] fill-[#13213b] shrink-0" />
              )}
              <span 
                className={`text-[#13213b] font-bold uppercase tracking-widest text-xs whitespace-nowrap transition-all duration-300 ${
                  isSearchFocused ? 'opacity-0 w-0 scale-0 pointer-events-none' : 'opacity-100'
                }`}
              >
                {isLocating ? 'Locating...' : 'Find Around Me'}
              </span>
            </div>
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-3 relative z-40">
          {/* Country Filter */}
          <div className="flex-1 relative">
            <button 
              onClick={() => {
                setIsCountryListOpen(!isCountryListOpen);
                setIsTypeListOpen(false);
                setIsTimeListOpen(false);
              }}
              className={`w-full flex items-center justify-center gap-2 px-2 py-3 rounded-xl border transition-all duration-300 ${
                selectedCountry 
                  ? 'bg-primary border-primary text-[#13213b]' 
                  : 'bg-white/5 border-white/10 text-white/60'
              } text-sm font-medium whitespace-nowrap`}
            >
              Country
              <ChevronDown size={16} className={`transition-transform duration-300 ${isCountryListOpen ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {isCountryListOpen && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full left-0 w-full mt-2 bg-[#15233D] border border-white/10 rounded-xl overflow-hidden z-50 shadow-2xl"
                >
                  {countries.map((country) => (
                    <button 
                      key={country}
                      onClick={() => handleCountrySelect(country)}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors border-b border-white/5 last:border-0 ${
                        selectedCountry === country ? 'bg-primary/20 text-primary' : 'text-white/80 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      {country}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Type Filter */}
          <div className="flex-1 relative">
            <button 
              onClick={() => {
                setIsTypeListOpen(!isTypeListOpen);
                setIsCountryListOpen(false);
                setIsTimeListOpen(false);
              }}
              className={`w-full flex items-center justify-center gap-2 px-2 py-3 rounded-xl border transition-all duration-300 ${
                selectedType 
                  ? 'bg-primary border-primary text-[#13213b]' 
                  : 'bg-white/5 border-white/10 text-white/60'
              } text-sm font-medium whitespace-nowrap`}
            >
              Type
              <ChevronDown size={16} className={`transition-transform duration-300 ${isTypeListOpen ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {isTypeListOpen && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full left-0 w-full mt-2 bg-[#15233D] border border-white/10 rounded-xl overflow-hidden z-50 shadow-2xl"
                >
                  {types.map((type) => (
                    <button 
                      key={type}
                      onClick={() => handleTypeSelect(type)}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors border-b border-white/5 last:border-0 ${
                        selectedType === type ? 'bg-primary/20 text-primary' : 'text-white/80 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Time Filter */}
          <div className="flex-1 relative">
            <button 
              onClick={() => {
                setIsTimeListOpen(!isTimeListOpen);
                setIsCountryListOpen(false);
                setIsTypeListOpen(false);
              }}
              className={`w-full flex items-center justify-center gap-2 px-2 py-3 rounded-xl border transition-all duration-300 ${
                selectedTime 
                  ? 'bg-primary border-primary text-[#13213b]' 
                  : 'bg-white/5 border-white/10 text-white/60'
              } text-sm font-medium whitespace-nowrap`}
            >
              Time
              <ChevronDown size={16} className={`transition-transform duration-300 ${isTimeListOpen ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {isTimeListOpen && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full left-0 w-full mt-2 bg-[#15233D] border border-white/10 rounded-xl overflow-hidden z-50 shadow-2xl"
                >
                  {times.map((time) => (
                    <button 
                      key={time}
                      onClick={() => handleTimeSelect(time)}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors border-b border-white/5 last:border-0 ${
                        selectedTime === time ? 'bg-primary/20 text-primary' : 'text-white/80 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      {time}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Active Tags */}
        {activeTags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2 justify-start">
            {activeTags.map((tag) => (
              <div key={tag} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/10 text-white/80 text-[10px] font-bold uppercase tracking-widest whitespace-nowrap">
                {tag}
                <button onClick={() => removeTag(tag)}>
                  <X size={12} className="text-white/40 hover:text-white transition-colors" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scrollable Exhibition List Wrapper */}
      <div className="relative flex-1 min-h-0">
        <div className="h-full overflow-y-auto no-scrollbar pb-32 px-6 pt-4">
          {displayExperiences.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Compass size={48} className="text-white/20 mb-4 animate-pulse" />
              <p className="text-white/40 text-sm">No exhibitions found matching your criteria.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {displayExperiences.map((exp) => {
                const isFavorited = myListExhibitions.includes(exp.id);
                const museumId = exp.museumId || '';
                const museum = MUSEUMS.find(m => m.id === museumId);
                
                let TypeIcon = Compass;
                if (exp.type === 'audio') TypeIcon = Headphones;
                else if (exp.type === 'route') TypeIcon = MapPin;
                else if (exp.type === 'ar' || exp.type === 'immersive') TypeIcon = Smartphone;

                return (
                  <motion.div 
                    key={exp.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    onClick={() => onSelectExperience(museumId, exp.id)}
                    className="group relative rounded-[32px] overflow-hidden bg-[#15233D]/50 backdrop-blur-xl border border-white/5 transition-all duration-500 hover:bg-[#15233D]/70 cursor-pointer shadow-2xl"
                  >
                    <div className="aspect-[16/11] w-full overflow-hidden relative">
                      <img src={exp.image} alt={exp.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" referrerPolicy="no-referrer" loading="lazy" />
                      
                      {/* Rating & Distance Badges */}
                      <div className="absolute top-5 left-5 flex items-center gap-2">
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#13213b]/80 backdrop-blur-xl border border-white/10 shadow-2xl">
                          {museum?.rating && museum.rating > 0 ? (
                            <>
                              <Star size={14} className="text-primary fill-primary" />
                              <span className="text-primary text-xs font-bold">{museum.rating}</span>
                            </>
                          ) : (
                            <>
                              <Sparkles size={14} className="text-primary" />
                              <span className="text-primary text-xs font-black tracking-wider uppercase">NEW</span>
                            </>
                          )}
                        </div>
                        {userCoords && museum && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-[#13213b] font-bold text-[10px] uppercase tracking-wider shadow-2xl">
                            <Navigation size={10} className="fill-[#13213b]" />
                            <span>{calculateDistance(userCoords.lat, userCoords.lon, museum.lat || 0, museum.lon || 0).toFixed(1)} km</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Favorite Button */}
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleMyListExhibition(exp.id);
                        }}
                        className={`absolute top-5 right-5 size-12 rounded-full backdrop-blur-md flex items-center justify-center border border-white/10 transition-all duration-300 ${
                          isFavorited 
                            ? 'bg-primary/20 text-primary border-primary/30' 
                            : 'bg-[#13213b]/40 text-white hover:bg-primary/20'
                        }`}
                      >
                        <Heart size={24} className={isFavorited ? 'fill-primary' : ''} />
                      </button>
                      
                      {/* Immersive Badge */}
                      <div className="absolute bottom-5 left-5 flex flex-row gap-2">
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#13213b]/80 backdrop-blur-xl border border-white/10 shadow-2xl">
                          <TypeIcon size={12} className="text-primary" />
                          <span className="text-[10px] font-bold text-white uppercase tracking-wider">{exp.type === 'immersive' || exp.type === 'ar' ? 'AR Artefact' : exp.type === 'route' ? 'Route' : exp.type === 'audio' ? 'Audio Guide' : exp.type}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-7">
                      <h4 className="text-white text-lg font-serif font-bold mb-2 leading-tight">{exp.title}</h4>
                      <p className="text-white/60 text-sm mb-4 line-clamp-2">{exp.description || exp.desc}</p>
                      
                      <div className="border-t border-white/5 pt-4">
                        <div className="text-slate-400 text-xs font-semibold mb-1 uppercase tracking-wider">{museum?.name}</div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5 text-slate-500 text-xs">
                            <MapPin size={12} className="text-primary/60 shrink-0" />
                            <span className="truncate max-w-[200px]">{museum?.city && museum?.country ? `${museum.city}, ${museum.country}` : museum?.location}</span>
                          </div>
                          <ChevronRight size={16} className="text-primary transition-transform duration-300 group-hover:translate-x-1" />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ScannerScreen = ({ onBack, onScanSuccess }: { onBack: () => void, onScanSuccess: (itemId: string, itemName: string) => void }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [showInstructions, setShowInstructions] = useState(true);
  const [isFlashOn, setIsFlashOn] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);

  // Simulate scan success
  useEffect(() => {
    const timer = setTimeout(() => {
      const items = [
        { id: '1', name: "Robert Fludd's Head" },
        { id: '2', name: "Spinoza's Lens" },
        { id: '3', name: "The Pansophic Map" }
      ];
      const randomItem = items[Math.floor(Math.random() * items.length)];
      onScanSuccess(randomItem.id, randomItem.name);
    }, 2500);
    return () => clearTimeout(timer);
  }, [onScanSuccess]);

  // Callback ref is the most reliable way to attach a stream 
  // immediately when the element mounts in React
  const setVideoRef = useCallback((node: HTMLVideoElement | null) => {
    if (node && streamRef.current) {
      node.srcObject = streamRef.current;
      node.setAttribute('playsinline', 'true');
      node.setAttribute('webkit-playsinline', 'true');
      // Force disable PiP and AirPlay to prevent system recording triggers
      (node as any).disablePictureInPicture = true;
      (node as any).disableRemotePlayback = true;
      node.setAttribute('x-webkit-airplay', 'deny');
    }
    (videoRef as any).current = node;
  }, []);

  useEffect(() => {
    async function setupCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: false,
          video: { 
            facingMode: 'environment',
          } 
        });
        streamRef.current = stream;
        setHasPermission(true);
      } catch (err) {
        console.error("Error accessing camera:", err);
        setHasPermission(false);
      }
    }

    setupCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const toggleFlash = async () => {
    if (!videoRef.current || !videoRef.current.srcObject) return;
    
    const stream = videoRef.current.srcObject as MediaStream;
    const track = stream.getVideoTracks()[0];
    
    try {
      // Check if torch is supported
      const capabilities = track.getCapabilities() as any;
      if (capabilities.torch) {
        const newFlashState = !isFlashOn;
        await track.applyConstraints({
          advanced: [{ torch: newFlashState }]
        } as any);
        setIsFlashOn(newFlashState);
      } else {
        console.warn("Flash (torch) is not supported on this device/camera.");
      }
    } catch (err) {
      console.error("Error toggling flash:", err);
    }
  };

  return (
    <div 
      className="h-full bg-black relative overflow-hidden flex flex-col overscroll-none"
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Camera Feed */}
      <div className="absolute inset-0 z-0 bg-black">
        {hasPermission === true && (
          <video 
            ref={setVideoRef}
            autoPlay 
            playsInline 
            muted 
            className="w-full h-full object-cover"
          />
        )}
        
        {/* Darkening overlay for cinematic look */}
        <div className="absolute inset-0 bg-black/40 pointer-events-none" />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#13213b]/40 via-transparent to-[#13213b]/60 pointer-events-none" />
        
        {hasPermission === false && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-bg-dark px-12 text-center">
            <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mb-6">
              <EyeOff size={40} className="text-red-500" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-white">Camera Access Denied</h3>
            <p className="text-white/40 text-sm">Please enable camera permissions in your device settings to use the AR scanner.</p>
            <button className="mt-8 px-8 py-3 bg-primary text-bg-dark font-bold rounded-xl uppercase tracking-widest text-xs">Open Settings</button>
          </div>
        )}
      </div>
      
      {/* Header */}
      <header className="relative z-10 pt-12 px-6">
        <div className="bg-[#2A241E]/80 backdrop-blur-xl p-4 rounded-3xl flex items-center justify-between border border-white/5 shadow-2xl">
          <button 
            onClick={onBack}
            className="w-12 h-12 rounded-full bg-black/40 flex items-center justify-center text-white hover:bg-black/60 transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <div className="text-center">
            <h1 className="font-bold text-lg uppercase tracking-[0.2em] text-white">QR Scanner</h1>
            <p className="text-primary text-[10px] font-bold uppercase tracking-[0.2em]">Museum of Freemasonry</p>
          </div>
          <button 
            onClick={toggleFlash}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${isFlashOn ? 'bg-primary text-bg-dark shadow-[0_0_15px_rgba(250,163,27,0.5)]' : 'bg-black/40 text-white hover:bg-black/60'}`}
          >
            <Zap size={24} fill={isFlashOn ? "currentColor" : "none"} />
          </button>
        </div>
      </header>

      {/* Main Scanner Area */}
      <main className="flex-1 flex flex-col items-center justify-center relative z-10">
        <div className="relative w-56 h-56">
          {/* Corner Brackets */}
          <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-primary/40 rounded-tl-[24px]"></div>
          <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-primary/40 rounded-tr-[24px]"></div>
          <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-primary/40 rounded-bl-[24px]"></div>
          <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-primary/40 rounded-br-[24px]"></div>
          
          {/* Circular Target */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-52 h-52 rounded-full border border-primary/10 flex items-center justify-center">
              <div className="w-44 h-44 rounded-full border border-primary/20 relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-[1px] h-full bg-primary/10"></div>
                  <div className="h-[1px] w-full bg-primary/10 absolute"></div>
                </div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-primary/60 rounded-full shadow-[0_0_8px_rgba(250,163,27,0.4)]"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Status Indicator */}
        <div className="mt-12 bg-black/60 backdrop-blur-md px-6 py-3 rounded-full border border-white/10 flex items-center gap-3">
          <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(34,197,94,0.5)]"></div>
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-white">Searching QR Code</span>
        </div>
      </main>

      {/* Instructions Panel */}
      <AnimatePresence>
        {showInstructions && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="relative z-10 px-6 pb-12"
          >
            <div className="bg-[#2A241E]/90 backdrop-blur-xl p-6 rounded-[32px] border border-white/5 shadow-2xl relative">
              <button 
                onClick={() => setShowInstructions(false)}
                className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
              <div className="text-center">
                <p className="text-primary text-[10px] font-bold uppercase tracking-[0.3em] mb-2">Instructions</p>
                <p className="text-white text-sm font-medium leading-relaxed">
                  Scan a QR code to unlock immersive content.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// getRank is imported from lib/rankCalculator

const ProfileScreen = ({ 
  onLogout, 
  onRewardClick, 
  avatarUrl, 
  setAvatarUrl,
  userName,
  setUserName
}: { 
  onLogout: () => void, 
  onRewardClick: (reward: any) => void, 
  avatarUrl: string, 
  setAvatarUrl: (url: string) => void,
  userName: string,
  setUserName: (name: string) => void
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [objectsDiscovered, setObjectsDiscovered] = useState(0);
  const [museumsVisited, setMuseumsVisited] = useState(0);
  const [giftsUnlocked, setGiftsUnlocked] = useState(0);
  const [userRewards, setUserRewards] = useState<any[]>([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [coverImage, setCoverImage] = useState('');
  const [showRankDetail, setShowRankDetail] = useState(false);
  const [rankUpTier, setRankUpTier] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [resetPasswordSaving, setResetPasswordSaving] = useState(false);
  const [resetPasswordSent, setResetPasswordSent] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState('');

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteSaving, setDeleteSaving] = useState(false);

  // Username editing state
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState(userName);
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    setEditNameValue(userName);
  }, [userName]);

  // Profanity blocklist (client-side only)
  const profanityBlocklist = [
    'fuck', 'shit', 'bitch', 'ass', 'damn', 'hell', 'crap', 'piss',
    'dick', 'cock', 'pussy', 'cum', 'sex', 'porn', 'nazi', 'hitler',
    'retard', 'fag', 'dyke', 'tranny', 'kike', 'nigger', 'spic',
    'chink', 'gook', 'wetback', 'beaner', 'towelhead', 'sandnigger',
    'amcik', 'amk', 'orospu', 'orosbu', 'siktir', 'gavat', 'pezevenk',
    'ibne', 'gay', 'lesbian', 'homo', 'trans', 'shemale', 'penis', 'vagina',
    'anal', 'oral', 'masturbation', 'rape', 'molest', 'pedo', 'child',
    'kill', 'murder', 'suicide', 'bomb', 'terror', 'isis', 'alqaeda'
  ];

  const hasProfanity = (text: string) => {
    const lower = text.toLowerCase();
    return profanityBlocklist.some(word => lower.includes(word));
  };

  const handleResetPassword = async () => {
    const email = auth.currentUser?.email;
    if (!email) return;
    setResetPasswordSaving(true);
    setResetPasswordError('');
    setResetPasswordSent(false);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetPasswordSent(true);
    } catch (err: any) {
      setResetPasswordError(err.message || 'Failed to send reset email');
    }
    setResetPasswordSaving(false);
  };

  const handleDeleteAccount = async () => {
    if (!auth.currentUser) return;
    setDeleteSaving(true);
    try {
      await deleteUser(auth.currentUser);
      onLogout();
    } catch (err: any) {
      console.warn('[Profile] Failed to delete account:', err);
      setDeleteConfirm(false);
    }
    setDeleteSaving(false);
};
  
  // Username editing handlers
  const handleNameEditStart = () => {
    setEditNameValue(userName);
    setEditingName(true);
    setNameError('');
  };

  const handleNameCancel = () => {
    setEditingName(false);
    setEditNameValue(userName);
    setNameError('');
  };

  const handleNameSave = async () => {
    const uid = auth.currentUser?.uid;
    const trimmed = editNameValue.trim();
    if (!trimmed || trimmed.length > 15 || hasProfanity(trimmed)) return;
    
    const previousName = userName;
    // Optimistic update: instantly change name in top-level state & close input
    setUserName(trimmed);
    setEditingName(false);
    setNameError('');
    setSavingName(true);

    try {
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, { displayName: trimmed });
      }
      if (uid) {
        await setDoc(doc(db, 'users', uid), {
          firstName: trimmed.split(' ')[0],
          lastName: trimmed.split(' ').slice(1).join(' ') || 'User',
        }, { merge: true });
      }
    } catch (err: any) {
      console.error('[Profile] Failed to update name:', err);
      // Revert optimistic update on failure
      setUserName(previousName);
      setEditingName(true);
      setNameError(err.message || 'Failed to update name');
    }
    setSavingName(false);
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        compressImage(result).then((compressed) => {
          setAvatarUrl(compressed);
        });
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    if (!auth.currentUser) { setLoading(false); return; }
    const uid = auth.currentUser.uid;

    const fetchStats = async () => {
      try {
        const scansQuery = query(
          collection(db, 'analytics_events'),
          where('userId', '==', uid),
          where('eventType', 'in', ['stop_view', 'direct_scan']),
          limit(500)
        );
        const scansSnap = await getDocs(scansQuery);
        const uniqueExhibitions = new Set(scansSnap.docs.map(d => d.data().exhibitionId).filter(Boolean));
        setObjectsDiscovered(uniqueExhibitions.size);

        const allQuery = query(
          collection(db, 'analytics_events'),
          where('userId', '==', uid),
          limit(500)
        );
        const allSnap = await getDocs(allQuery);
        const museumSet = new Set(allSnap.docs.map(d => d.data().museumId).filter(Boolean));
        setMuseumsVisited(museumSet.size);
        setTotalEvents(allSnap.size);

        const redeemedQuery = query(
          collection(db, 'user_rewards'),
          where('userId', '==', uid),
          where('redeemed', '==', true),
          limit(500)
        );
        const redeemedSnap = await getDocs(redeemedQuery);
        setGiftsUnlocked(redeemedSnap.size);

        const allRewardsQuery = query(
          collection(db, 'user_rewards'),
          where('userId', '==', uid),
          limit(500)
        );
        const allRewardsSnap = await getDocs(allRewardsQuery);
        const ruleIds = Array.from(new Set(allRewardsSnap.docs.map(d => d.data().ruleId).filter(Boolean)));
        const rulesMap = new Map<string, any>();
        if (ruleIds.length > 0) {
          const rulesQuery = query(
            collection(db, 'rewards_rules'),
            where(documentId(), 'in', ruleIds)
          );
          const rulesSnap = await getDocs(rulesQuery);
          rulesSnap.docs.forEach(d => rulesMap.set(d.id, d.data()));
        }
        const rewards: any[] = allRewardsSnap.docs.map(docSnap => {
          const data = docSnap.data();
          const rule = data.ruleId ? (rulesMap.get(data.ruleId) || null) : null;
          return { id: docSnap.id, ...data, rule };
        });
        setUserRewards(rewards);
      } catch (err) {
        console.warn('[Profile] Error fetching stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();

    if (MUSEUMS.length > 0) {
      setCoverImage(MUSEUMS[0].coverImageUrl || MUSEUMS[0].image);
    }
  }, []);

  // Rank-up detection: compare stored count to current
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid || objectsDiscovered === 0) return;
    const key = `comenius_rank_objs_${uid}`;
    const prev = Number(localStorage.getItem(key) || '0');
    if (objectsDiscovered > prev) {
      const prevRank = getRank(prev);
      const newRank = getRank(objectsDiscovered);
      if (prevRank.key !== newRank.key) {
        setRankUpTier(newRank.title);
        setTimeout(() => setRankUpTier(null), 4000);
      }
      localStorage.setItem(key, String(objectsDiscovered));
    }
  }, [objectsDiscovered]);

  const rank = getRank(objectsDiscovered);
  const nextRank = getNextRank(rank);
  const { pct: _rankPct } = getRankProgress(rank, objectsDiscovered);

  const rewardIcon = (rule: any) => {
    const title = rule?.title || '';
    if (title.includes('Discount') || title.includes('Shop')) return Rocket;
    if (title.includes('Cafe')) return Compass;
    return Star;
  };

  return (
    <div className="bg-[#13213b] h-full flex flex-col overflow-hidden">
      {/* Hidden File Input */}
      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={handleFileChange} 
        accept="image/*" 
        className="hidden" 
      />

      {/* === FIXED TOP SECTION === */}
      {/* Hero Header */}
      <div className="relative h-[200px] overflow-hidden shrink-0">
        <img 
          src={coverImage || 'https://upload.wikimedia.org/wikipedia/commons/1/1a/William_Blake_-_Isaac_Newton_-_WGA02217.jpg'} 
          alt="Hero" 
          className="w-full h-full object-cover opacity-60"
          referrerPolicy="no-referrer"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#13213b]/90 via-[#13213b]/45 to-[#13213b]"></div>
        
        <div className="absolute top-16 left-8 right-8 flex justify-between items-start">
          <div>
            <p className="text-primary text-[10px] font-bold uppercase tracking-[0.3em] mb-1">Welcome back</p>
            <AnimatePresence mode="wait">
              {editingName ? (
                <motion.div
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                  className="flex items-center gap-2 w-full max-w-xs"
                >
                  <input
                    ref={(el) => { if (el) el.focus(); }}
                    value={editNameValue}
                    onChange={(e) => setEditNameValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleNameSave(); }}
                    maxLength={15}
                    className={`
                      flex-1 bg-surface-dark/60 border border-white/10
                      focus:border-primary/50 focus:ring-2 focus:ring-primary/20
                      text-white placeholder:text-slate-500
                      px-4 py-2.5 rounded-xl text-sm font-medium
                      transition-all duration-200 outline-none
                    `}
                    placeholder="Name"
                  />
                  <span className={`text-[10px] tabular-nums ${editNameValue.length >= 15 ? 'text-red-400' : 'text-slate-500'}`}>
                    {editNameValue.length}/15
                  </span>
                  <button 
                    onClick={handleNameCancel} 
                    className="text-slate-400 hover:text-slate-300 p-1 active:scale-90 transition-transform"
                    aria-label="Cancel"
                  >
                    <X size={18} />
                  </button>
                  <button 
                    onClick={handleNameSave} 
                    disabled={savingName || !editNameValue.trim() || editNameValue.trim().length > 15 || hasProfanity(editNameValue.trim())}
                    className={`
                      bg-primary text-bg-dark px-3 py-1.5 rounded-xl text-xs font-bold
                      uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed
                      active:scale-95 transition-transform
                    `}
                  >
                    {savingName ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  </button>
                </motion.div>
              ) : (
                <motion.h2
                  initial={{ opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -8 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                  className="text-3xl font-serif font-bold text-white flex items-center gap-2"
                >
                  {userName}
                  <button 
                    onClick={handleNameEditStart} 
                    className="text-slate-400 hover:text-primary p-1 active:scale-90 transition-colors" 
                    aria-label="Edit username"
                  >
                    <Edit2 size={18} />
                  </button>
                </motion.h2>
              )}
            </AnimatePresence>
            <div className="flex items-center gap-1 mt-1">
              <span className="bg-primary/20 border border-primary/40 text-primary px-2.5 py-0.5 rounded-full text-xs font-bold tracking-wider flex items-center gap-1">
                <span>{rank.emoji}</span> {rank.title.toUpperCase()}
              </span>
            </div>
          </div>
          <div className="relative">
            <div 
              onClick={handleAvatarClick}
              className="w-20 h-20 rounded-full border-2 border-primary/30 p-1 bg-[#13213b]/40 backdrop-blur-md cursor-pointer active:scale-95 transition-transform overflow-hidden relative group"
            >
              <img 
                src={avatarUrl} 
                className="w-full h-full rounded-full object-cover" 
                alt="Profile" 
                referrerPolicy="no-referrer" 
              />
              <div className="absolute inset-0 bg-black/55 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-[10px] text-white font-bold uppercase tracking-wider text-center">
                Edit
              </div>
            </div>
            <div 
              onClick={handleAvatarClick}
              className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-primary text-[#13213b] flex items-center justify-center border-2 border-[#13213b] shadow-md cursor-pointer active:scale-90 transition-transform z-10"
            >
              <span className="text-sm font-bold leading-none">+</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="px-8 -mt-2 relative z-10 shrink-0">
        <div className="grid grid-cols-3 gap-4 mb-0">
          {[
            { label: 'Experiences Explored', val: String(objectsDiscovered), icon: Search },
            { label: 'Museums Visited', val: String(museumsVisited), icon: HomeIcon },
            { label: 'Gifts Unlocked', val: String(giftsUnlocked), icon: Gift },
          ].map((stat) => (
            <div key={stat.label} className="bg-surface-dark/40 border border-white/5 rounded-2xl p-4 flex flex-col items-center text-center gap-2">
              <stat.icon size={20} className="text-primary" />
              <span className="text-xl font-bold text-primary">{stat.val}</span>
              <span className="text-xs text-slate-300 font-bold uppercase leading-tight">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* === SCROLLABLE SECTION === */}
      <div className="relative flex-1 min-h-0">
        <div className="absolute top-0 left-0 right-0 h-10 bg-gradient-to-b from-[#13213b] via-[#13213b]/90 to-transparent z-20 pointer-events-none" />

        <div className="h-full overflow-y-auto no-scrollbar px-8 pt-8 pb-32">

        {/* Rank Card — scrolls with content (Stitch twin); header + stats stay pinned */}
        <RankCard
          rank={rank}
          objectsDiscovered={objectsDiscovered}
          isNew={!!rankUpTier}
          onPress={() => setShowRankDetail(true)}
        />

        <div className="mb-12">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-[22px] font-serif font-semibold">Your Rewards</h3>
          </div>
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-surface-dark/40 border border-white/5 rounded-2xl p-4 flex items-center gap-4 animate-pulse">
                  <div className="w-16 h-16 rounded-xl bg-white/5" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-white/10 rounded w-2/3" />
                    <div className="h-3 bg-white/5 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : userRewards.length === 0 ? (
            <div className="text-slate-500 text-sm text-center py-8 leading-relaxed">
              Complete experiences to earn rewards!
            </div>
          ) : (
            <div className="space-y-4">
              {userRewards.map((reward) => {
                const Icon = rewardIcon(reward.rule);
                return (
                  <div 
                    key={reward.id} 
                    onClick={() => onRewardClick(reward)}
                    className="bg-surface-dark/40 border border-white/5 rounded-2xl p-4 flex items-center gap-4 cursor-pointer hover:bg-surface-dark/60 transition-colors active:scale-[0.98]"
                  >
                    <div className="w-11 h-11 rounded-full bg-white/5 flex items-center justify-center text-primary shrink-0">
                      <Icon size={22} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-[15px]">{reward.rule?.title || reward.ruleTitle || 'Reward'}</h4>
                      <p className="text-[10px] text-slate-500 mt-1">{reward.rule?.description || reward.ruleTitle || ''}</p>
                    </div>
                    <ChevronRight size={20} className="text-slate-600" />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <h3 className="text-[22px] font-serif font-semibold mb-4">Settings</h3>

          {/* Email (read-only) */}
          <div className="bg-surface-dark/40 border border-white/5 rounded-2xl p-4 flex items-center justify-between gap-3">
            <span className="text-slate-300 font-medium text-sm">Email</span>
            <span className="text-slate-400 text-sm truncate">{auth.currentUser?.email || '—'}</span>
          </div>

          {/* Reset Password */}
          <div className="bg-surface-dark/40 border border-white/5 rounded-2xl p-5">
            <h4 className="font-bold text-sm mb-2">Password</h4>
            <p className="text-[10px] text-slate-500 mb-3 leading-relaxed">
              We'll send a password reset link to your email address.
            </p>
            {resetPasswordSent ? (
              <p className="text-xs text-emerald-400">Reset link sent! Check your email.</p>
            ) : (
              <>
                {resetPasswordError && <p className="text-[10px] text-red-400 mb-2">{resetPasswordError}</p>}
                <button
                  onClick={handleResetPassword}
                  disabled={resetPasswordSaving}
                  className="w-full bg-transparent border border-white/20 text-white h-11 rounded-xl font-bold text-xs uppercase tracking-widest hover:border-primary hover:text-primary active:scale-[0.98] transition-all disabled:opacity-40"
                >
                  {resetPasswordSaving ? 'Sending...' : 'Reset Password'}
                </button>
              </>
            )}
          </div>

          {/* Delete Account */}
          <div className="bg-surface-dark/40 border border-red-500/10 rounded-2xl p-5 flex items-center justify-between gap-3">
            <div className="min-w-0">
            <h4 className="font-bold text-sm mb-2 text-red-400">Delete Account</h4>
            {!deleteConfirm ? (
              <button
                onClick={() => setDeleteConfirm(true)}
                className="text-xs text-red-400/70 hover:text-red-400 underline underline-offset-2 transition-colors"
              >
                Request account deletion
              </button>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-red-300 leading-relaxed">
                  This will permanently delete your account and all associated data. This action cannot be undone.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setDeleteConfirm(false)}
                    className="px-5 py-2.5 text-xs font-bold uppercase tracking-widest rounded-xl bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleteSaving}
                    className="px-5 py-2.5 text-xs font-bold uppercase tracking-widest rounded-xl bg-red-500 text-white hover:bg-red-600 active:scale-95 transition-all disabled:opacity-50"
                  >
                    {deleteSaving ? 'Deleting...' : 'Confirm Delete'}
                  </button>
                </div>
              </div>
            )}
            </div>
            <ChevronRight size={20} className="text-red-400/60 shrink-0" />
          </div>

          <button 
            onClick={onLogout}
            className="w-full bg-[#13213b]/40 border border-white/20 text-slate-300 hover:text-white h-14 rounded-2xl font-bold flex items-center justify-center gap-3 active:scale-[0.98] transition-all"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>

      {/* Rank-up toast */}
      <AnimatePresence>
        {rankUpTier && (
          <motion.div
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -40 }}
            transition={{ type: 'spring', stiffness: 320, damping: 26 }}
            className="fixed top-14 left-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-2xl border border-primary/30 bg-[#0f1c30]/95 backdrop-blur-xl shadow-2xl"
          >
            <span className="text-2xl shrink-0">{rank.emoji}</span>
            <div className="min-w-0">
              <p className="text-xs font-black uppercase tracking-widest text-primary">Rank Up!</p>
              <p className="text-sm font-bold text-white truncate">You're now a {rankUpTier}! 🎉</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rank Detail Sheet */}
      <RankDetailSheet
        rank={rank}
        objectsDiscovered={objectsDiscovered}
        isOpen={showRankDetail}
        onClose={() => setShowRankDetail(false)}
      />
  </div>
);
};

const RewardRedeemScreen = ({ reward, onClose }: { reward: any, onClose: () => void }) => {
  if (!reward) return null;
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      className="absolute inset-0 z-50 bg-[#13213b] flex flex-col"
    >
      {/* Background with Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1554907984-15263bfd63bd?w=800&q=80" 
          className="w-full h-full object-cover opacity-30"
          alt="Venue"
          referrerPolicy="no-referrer"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#13213b]/80 via-[#13213b]/90 to-[#13213b]"></div>
      </div>

      {/* Header */}
      <div className="relative z-10 flex justify-end p-6">
        <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white active:scale-90 transition-transform">
          <X size={24} />
        </button>
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center px-8 pt-4 pb-12 overflow-y-auto no-scrollbar">
        <div className="mb-8 text-center">
          <p className="text-primary text-[10px] font-bold uppercase tracking-[0.4em] mb-2">Exclusive Reward</p>
          <h2 className="text-3xl font-serif font-bold text-white leading-tight px-2.5">{reward.title}</h2>
        </div>

        {/* QR Code Container */}
        <div className="bg-white p-6 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] mb-10 relative group">
          <div className="absolute -inset-4 bg-primary/20 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <img 
            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=COMENIUS-REDEEM:${auth.currentUser?.uid || 'anon'}:${reward.ruleId || reward.id}`} 
            className="w-48 h-48 relative z-10"
            alt="QR Code"
            referrerPolicy="no-referrer"
          />
        </div>

        {/* Details */}
        <div className="w-full space-y-8">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h4 className="text-primary text-xs font-bold uppercase tracking-widest mb-4">Redemption Conditions</h4>
            <ul className="space-y-3">
              {[
                'Present this QR code at the museum shop counter.',
                'Valid for one-time use only.',
                'Cannot be combined with other offers.',
                'Valid until December 31, 2026.'
              ].map((item, i) => (
                <li key={i} className="flex gap-3 text-sm text-white/70 leading-relaxed">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0"></div>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h4 className="text-primary text-xs font-bold uppercase tracking-widest mb-4">About this Reward</h4>
            <p className="text-sm text-white/70 leading-relaxed">
              {reward.description}. This exclusive benefit is our way of thanking you for your dedication to exploring the hidden histories of culture and art.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// --- Feedback Popup ---

const FeedbackPopup = ({ 
  onClose, 
  onSubmit 
}: { 
  onClose: () => void; 
  onSubmit?: (data: { ratingValue: number; q1?: number; q2?: number; q3?: number; comment?: string }) => void;
}) => {
  const [overallScore, setOverallScore] = useState(0);
  const [q1, setQ1] = useState(0);
  const [q2, setQ2] = useState(0);
  const [q3, setQ3] = useState(0);
  const [comment, setComment] = useState('');

  const handleSend = () => {
    if (overallScore === 0) {
      alert('Please provide an overall experience score.');
      return;
    }
    if (onSubmit) {
      onSubmit({
        ratingValue: overallScore,
        q1: q1 || undefined,
        q2: q2 || undefined,
        q3: q3 || undefined,
        comment: comment.trim() || undefined
      });
    }
    onClose();
  };

  const StarRating = ({ value, onChange, label }: { value: number, onChange: (v: number) => void, label?: string }) => (
    <div className="mb-6">
      {label && <p className="text-white/60 text-xs uppercase tracking-widest mb-3">{label}</p>}
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button 
            key={star} 
            onClick={() => onChange(star)}
            className="transition-transform active:scale-90"
          >
            <Star 
              size={28} 
              className={star <= value ? "fill-primary text-primary" : "text-white/20"} 
            />
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-[1000] bg-[#13213b]/95 backdrop-blur-xl flex flex-col"
    >
      {/* Header */}
      <div className="flex justify-end p-8 pb-0 shrink-0">
        <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
          <X size={32} />
        </button>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto no-scrollbar px-8 pt-4 pb-40">
        <h2 className="text-4xl font-serif font-bold text-white mb-2">Thank You</h2>
        <p className="text-white/40 text-sm mb-12">We value your feedback to improve the museum experience.</p>

        <StarRating 
          label="Overall Experience Score" 
          value={overallScore} 
          onChange={setOverallScore} 
        />

        <div className="space-y-8 mt-12">
          <StarRating 
            label="Emotional impact of the experience" 
            value={q1} 
            onChange={setQ1} 
          />
          <StarRating 
            label="Museum storytelling and flow" 
            value={q2} 
            onChange={setQ2} 
          />
          <StarRating 
            label="Space layout and navigation" 
            value={q3} 
            onChange={setQ3} 
          />
        </div>

        <div className="mt-12">
          <p className="text-white/60 text-xs uppercase tracking-widest mb-3">Additional Comments</p>
          <textarea 
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Tell us more about your visit..."
            className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white text-sm focus:outline-none focus:border-primary/50 min-h-[120px] resize-none"
          />
        </div>
      </div>

      {/* Floating Bottom Button */}
      <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-[#13213b] via-[#13213b] to-transparent pt-12 pointer-events-none">
        <button 
          onClick={handleSend}
          disabled={overallScore === 0}
          className={`w-full h-16 rounded-2xl flex items-center justify-center gap-3 font-bold uppercase tracking-widest transition-all pointer-events-auto ${
            overallScore > 0 
              ? "bg-primary text-[#13213b] shadow-[0_4px_20px_rgba(250,163,27,0.3)] active:scale-[0.98]" 
              : "bg-white/5 text-white/20 cursor-not-allowed"
          }`}
        >
          <Send size={20} />
          <span>Send Feedback</span>
        </button>
      </div>
    </motion.div>
  );
};

interface ZoomableImageModalProps {
  src: string;
  alt: string;
  isOpen: boolean;
  onClose: () => void;
}

const ZoomableImageModal = ({ src, alt, isOpen, onClose }: ZoomableImageModalProps) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const imgRef = useRef<HTMLImageElement>(null);

  // Track touch state
  const touchStartRef = useRef({
    distance: 0,
    scale: 1,
    x: 0,
    y: 0,
    startX: 0,
    startY: 0,
    time: 0,
    isPinching: false,
    moved: false,
  });

  useEffect(() => {
    if (!isOpen) {
      // Reset zoom states when closed
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen]);

  const handleTouchStart = (e: React.TouchEvent) => {
    const touches = e.touches;
    if (touches.length === 1) {
      const touch = touches[0];
      touchStartRef.current = {
        distance: 0,
        scale: scale,
        x: touch.clientX - position.x,
        y: touch.clientY - position.y,
        startX: touch.clientX,
        startY: touch.clientY,
        time: Date.now(),
        isPinching: false,
        moved: false,
      };
    } else if (touches.length === 2) {
      const dist = Math.hypot(
        touches[0].clientX - touches[1].clientX,
        touches[0].clientY - touches[1].clientY
      );
      touchStartRef.current = {
        distance: dist,
        scale: scale,
        x: position.x,
        y: position.y,
        startX: 0,
        startY: 0,
        time: Date.now(),
        isPinching: true,
        moved: false,
      };
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const touches = e.touches;
    if (touches.length === 1 && !touchStartRef.current.isPinching) {
      const touch = touches[0];
      const distMoved = Math.hypot(
        touch.clientX - touchStartRef.current.startX,
        touch.clientY - touchStartRef.current.startY
      );
      if (distMoved > 8) {
        touchStartRef.current.moved = true;
      }

      if (scale > 1) {
        const newX = touch.clientX - touchStartRef.current.x;
        const newY = touch.clientY - touchStartRef.current.y;
        
        // Clamp translations to prevent dragging too far off screen
        const maxDragX = (scale - 1) * (window.innerWidth / 2);
        const maxDragY = (scale - 1) * (window.innerHeight / 2);
        const clampedX = Math.max(-maxDragX, Math.min(maxDragX, newX));
        const clampedY = Math.max(-maxDragY, Math.min(maxDragY, newY));

        setPosition({ x: clampedX, y: clampedY });
      }
    } else if (touches.length === 2) {
      const dist = Math.hypot(
        touches[0].clientX - touches[1].clientX,
        touches[0].clientY - touches[1].clientY
      );
      const factor = dist / touchStartRef.current.distance;
      const newScale = Math.max(1, Math.min(4, touchStartRef.current.scale * factor));
      setScale(newScale);

      // If scale becomes 1, reset position
      if (newScale === 1) {
        setPosition({ x: 0, y: 0 });
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const duration = Date.now() - touchStartRef.current.time;
    if (e.touches.length === 0) {
      if (!touchStartRef.current.isPinching && !touchStartRef.current.moved && duration < 300) {
        onClose();
      } else if (scale < 1.05) {
        setScale(1);
        setPosition({ x: 0, y: 0 });
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 z-[1000] bg-black/90 flex items-center justify-center p-4 backdrop-blur-sm touch-none"
        >
          {/* Close button indicator */}
          <div className="absolute top-6 right-6 text-white/50 pointer-events-none text-xs uppercase tracking-widest bg-white/5 px-4 py-2 rounded-full border border-white/5 shadow-lg">
            Tap anywhere to close
          </div>

          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-full max-w-4xl max-h-[85vh] flex items-center justify-center overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              ref={imgRef}
              src={src}
              alt={alt}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onClick={onClose}
              style={{
                transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(${scale})`,
                transition: touchStartRef.current.isPinching ? 'none' : 'transform 0.1s ease-out',
                cursor: scale > 1 ? 'grab' : 'zoom-in',
              }}
              className="max-w-full max-h-[80vh] object-contain select-none drop-shadow-[0_20px_50px_rgba(0,0,0,0.9)] rounded-2xl border border-white/10"
              draggable={false}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const TourObjectScreen = ({ 
  museumId, 
  experienceId, 
  dynamicItinerary, 
  onBack, 
  onClose, 
  onStartAR,
  onOpenAIGuide,
  onProgressUpdate,
  initialItem = 1,
  onStopView,
  onStopAction,
  onDwellTime,
  isMember = false,
  onRequireSignIn
}: { 
  museumId: string, 
  experienceId: string, 
  dynamicItinerary: any[], 
  onBack: () => void, 
  onClose: () => void, 
  onStartAR?: (linkedId?: string, mode?: string, stopNumber?: number, title?: string) => void,
  onOpenAIGuide: (exhibit: any) => void,
  onProgressUpdate?: (currentItem: number, totalItems: number, completed: boolean) => void,
  initialItem?: number,
  onStopView?: (stopNumber: number, title: string) => void,
  onStopAction?: (actionType: 'audio' | 'immersive', stopNumber: number, title: string) => void,
  onDwellTime?: (stopNumber: number, title: string, durationSeconds: number) => void
  isMember?: boolean,
  onRequireSignIn?: () => void
}) => {
  const details: any = { title: '', description: '', image: '', minutes: '', objectsCount: '', about: '', itinerary: [] };
  const itinerary = dynamicItinerary && dynamicItinerary.length > 0 ? dynamicItinerary : details.itinerary;

  const [isPlaying, setIsPlaying] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // Sticky mini-title: when the stop title scrolls past the top edge of the scroll
  // region, the header crossfades from the route title to the exhibit title and the
  // mini player floats on the bottom bar's top border.
  // Deterministic scroll listener (no IntersectionObserver): on every scroll frame we
  // compare the LIVE positions of the <h3> and the scroll region top, so this works
  // regardless of when data/images finish loading or how the screen mounts.
  const stopTitleRef = useRef<HTMLHeadingElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [titleStuck, setTitleStuck] = useState(false);
  const updateTitleStuck = useCallback(() => {
    const el = stopTitleRef.current;
    const scroller = scrollRef.current;
    if (!el || !scroller) return;
    setTitleStuck(el.getBoundingClientRect().top < scroller.getBoundingClientRect().top);
  }, []);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    scroller.addEventListener('scroll', updateTitleStuck, { passive: true });
    updateTitleStuck();
    return () => scroller.removeEventListener('scroll', updateTitleStuck);
  }, [updateTitleStuck]);

  // Remaining-time label for the Stitch LISTEN button timer chip (mm:ss, countdown)
  const formatListenRemaining = () => {
    const remaining = Math.max(0, duration * (1 - progress / 100));
    const m = Math.floor(remaining / 60);
    const s = Math.floor(remaining % 60);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };
  
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const fullBufferRef = useRef<AudioBuffer | null>(null);
  const playbackOffsetRef = useRef(0);
  const lastStartTimeRef = useRef(0);
  const animationFrameRef = useRef<number | null>(null);
  const isPlayingRef = useRef(false);
  // Progress-loop guards: idempotent start + ~10Hz state throttle (the ring's
  // CSS transition smooths between ticks, so 60 renders/sec is unnecessary)
  const loopRunningRef = useRef(false);
  const lastProgressEmitRef = useRef(0);
  const lastProgressTimeRef = useRef(0);


  const totalItems = itinerary.length;
  const [currentItem, setCurrentItem] = useState(() => Math.max(1, Math.min(initialItem, totalItems || 1)));

  const currentObj = itinerary[currentItem - 1] || itinerary[0] || { title: '', desc: '', img: '', itemNo: '', audioText: '', audioUrl: null, type: '', linkedExhibitId: null, aiGuide: null };

  // Route exhibition title for the header (Exhibition Title from Firestore via the loaded map; render-only)
  const exhibitionTitle = ((MUSEUM_EXPERIENCES[museumId] || []).find((e: any) => e.id === experienceId)?.title || details.title || '').toString();

  const entryTimeRef = useRef<number>(Date.now());
  const prevItemRef = useRef<{ stopNumber: number; title: string }>({ stopNumber: currentItem, title: currentObj.title });

  // Notify parent of progress changes & log stop_view and dwell_time
  useEffect(() => {
    if (onProgressUpdate && totalItems > 0) {
      onProgressUpdate(currentItem, totalItems, false);
    }

    // Report dwell time of the previous stop before transitioning to the new one
    const elapsed = (Date.now() - entryTimeRef.current) / 1000;
    if (elapsed > 1.5 && prevItemRef.current) {
      if (onDwellTime) {
        onDwellTime(prevItemRef.current.stopNumber, prevItemRef.current.title, Math.round(elapsed));
      }
    }
    entryTimeRef.current = Date.now();
    prevItemRef.current = { stopNumber: currentItem, title: currentObj.title };

    if (onStopView) {
      onStopView(currentItem, currentObj.title);
    }
  }, [currentItem, currentObj.title, experienceId]);

  // Handle clean up for dwell time when component unmounts
  useEffect(() => {
    return () => {
      const elapsed = (Date.now() - entryTimeRef.current) / 1000;
      if (elapsed > 1.5 && prevItemRef.current) {
        if (onDwellTime) {
          onDwellTime(prevItemRef.current.stopNumber, prevItemRef.current.title, Math.round(elapsed));
        }
      }
    };
  }, []);

  const updateProgress = () => {
    if (!isPlayingRef.current || !audioContextRef.current || !fullBufferRef.current) {
      loopRunningRef.current = false;
      return;
    }
    const elapsed = audioContextRef.current.currentTime - lastStartTimeRef.current;
    const currentPos = playbackOffsetRef.current + elapsed;
    const total = fullBufferRef.current.duration;

    if (currentPos >= total) {
      setIsPlaying(false);
      isPlayingRef.current = false;
      playbackOffsetRef.current = 0;
      setProgress(0);
      lastProgressEmitRef.current = 0;
      loopRunningRef.current = false;
      return;
    }

    // Throttle state writes (~10Hz or >=0.5% change): the ring's CSS transition
    // interpolates between ticks, so per-frame setState is unnecessary
    const now = performance.now();
    const pct = (currentPos / total) * 100;
    if (pct - lastProgressEmitRef.current >= 0.5 || now - lastProgressTimeRef.current >= 150) {
      lastProgressEmitRef.current = pct;
      lastProgressTimeRef.current = now;
      setProgress(pct);
    }
    animationFrameRef.current = requestAnimationFrame(updateProgress);
  };

  const startProgressLoop = () => {
    if (loopRunningRef.current) return;
    loopRunningRef.current = true;
    animationFrameRef.current = requestAnimationFrame(updateProgress);
  };

  const stopProgressLoop = () => {
    loopRunningRef.current = false;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  useEffect(() => {
    if (isPlaying) {
      startProgressLoop();
    } else {
      stopProgressLoop();
    }
    return () => {
      stopProgressLoop();
    };
  }, [isPlaying]);

  const stopAudio = (resetOffset = true) => {
    stopProgressLoop();
    if (sourceRef.current) {
      try { sourceRef.current.stop(); } catch { /* already ended */ }
      sourceRef.current = null;
    }
    if (isPlayingRef.current && audioContextRef.current) {
      const elapsed = audioContextRef.current.currentTime - lastStartTimeRef.current;
      playbackOffsetRef.current += elapsed;
    }
    if (resetOffset) {
      playbackOffsetRef.current = 0;
      setProgress(0);
      lastProgressEmitRef.current = 0;
    }
    setIsPlaying(false);
    isPlayingRef.current = false;
  };

  const playFromOffset = async (offset: number) => {
    if (!fullBufferRef.current || !audioContextRef.current) return;

    // Autoplay-policy guard: a context created after an await may start suspended,
    // which freezes currentTime (ring + timer stall while Pause shows). Resume first.
    try {
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }
    } catch (err) {
      console.warn('[TTS] AudioContext resume failed:', err);
    }

    if (sourceRef.current) {
      try { sourceRef.current.stop(); } catch { /* already ended */ }
      sourceRef.current = null;
    }

    const source = audioContextRef.current.createBufferSource();
    source.buffer = fullBufferRef.current;
    source.connect(audioContextRef.current.destination);

    lastStartTimeRef.current = audioContextRef.current.currentTime;
    playbackOffsetRef.current = offset;
    lastProgressEmitRef.current = (offset / fullBufferRef.current.duration) * 100;

    source.start(0, offset);
    sourceRef.current = source;
    setIsPlaying(true);
    isPlayingRef.current = true;

    source.onended = () => {
      // Only reset if it finished naturally (not paused/stopped manually)
      if (isPlayingRef.current) {
        setIsPlaying(false);
        isPlayingRef.current = false;
        playbackOffsetRef.current = 0;
        setProgress(0);
        lastProgressEmitRef.current = 0;
        sourceRef.current = null;
      }
    };
  };

  const handleTTS = async () => {
    // Free-member feature: guests see the sign-up pop-up instead of a silent backend rejection
    if (!isMember) {
      onRequireSignIn?.();
      return;
    }

    if (isPlayingRef.current) {
      // Pause logic
      if (sourceRef.current) {
        sourceRef.current.stop();
        sourceRef.current = null;
      }
      const elapsed = audioContextRef.current!.currentTime - lastStartTimeRef.current;
      const newOffset = playbackOffsetRef.current + elapsed;
      playbackOffsetRef.current = newOffset;
      if (fullBufferRef.current) {
        setProgress((newOffset / fullBufferRef.current.duration) * 100);
      }
      setIsPlaying(false);
      isPlayingRef.current = false;
      return;
    }

    if (onStopAction) {
      onStopAction('audio', currentItem, currentObj.title);
    }

    if (fullBufferRef.current) {
      // Resume logic
      playFromOffset(playbackOffsetRef.current);
      return;
    }

    if (currentObj.audioUrl) {
      try {
        setIsGenerating(true);
        const response = await fetch(currentObj.audioUrl);
        const arrayBuffer = await response.arrayBuffer();
        
        const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        audioContextRef.current = audioCtx;
        
        const decodedBuffer = await audioCtx.decodeAudioData(arrayBuffer);
        fullBufferRef.current = decodedBuffer;
        setDuration(decodedBuffer.duration);
        setIsGenerating(false);
        playFromOffset((progress / 100) * decodedBuffer.duration);
      } catch (err) {
        console.error("Error loading pre-recorded audio:", err);
        setIsGenerating(false);
        setIsPlaying(false);
      }
      return;
    }

    if (isGenerating) return;

    try {
      setIsGenerating(true);
      
      const textToRead = currentObj.audioText;

      const data = await proxyTTS(textToRead, 'Charon');
      const base64Audio = data?.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
      setIsGenerating(false);
      
      if (base64Audio) {
        const binaryString = window.atob(base64Audio);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const pcmData = new Int16Array(bytes.buffer);
        
        const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        audioContextRef.current = audioCtx;
        
        const buffer = audioCtx.createBuffer(1, pcmData.length, 24000);
        const channelData = buffer.getChannelData(0);
        
        for (let i = 0; i < pcmData.length; i++) {
          channelData[i] = pcmData[i] / 32768;
        }
        
        fullBufferRef.current = buffer;
        setDuration(buffer.duration);
        // Start from the pre-selected progress if any
        playFromOffset((progress / 100) * buffer.duration);
      }
    } catch (error) {
      console.error("TTS Error:", error);
      setIsGenerating(false);
      setIsPlaying(false);
    }
  };

  useEffect(() => {
    return () => stopAudio();
  }, []);

  useEffect(() => {
    // Reset to the top of the stop so the header/mini-player state is clean
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    stopAudio();
    fullBufferRef.current = null;
    playbackOffsetRef.current = 0;
    setProgress(0);
    setDuration(0);
    setTitleStuck(false);
    // Recompute once the freshly rendered title settles (covers layout shifts)
    const raf = requestAnimationFrame(updateTitleStuck);
    return () => cancelAnimationFrame(raf);
  }, [currentItem, updateTitleStuck]);

  return (
    <div className="bg-[#13213b] flex flex-col overflow-hidden overscroll-none" style={{ height: 'calc(100dvh - env(safe-area-inset-top, 0px))' }}>
      {/* Fixed header — outside the scroll region so it can never scroll away.
          Center title crossfades route ↔ exhibit and wraps to two lines when long. */}
      <header className="shrink-0 z-20 px-6 pt-6 pb-4 bg-[#13213b] border-b border-white/5">
        <div className="relative flex items-center min-h-[28px]">
          <button onClick={onBack} aria-label="Back" className="text-white/80 hover:text-white transition-colors shrink-0 relative z-10">
            <ChevronLeft size={28} />
          </button>
          <h2 className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-2 text-center max-w-[60vw] grid">
            <span className={`col-start-1 row-start-1 self-center line-clamp-2 leading-[1.7] text-primary text-[10px] font-bold uppercase tracking-[0.4em] [text-indent:0.2em] transition-all duration-300 ${titleStuck && currentObj.title ? 'opacity-0 -translate-y-1' : 'opacity-100 translate-y-0'}`}>{exhibitionTitle}</span>
            <span className={`col-start-1 row-start-1 self-center line-clamp-2 leading-[1.7] text-primary text-[10px] font-bold uppercase tracking-[0.4em] [text-indent:0.2em] transition-all duration-300 w-full ${titleStuck && currentObj.title ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}`}>{currentObj.title}</span>
          </h2>
          <button onClick={onClose} aria-label="Close" className="ml-auto text-white/80 hover:text-white transition-colors shrink-0 relative z-10">
            <X size={28} />
          </button>
        </div>
      </header>
      {/* Single scroll region: image + title + narration scroll together; bottom bar stays fixed.
          Gradient starts at the root navy so the header junction has no color step,
          then melts into the panel tones (full-bleed, no two-blue seam). */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar overscroll-none min-h-0 bg-gradient-to-b from-[#13213b] via-[#1A2B4D] to-[#15233D]">
      {/* Top Section (Image Area) — image frame is fit-content: full-bleed sides, natural aspect height */}
      <div className="flex flex-col shrink-0 h-auto">
        {/* Image Area — frame hugs the image: edge-to-edge width, proportional height (transparent: container gradient flows through) */}
        <div className="shrink-0 flex items-center justify-center px-8 pt-4 pb-3">
          <div className="relative w-full h-auto flex items-center justify-center rounded-[24px] overflow-hidden border border-white/10 bg-[#0e1830]">
            <div className="absolute inset-0 bg-primary/5 blur-[100px] rounded-full" />
            <img
              src={currentObj.img}
              alt={currentObj.title}
              onClick={() => setIsImageModalOpen(true)}
              onLoad={updateTitleStuck}
              className="w-full h-auto block object-cover relative z-10 cursor-pointer active:scale-95 transition-transform"
              referrerPolicy="no-referrer"
            />
          </div>
        </div>
      </div>

      {/* Content Panel — transparent (container gradient shows through); rounded top + hairline.
          Ambient shadow via .panel-shadow pseudo-element sits behind the panel for depth. */}
      <div className="relative flex flex-col rounded-t-[40px] border-t border-white/10 panel-shadow">
        {/* Static: title only — Play lives in the bottom bar (Stitch "PLAY" comp) */}
        <div className="shrink-0 px-8 pt-8 pb-3">
          <div className="mb-5">
            <h3 ref={stopTitleRef} className="font-serif italic text-2xl text-primary mb-1 tracking-tight">{currentObj.title}</h3>
            <p className="text-primary/40 text-[10px] font-bold uppercase tracking-[0.2em]">Item No: {currentObj.itemNo}</p>
          </div>
        </div>

        {/* Narration text — flows inside the single scroll region (preserves Curator line breaks) */}
        <div className="px-8 pb-10">
          {currentObj.audioText && (
            <p className="text-white/80 leading-relaxed text-sm whitespace-pre-line">
              {currentObj.audioText}
            </p>
          )}
        </div>
      </div>
      </div>{/* end single scroll region */}

      {/* Bottom Bar — always fixed, never scrolls away (Stitch order: CTAs → progress → nav) */}
      <div 
        className="relative shrink-0 bg-[#13213b] px-6 pt-6 border-t border-white/5 touch-none"
        style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }}
      >
        {/* AR Button */}
        {(!currentObj.type || (currentObj.type === 'exhibit' && currentObj.linkedExhibitId)) && (
          currentObj.aiGuide?.enabled === true ? (
            <div className="flex items-center gap-3 w-full mb-5">
              <button
                onClick={() => {
                  if (onStopAction) {
                    onStopAction('immersive', currentItem, currentObj.title);
                  }
                  const flushElapsed = (Date.now() - entryTimeRef.current) / 1000;
                  if (flushElapsed > 1.5 && prevItemRef.current && onDwellTime) {
                    onDwellTime(prevItemRef.current.stopNumber, prevItemRef.current.title, Math.round(flushElapsed));
                  }
                  entryTimeRef.current = Date.now();
                  onStartAR(currentObj.linkedExhibitId, undefined, currentItem, currentObj.title);
                }}
                className="flex-1 min-w-0 h-14 bg-primary text-[#13213b] font-bold rounded-2xl flex items-center justify-center gap-2 shadow-[0_4px_16px_rgba(250,163,27,0.25)] active:scale-[0.98] transition-all uppercase tracking-[0.1em] text-sm whitespace-nowrap overflow-hidden"
              >
                <Box size={18} className="shrink-0" />
                <span className="truncate">Start AR</span>
              </button>

              {/* Stitch "PLAY" comp: center Play is always in the row (no scroll gating, no clipping).
                  Same handleTTS / progress / duration source as the retired top ring player. */}
              <div className="flex flex-col items-center justify-center shrink-0">
                <button
                  onClick={handleTTS}
                  disabled={isGenerating}
                  aria-label={isPlaying ? 'Pause audio' : 'Listen to audio narration'}
                  className="relative w-14 h-14 bg-[#15233D] text-primary rounded-full flex items-center justify-center hover:bg-white/10 active:scale-95 transition-all shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 shrink-0"
                >
                  <svg className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none" viewBox="0 0 52 52" aria-hidden="true">
                    <circle cx="26" cy="26" r="23" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="2.5" />
                    <circle cx="26" cy="26" r="23" fill="none" stroke="currentColor" strokeWidth="2.5" strokeDasharray="144.51" strokeDashoffset={144.51 * (progress / 100)} strokeLinecap="round" className="text-primary transition-[stroke-dashoffset] duration-200 motion-reduce:transition-none" />
                  </svg>
                  {isGenerating ? (
                    <Loader2 size={24} className="text-primary animate-spin relative z-10" />
                  ) : isPlaying ? (
                    <Pause size={24} className="text-primary relative z-10" fill="currentColor" />
                  ) : (
                    <Play size={24} className="text-primary relative z-10" fill="currentColor" />
                  )}
                </button>
                {(isPlaying || progress > 0) && duration > 0 && (
                  <span className="font-mono text-[10px] tracking-wider text-primary font-semibold mt-1 leading-none tabular-nums">
                    {formatListenRemaining()}
                  </span>
                )}
              </div>

              {/* Stitch "AR & AI Guide": AI GUIDE button with panel fill + hairline border + spark icon.
                  Handler untouched (stop-action log + onOpenAIGuide) — presentation only. */}
              <button
                onClick={() => {
                  if (onStopAction) {
                    onStopAction('immersive', currentItem, currentObj.title);
                  }
                  onOpenAIGuide(currentObj);
                }}
                className="flex-1 min-w-0 h-14 bg-[#15233D]/80 border border-primary/40 text-primary font-bold rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98] transition-all uppercase tracking-[0.1em] hover:bg-white/10 text-sm whitespace-nowrap overflow-hidden"
              >
                <Sparkles size={18} className="shrink-0" />
                <span className="truncate">AI Guide</span>
              </button>
            </div>
          ) : (
            <div className="flex gap-3 w-full mb-5">
              <button
                onClick={() => {
                  if (onStopAction) {
                    onStopAction('immersive', currentItem, currentObj.title);
                  }
                  const flushElapsed = (Date.now() - entryTimeRef.current) / 1000;
                  if (flushElapsed > 1.5 && prevItemRef.current && onDwellTime) {
                    onDwellTime(prevItemRef.current.stopNumber, prevItemRef.current.title, Math.round(flushElapsed));
                  }
                  entryTimeRef.current = Date.now();
                  onStartAR(currentObj.linkedExhibitId, undefined, currentItem, currentObj.title);
                }}
                className="flex-1 min-w-0 h-14 bg-primary text-[#13213b] font-bold rounded-2xl flex items-center justify-center gap-2 shadow-[0_4px_16px_rgba(250,163,27,0.25)] active:scale-[0.98] transition-all uppercase tracking-[0.1em] text-sm whitespace-nowrap overflow-hidden"
              >
                <Box size={18} className="shrink-0" />
                <span className="truncate">Start AR</span>
              </button>

              {/* Stitch "Interactive Audio Playback": LISTEN button with border progress ring + timer chip.
                  Same handleTTS / progress / duration source as the top ring player — presentation only. */}
              <button
                onClick={handleTTS}
                disabled={isGenerating}
                aria-label={isPlaying ? 'Pause audio' : 'Listen to audio narration'}
                className="relative flex-1 min-w-0 h-14 bg-[#15233D]/80 text-primary font-bold rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98] transition-all uppercase tracking-[0.1em] text-sm overflow-hidden whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 motion-reduce:transition-none"
              >
                <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" aria-hidden="true">
                  <rect x="0" y="0" width="100%" height="100%" rx="16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-white/10" />
                  <rect x="0" y="0" width="100%" height="100%" rx="16" fill="none" stroke="currentColor" strokeWidth="2" pathLength={100} strokeDasharray="100" strokeDashoffset={progress} strokeLinecap="round" className="text-primary transition-[stroke-dashoffset] duration-200 motion-reduce:transition-none" />
                </svg>
                <span className="relative z-10 flex items-center justify-center gap-2 px-2 min-w-0">
                  {isGenerating ? (
                    <Loader2 size={18} className="animate-spin shrink-0" />
                  ) : isPlaying ? (
                    <Pause size={18} fill="currentColor" className="shrink-0" />
                  ) : (
                    <Play size={18} fill="currentColor" className="shrink-0" />
                  )}
                  <span className="shrink-0">{isGenerating ? 'Loading' : isPlaying ? 'Pause' : 'Listen'}</span>
                  {(isPlaying || progress > 0) && duration > 0 && (
                    <span className="font-mono font-medium text-xs tracking-normal text-white bg-white/10 px-1.5 py-0.5 rounded ml-0.5 tabular-nums shrink-0">
                      {formatListenRemaining()}
                    </span>
                  )}
                </span>
              </button>
            </div>
          )
        )}

        {/* Progress line */}
        <div className="w-full h-[3px] bg-white/10 rounded-full mb-4 overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500"
            style={{ width: `${(currentItem / totalItems) * 100}%` }}
          />
        </div>

        {/* Navigation Controls (Stitch: check medallion + count below; grid keeps center fixed regardless of label widths) */}
        <div className="grid grid-cols-[1fr_auto_1fr] items-center">
          <button
            onClick={() => setCurrentItem(prev => Math.max(1, prev - 1))}
            disabled={currentItem === 1}
            className={`justify-self-start flex items-center gap-2 text-white font-bold text-xs uppercase tracking-widest transition-opacity py-2 -my-2 pr-2 ${currentItem === 1 ? 'opacity-10' : 'opacity-40 hover:opacity-100'}`}
          >
            <ChevronLeft size={16} />
            <span>Prev</span>
          </button>

          <div className="flex flex-col items-center gap-[2px]">
            <Check size={16} className="text-primary" />
            <span className="text-white/80 font-bold text-xs tabular-nums">{currentItem} / {totalItems}</span>
          </div>

          <button
            onClick={() => {
              if (currentItem < totalItems) {
                setCurrentItem(prev => prev + 1);
              } else {
                if (onProgressUpdate) {
                  onProgressUpdate(totalItems, totalItems, true);
                }
                onClose();
              }
            }}
            className="justify-self-end flex items-center gap-2 text-white font-bold text-xs uppercase tracking-widest hover:text-primary transition-colors py-2 -my-2 pl-2"
          >
            <span>{currentItem === totalItems ? 'Finish' : 'Next'}</span>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <ZoomableImageModal
        src={currentObj.img}
        alt={currentObj.title}
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
      />
    </div>
  );
};

const DEFAULT_YELLOW_AVATAR = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjZmJhMzFiIiBzdHJva2Utd2lkdGg9IjEuNSIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48Y2lyY2xlIGN4PSIxMiIgY3k9IjgiIHI9IjQiLz48cGF0aCBkPSJNNCAyMGE4IDggMCAwIDEgMTYgMCIvPjwvc3ZnPg==";

const compressImage = (base64Str: string, maxWidth = 160, maxHeight = 160): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = base64Str;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      } else {
        resolve(base64Str);
      }
    };
    img.onerror = () => {
      resolve(base64Str);
    };
  });
};

export default function App() {
  // ── AR Return State ─────────────────────────────────────────────────
  // Synchronously parse AR return info so screen/experienceId/museumId are set
  // on initial render. This takes the user directly back to the exhibition screen.
  const getInitialArReturnState = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      let expId = params.get('experience');
      let requestedScreen = params.get('screen') as Screen | null;
      let museumId = params.get('museum');

      if (!expId) {
        const raw = sessionStorage.getItem('comenius_ar_return');
        if (raw) {
          const state = JSON.parse(raw);
          // Extend AR return window from 60s to 15 minutes (900_000 ms) for long AR sessions
          if (Date.now() - (state.timestamp || 0) < 900_000) {
            expId = state.experience;
            requestedScreen = state.screen;
            museumId = state.museum;
          }
        }
      }

      if (expId) {
        const validScreens: Screen[] = ['itinerary', 'tour_object', 'experience', 'audio_player'];
        const targetScreen = (requestedScreen && validScreens.includes(requestedScreen)) ? requestedScreen : 'itinerary';
        return {
          experienceId: expId,
          museumId: museumId || 'embassy-of-the-free-mind',
          screen: targetScreen
        };
      }
    } catch {}
    return null;
  };

  const initialArReturnRef = useRef(getInitialArReturnState());
  const isReturningFromAR = useRef(!!initialArReturnRef.current);

  useEffect(() => {
    try {
      sessionStorage.removeItem('comenius_ar_return');
    } catch {}
  }, []);

  const [screen, setScreen] = useState<Screen>(() => {
    if (initialArReturnRef.current) {
      return initialArReturnRef.current.screen;
    }
    return 'splash';
  });
  const [direction, setDirection] = useState(1);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  // True after the first onAuthStateChanged fire — entry gate waits for this
  // instead of assuming auth state from splash timing alone.
  const [authReady, setAuthReady] = useState(false);
  const [userName, setUserName] = useState('Goksel');
  const [user, setUser] = useState<any>(null);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [avatarUrl, setAvatarUrl] = useState(() => {
    try {
      return localStorage.getItem('comenius_avatar_fallback') || DEFAULT_YELLOW_AVATAR;
    } catch (e) {
      return DEFAULT_YELLOW_AVATAR;
    }
  });
  const [myListExhibitions, setMyListExhibitions] = useState<string[]>([]);
  const [myListTab, setMyListTab] = useState<'continue' | 'saved' | 'completed'>('continue');
  const [arSceneId, setArSceneId] = useState<string | null>(null);
  const [arMode, setArMode] = useState<string | null>(null);
  const [aiGuideExhibit, setAiGuideExhibit] = useState<any | null>(null);
  const [isLandscape, setIsLandscape] = useState(false);

  // ── Host guard: never create marketplace anon on curator host ──────────
  const isCuratorHost = () => {
    try {
      const h = window.location.hostname;
      const p = window.location.pathname;
      return h === 'curator.c0menius.app' || h === 'curator-staging.c0menius.app' || p.startsWith('/curator') || p.startsWith('/studio');
    } catch { return false; }
  };

  // ── Analytics: anonymous visitor ID persisted in localStorage ──────────
  const analyticsUserIdRef = useRef<string>('');
  // ── Analytics: per-tour AR-interacted stop numbers (memory-only, distinguishes
  // genuine AR completion from Next-only browsing in route_complete eventData) ──
  const arVisitedStopsRef = useRef<Record<string, Set<number>>>({});
  const [analyticsUserId] = useState<string>(() => {
    try {
      // Curator host → no synthetic anon
      if (typeof window !== 'undefined' && (window.location.hostname === 'curator.c0menius.app' || window.location.pathname.startsWith('/curator') || window.location.pathname.startsWith('/studio'))) {
        const cur = 'curator-noop';
        analyticsUserIdRef.current = cur;
        return cur;
      }
      const stored = localStorage.getItem('analytics_user_id');
      if (stored) { analyticsUserIdRef.current = stored; return stored; }
      const newId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('analytics_user_id', newId);
      analyticsUserIdRef.current = newId;
      return newId;
    } catch {
      const id = `anon_${Date.now()}`;
      analyticsUserIdRef.current = id;
      return id;
    }
  });

  // Key: exhibitionId, Value: { currentItem: number, totalItems: number, completed: boolean }
  const [experienceProgress, setExperienceProgress] = useState<Record<string, { currentItem: number; totalItems: number; completed: boolean }>>(() => {
    try {
      const saved = localStorage.getItem('experience_progress');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    localStorage.setItem('experience_progress', JSON.stringify(experienceProgress));
    const uid = user?.uid || analyticsUserIdRef.current;
    if (uid && Object.keys(experienceProgress).length > 0) {
      setDoc(doc(db, 'user_progress', uid), {
        experiences: experienceProgress,
        updatedAt: serverTimestamp()
      }, { merge: true }).catch(err => console.warn('[Progress] Firestore sync error:', err));
    }
  }, [experienceProgress, user?.uid]);

  // Mirror for use inside effects without stale-closure / dependency issues
  const experienceProgressRef = useRef(experienceProgress);
  useEffect(() => {
    experienceProgressRef.current = experienceProgress;
  }, [experienceProgress]);

  // PWA Version Auto-Update Check
  // Runs on every app open and every time the app comes back to the foreground
  useEffect(() => {
    let checkTimeout: any = null;

    const checkVersion = async () => {
      // Skip version reload if OAuth popup/redirect is currently in flight
      if (localStorage.getItem('oauth_in_flight') === '1') {
        console.log('[PWA Update] Deferring version check — OAuth in flight');
        return;
      }
      // Only check when tab/app is visible
      if (document.visibilityState !== 'visible') return;

      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        const serverVersion = String(data.version);
        const localVersion = localStorage.getItem('app_version');

        if (localVersion && localVersion !== serverVersion) {
          // Double-check OAuth in-flight state right before reloading
          if (localStorage.getItem('oauth_in_flight') === '1') {
            console.log('[PWA Update] Suppressing reload — OAuth popup returning');
            return;
          }
          console.log('[PWA Update] New version detected, reloading:', serverVersion);
          localStorage.setItem('app_version', serverVersion);
          if (navigator.serviceWorker?.controller) {
            navigator.serviceWorker.controller.postMessage('SKIP_WAITING');
          }
          window.location.reload();
        } else if (!localVersion) {
          localStorage.setItem('app_version', serverVersion);
        }
      } catch (err) {
        // Silently ignore network errors (offline mode)
      }
    };

    // Check immediately on mount (every app open)
    checkVersion();

    // Check when app comes back to foreground, with a 3.5s delay to allow OAuth popups to settle
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (checkTimeout) clearTimeout(checkTimeout);
        checkTimeout = setTimeout(() => {
          checkVersion();
        }, 3500);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (checkTimeout) clearTimeout(checkTimeout);
    };
  }, []);

  // Service Worker Registration — enables offline caching and iOS cache bypass.
  // Local-dev escape hatch: on localhost the SW's stale-while-revalidate strategy
  // serves one-version-old modules, hiding fresh edits — so unregister any SW and skip.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const host = window.location.hostname;
    const isLocalDev = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
    if (isLocalDev) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        return Promise.all(registrations.map((r) => {
          console.log('[SW] Local dev — unregistering:', r.active?.scriptURL || r.scope);
          return r.unregister();
        }));
      }).catch((err) => console.warn('[SW] Local dev unregister failed:', err));
      return;
    }

    // First: unregister any stale SWs from a different script URL
    // This clears corrupted caches from previous deploys with wrong asset hashes
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      const expectedScriptURL = `${window.location.origin}/sw.js`;
      const stale = registrations.filter(
        (r) => r.active?.scriptURL && r.active.scriptURL !== expectedScriptURL
      );
      return Promise.all(stale.map((r) => {
        console.log('[SW] Unregistering stale SW:', r.active?.scriptURL);
        return r.unregister();
      }));
    }).then(() => {
      return navigator.serviceWorker.register('/sw.js');
    }).then((registration) => {
      console.log('[SW] Registered, scope:', registration.scope);
      // When a new SW version is found, activate it immediately
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New SW installed in background — tell it to activate now
            newWorker.postMessage('SKIP_WAITING');
            console.log('[SW] New version installed, activating...');
          }
        });
      });
      // CRITICAL: When a new SW takes control of the page (after SKIP_WAITING + clients.claim()),
      // reload the page so fresh JS bundles are loaded from the new cache.
      // Without this, the page continues running old JS even though the SW cache was updated.
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('[SW] Controller changed — reloading for fresh assets');
        window.location.reload();
      });
    }).catch((err) => {
      console.warn('[SW] Registration failed:', err);
    });
  }, []);


  const handleProgressUpdate = (expId: string, mId: string, current: number, total: number, completed: boolean) => {
    setExperienceProgress(prev => {
      const existing = prev[expId];
      const wasCompleted = existing?.completed || false;

      if (existing && existing.completed && !completed && current === 1) {
        // Restarted tour — reset to in-progress (and clear AR-interaction memory)
        delete arVisitedStopsRef.current[expId];
        return {
          ...prev,
          [expId]: { currentItem: current, totalItems: total, completed: false, completedStops: [1] }
        };
      }

      const newCurrent = existing ? Math.max(existing.currentItem, current) : current;
      const newCompleted = completed || (existing ? existing.completed : false);

      const prevStops = new Set<number>(existing?.completedStops || []);
      for (let i = 1; i < current; i++) {
        prevStops.add(i);
      }
      if (arVisitedStopsRef.current[expId]) {
        arVisitedStopsRef.current[expId].forEach(s => prevStops.add(s));
      }
      if (newCompleted) {
        for (let i = 1; i <= total; i++) {
          prevStops.add(i);
        }
      }
      const completedStopsArr = Array.from(prevStops).sort((a, b) => a - b);

      // Fire route_complete exactly once when the tour finishes (skipping in preview mode)
      if (newCompleted && !wasCompleted && !isUniversalPreviewSession()) {
        const arStopsVisited = arVisitedStopsRef.current[expId]?.size || 0;
        addDoc(collection(db, 'analytics_events'), {
          eventType: 'route_complete',
          eventData: { stopNumber: total, arStopsVisited, hadArInteraction: arStopsVisited > 0 },
          exhibitionId: expId,
          museumId: mId,
          userId: analyticsUserIdRef.current,
          timestamp: serverTimestamp()
        }).catch(e => console.warn('[Analytics] route_complete failed:', e));
      }

      return {
        ...prev,
        [expId]: {
          currentItem: newCurrent,
          totalItems: total,
          completed: newCompleted,
          completedStops: completedStopsArr
        }
      };
    });
  };

  const [pendingRewardRule, setPendingRewardRule] = useState<any | null>(null);
  const [showRewardUnlock, setShowRewardUnlock] = useState(false);
  const [showAudioMemberGate, setShowAudioMemberGate] = useState(false);
  const [isAudioMemberSigningIn, setIsAudioMemberSigningIn] = useState(false);
  const [audioMemberSignInError, setAudioMemberSignInError] = useState<string | null>(null);
  const [showMemberPerksModal, setShowMemberPerksModal] = useState(false);
  const [claimedRewardDoc, setClaimedRewardDoc] = useState<any | null>(null);
  const [isClaiming, setIsClaiming] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [emailForClaim, setEmailForClaim] = useState('');

  // Single authoritative completion path. Called only after the visitor
  // confirms the End Tour action in the viewer. The viewer never writes
  // progress/completed state itself — it only signals via
  // FINISH_EXPERIENCE postMessage or the tourCompleted=1 return param.
  const completeTour = useCallback(async (expId: string, mId: string) => {
    const p = experienceProgressRef.current[expId];
    handleProgressUpdate(expId, mId, p?.currentItem || p?.totalItems || 1, p?.totalItems || 1, true);
    setToursInProgress(prev => {
      if (!prev.has(expId)) return prev;
      const next = new Set(prev);
      next.delete(expId);
      return next;
    });

    // Search for active reward rules matching this experience or any experience
    try {
      const snap = await getDocs(collection(db, 'rewards_rules'));
      console.log(`[Rewards] completeTour called — expId="${expId}" museumId="${mId}" — found ${snap.docs.length} total rules`);
      snap.docs.forEach(d => {
        const data = d.data();
        console.log(`[Rewards]  rule=${d.id} status=${data.status} conditionType=${data.conditionType} conditionValue=${data.conditionValue}`);
      });

      const eligibleDoc = snap.docs.find(d => {
        const data = d.data();
        const isActive = data.status === 'active' || data.status === undefined || data.active === true;
        if (!isActive) return false;

        const condType = data.conditionType || 'experience_complete';
        const condVal = (data.conditionValue || '').trim();

        // 1. Exact exhibitionId match
        if (condVal === expId || condVal === expId.trim()) return true;

        // 2. Wildcard or any_experience_complete
        if (condType === 'any_experience_complete' || condVal === 'any' || condVal === '') return true;

        // 3. Same museum match: if rule belongs to the same museum (or global), match active rule
        if (!data.museumId || data.museumId === mId) return true;

        return false;
      });

      console.log('[Rewards] eligibleDoc:', eligibleDoc?.id ?? 'none');

      if (eligibleDoc) {
        // If authenticated user, check if this reward rule was already claimed
        if (auth.currentUser && !auth.currentUser.isAnonymous) {
          try {
            const claimDocRef = doc(db, 'user_rewards', `${auth.currentUser.uid}_${eligibleDoc.id}`);
            const claimSnap = await getDoc(claimDocRef);
            if (claimSnap.exists()) {
              console.log(`[Rewards] User ${auth.currentUser.uid} already claimed rule ${eligibleDoc.id} — skipping unlock modal`);
              setShowFeedback(true);
              return;
            }
          } catch (checkErr) {
            console.warn('[Rewards] Error checking previous claim:', checkErr);
          }
        }

        setPendingRewardRule({ id: eligibleDoc.id, ...eligibleDoc.data() });
        setShowRewardUnlock(true);
        return;
      }
    } catch (e) {
      console.warn('[Rewards] Error checking eligible rules:', e);
    }

    if (auth.currentUser && !auth.currentUser.isAnonymous) {
      setShowFeedback(true);
    } else {
      setShowMemberPerksModal(true);
    }
  }, []);

  const [showFeedback, setShowFeedback] = useState(false);
  const [toursInProgress, setToursInProgress] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('comenius_tours_in_progress');
      const arr = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(arr)) return new Set();
      let prog: Record<string, any> = {};
      try {
        prog = JSON.parse(localStorage.getItem('experience_progress') || '{}');
      } catch {}
      return new Set(arr.filter((id: any) => typeof id === 'string' && id && !(prog?.[id]?.completed === true)));
    } catch {
      return new Set();
    }
  });

  // Persist in-progress tours across PWA cold restarts.
  // Declared AFTER the toursInProgress state so the dependency array never
  // references a const before its initialization (TDZ).
  // `experienceProgress.completed` stays authoritative: completed tours are
  // filtered out on hydration so they can never resurface as "Continue".
  useEffect(() => {
    try {
      localStorage.setItem('comenius_tours_in_progress', JSON.stringify(Array.from(toursInProgress)));
    } catch {}
  }, [toursInProgress]);

  const [selectedReward, setSelectedReward] = useState<any>(null);
  const [selectedMuseumId, setSelectedMuseumIdState] = useState<string>(() => {
    return initialArReturnRef.current?.museumId || sessionStorage.getItem('comenius_selected_museum_id') || 'embassy-of-the-free-mind';
  });
  const setSelectedMuseumId = (id: string) => {
    setSelectedMuseumIdState(id);
    try {
      if (id) sessionStorage.setItem('comenius_selected_museum_id', id);
      else sessionStorage.removeItem('comenius_selected_museum_id');
    } catch (e) {}
  };
  const [selectedExperienceId, setSelectedExperienceId] = useState<string>(() => {
    return initialArReturnRef.current?.experienceId || '';
  });

  const [tourItinerary, setTourItinerary] = useState<any[]>([]);
  const [linkedArSceneId, setLinkedArSceneId] = useState<string | null>(null);
  const [isItineraryLoading, setIsItineraryLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState<'newly' | 'popular' | 'nearby'>('newly');
  const [userCoords, setUserCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [userObjectsDiscovered, setUserObjectsDiscovered] = useState(0);

  useEffect(() => {
    if (!auth.currentUser) {
      setUserObjectsDiscovered(0);
      return;
    }
    const uid = auth.currentUser.uid;
    queryClient.fetchQuery({
      queryKey: ['userStats', uid],
      queryFn: async () => {
        const q = query(
          collection(db, 'analytics_events'),
          where('userId', '==', uid),
          where('eventType', 'in', ['stop_view', 'direct_scan']),
          limit(500)
        );
        const snap = await getDocs(q);
        const uniqueExhibitions = new Set(snap.docs.map(d => d.data().exhibitionId).filter(Boolean));
        return uniqueExhibitions.size;
      }
    }).then((count) => {
      setUserObjectsDiscovered(count);
    }).catch(() => {
      setUserObjectsDiscovered(0);
    });
  }, [auth.currentUser?.uid]);

  const userRank = getRank(userObjectsDiscovered);

  const triggerGeolocation = (onSuccess?: (coords: { lat: number; lon: number }, countryCode: string) => void) => {
    if (userCoords) {
      if (onSuccess && userCountry) {
        onSuccess(userCoords, userCountry);
      }
      return;
    }

    setIsLocating(true);
    setLocationError(null);

    const getNearestCountry = (lat: number, lon: number) => {
      let minDistance = Infinity;
      let nearestCountry = 'NL';
      MUSEUMS.forEach(m => {
        const dist = calculateDistance(lat, lon, m.lat || 0, m.lon || 0);
        if (dist < minDistance) {
          minDistance = dist;
          nearestCountry = m.countryCode || 'NL';
        }
      });
      return nearestCountry;
    };

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        const coords = { lat, lon };
        setUserCoords(coords);

        let resolvedCountry = 'NL';
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);
          
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`,
            { signal: controller.signal }
          );
          clearTimeout(timeoutId);
          
          if (!response.ok) throw new Error("Nominatim reverse geocode request failed");
          
          const data = await response.json();
          const countryCode = data.address?.country_code?.toUpperCase();
          if (countryCode) {
            resolvedCountry = countryCode;
            setUserCountry(countryCode);
          } else {
            throw new Error("No country code returned");
          }
        } catch (err) {
          console.warn("Nominatim reverse geocoding failed, falling back to closest museum country:", err);
          resolvedCountry = getNearestCountry(lat, lon);
          setUserCountry(resolvedCountry);
        } finally {
          setIsLocating(false);
          if (onSuccess) {
            onSuccess(coords, resolvedCountry);
          }
        }
      },
      async (error) => {
        console.warn("Browser geolocation failed, attempting IP-based geolocation fallback...", error);
        let fallbackCoords = { lat: 51.5161, lon: -0.1213 };
        let fallbackCountry = 'GB';
        try {
          const response = await fetch("https://ipapi.co/json/");
          if (!response.ok) throw new Error("IP API lookup failed");
          const ipData = await response.json();
          if (ipData.latitude && ipData.longitude) {
            fallbackCoords = { lat: ipData.latitude, lon: ipData.longitude };
            fallbackCountry = ipData.country_code?.toUpperCase() || 'GB';
            setUserCoords(fallbackCoords);
            setUserCountry(fallbackCountry);
            console.log("IP-based geolocation succeeded:", ipData);
          } else {
            throw new Error("No coordinates in IP response");
          }
        } catch (ipErr) {
          console.error("IP geolocation fallback failed, using hardcoded default:", ipErr);
          setLocationError(`Location services unavailable (${error.message}). Showing closest museums from London, UK.`);
          setUserCoords(fallbackCoords);
          setUserCountry(fallbackCountry);
        } finally {
          setIsLocating(false);
          if (onSuccess) {
            onSuccess(fallbackCoords, fallbackCountry);
          }
        }
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  };

  // Helper to detect universal preview sessions (editor, previewConsole, iframe, or curator session)
  const isUniversalPreviewSession = useCallback(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const hasPreviewParam = params.get('previewConsole') === '1' ||
                              params.get('mode') === 'preview' ||
                              params.get('mode') === 'view' ||
                              params.get('isPreview') === 'true';
      const isIframe = window.self !== window.top;
      const isCuratorPath = window.location.pathname.startsWith('/admin') ||
                           (window.location.pathname.startsWith('/viewer') && params.get('origin') !== 'marketplace');
      const isCuratorSession = sessionStorage.getItem('is_curator_preview') === 'true';

      return hasPreviewParam || isIframe || isCuratorPath || isCuratorSession;
    } catch {
      return false;
    }
  }, []);

  // ── logAnalyticsEvent: writes a single event document to Firestore ───────
  const logAnalyticsEvent = useCallback(async (
    eventType: string,
    eventData: Record<string, any> = {},
    overrideExhibitionId?: string,
    overrideMuseumId?: string
  ) => {
    try {
      if (isUniversalPreviewSession()) {
        console.log('[Analytics] Skipping telemetry write (Universal Preview Session active):', eventType);
        return;
      }
      // Clean undefined fields so Firestore addDoc never throws invalid-argument
      const sanitizedData = JSON.parse(JSON.stringify(eventData || {}));
      const userType = auth.currentUser && !auth.currentUser.isAnonymous ? 'user' : 'guest';
      await addDoc(collection(db, 'analytics_events'), {
        eventType,
        eventData: sanitizedData,
        userType,
        exhibitionId: overrideExhibitionId ?? selectedExperienceId,
        museumId: overrideMuseumId ?? selectedMuseumId,
        userId: analyticsUserIdRef.current,
        timestamp: serverTimestamp()
      });
    } catch (err) {
      console.warn('[Analytics] Failed to log event:', err);
    }
  }, [selectedExperienceId, selectedMuseumId, isUniversalPreviewSession]);

  // WebAR Viewer postmessage listener
  // Declared AFTER completeTour, selectedExperienceId, selectedMuseumId AND
  // logAnalyticsEvent so the dependency array never references a const before
  // initialization (TDZ).
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && (event.data === 'CLOSE_AR' || event.data.type === 'CLOSE_AR' || event.data.type === 'close-ar')) {
        console.log("[Marketplace] Received CLOSE_AR message from WebAR viewer");
        setArSceneId(null);
      } else if (event.data && event.data.type === 'FINISH_EXPERIENCE') {
        console.log("[Marketplace] Received FINISH_EXPERIENCE message from WebAR viewer");
        if (selectedExperienceId) {
          completeTour(selectedExperienceId, selectedMuseumId);
        }
      } else if (event.data && event.data.type === 'DWELL_TIME') {
        console.log("[Marketplace] Received DWELL_TIME message from WebAR viewer");
        const d = event.data;
        if (selectedExperienceId && typeof d.durationSeconds === 'number' && d.durationSeconds > 0) {
          logAnalyticsEvent('stop_view', {
            stopNumber: typeof d.stopNumber === 'number' && d.stopNumber >= 1 ? d.stopNumber : 1,
            itemName: d.itemName || '',
            durationSeconds: Math.round(d.durationSeconds),
            source: 'ar',
          }, selectedExperienceId, selectedMuseumId);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [completeTour, selectedExperienceId, selectedMuseumId, logAnalyticsEvent]);

  // Heartbeat — updates active_sessions every 30 s while the app is open
  useEffect(() => {
    if (!isLoggedIn) return;
    const sessionId = analyticsUserIdRef.current || 'anon';
    const sessionDoc = doc(db, 'active_sessions', sessionId);
    const updateSession = async () => {
      const museumScreens = ['museum_detail', 'itinerary', 'tour_object', 'scanner', 'experience', 'audio_player'];
      // Only count as inside a specific experience when on experience-level screens
      const experienceScreens = ['experience', 'itinerary', 'tour_object', 'scanner', 'audio_player'];
      const museumId = museumScreens.includes(screen) ? (selectedMuseumId || 'none') : 'none';
      const exhibitionId = experienceScreens.includes(screen) ? (selectedExperienceId || 'none') : 'none';
      try {
        await setDoc(sessionDoc, {
          userId: sessionId,
          currentMuseumId: museumId,
          currentExhibitionId: exhibitionId,
          currentScreen: screen,
          lastActive: serverTimestamp()
        });
      } catch (err) {
        console.warn('[Analytics] Heartbeat failed:', err);
      }
    };
    updateSession();
    const interval = setInterval(updateSession, 3000);
    return () => {
      clearInterval(interval);
    };
  }, [isLoggedIn, selectedExperienceId, selectedMuseumId, screen]);

  useEffect(() => {
    // Magic link sign in return handler
    if (isSignInWithEmailLink(auth, window.location.href)) {
      let email = window.localStorage.getItem('emailForSignIn');
      if (!email) {
        email = window.prompt('Lütfen doğrulama için e-posta adresinizi giriniz:');
      }
      if (email) {
        signInWithEmailLink(auth, email, window.location.href)
          .then(async (result) => {
            window.localStorage.removeItem('emailForSignIn');
            if (result.user) {
              // Pre-launch: skip the profile doc for non-admin sessions
              // (the auth listener signs them out right after).
              if (isPrelaunchAdminEmail(email)) {
                await setDoc(doc(db, 'users', result.user.uid), {
                  email: result.user.email,
                  firstName: email.split('@')[0] || 'Explorer',
                  createdAt: serverTimestamp()
                }, { merge: true });
              }
            }
          })
          .catch(err => console.error('Magic link sign in error:', err));
      }
    }
  }, []);

  useEffect(() => {
    // Root path device-based redirect (/ -> /curator or /marketplace)
    if (window.location.pathname === '/' || window.location.pathname === '') {
      const isVisitor = window.location.hostname === 'visitor.c0menius.app';
      const isMobileDevice = () => {
        const ua = (navigator.userAgent || '').toLowerCase();
        if (ua.indexOf('android') !== -1 || ua.indexOf('mobi') !== -1 || ua.indexOf('iphone') !== -1 || ua.indexOf('ipad') !== -1 || ua.indexOf('ipod') !== -1) return true;
        if (navigator.userAgentData && typeof (navigator as any).userAgentData.mobile === 'boolean') {
          return (navigator as any).userAgentData.mobile;
        }
        if (ua.indexOf('windows nt') !== -1 || ua.indexOf('macintosh') !== -1 || ua.indexOf('x11') !== -1 || ua.indexOf('linux x86_64') !== -1) return false;
        return window.innerWidth < 768;
      };
      const mobile = isMobileDevice();
      if (isVisitor) {
        if (mobile) window.location.replace('/marketplace');
      } else {
        window.location.replace(mobile ? '/marketplace' : '/curator');
      }
    }
  }, []);

const extractFirstName = (user: any): string => {
  if (user?.displayName && user.displayName.trim() !== '') {
    const parts = user.displayName.trim().split(' ');
    if (parts[0] && parts[0].toLowerCase() !== 'explorer' && parts[0].toLowerCase() !== 'guest') {
      return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    }
  }
  if (user?.email && user.email.includes('@')) {
    const nameFromEmail = user.email.split('@')[0].split('.')[0].split('_')[0];
    if (nameFromEmail && nameFromEmail.trim() !== '') {
      return nameFromEmail.charAt(0).toUpperCase() + nameFromEmail.slice(1);
    }
  }
  return 'Explorer';
};

  // Handle returning OAuth redirects (iOS Safari safe flow)
  useEffect(() => {
    getRedirectResult(auth)
      .then(async (result) => {
        if (result && result.user) {
          localStorage.removeItem('oauth_in_flight');
          const user = result.user;
          const resolvedName = extractFirstName(user);
          const resolvedAvatar = user.photoURL || DEFAULT_YELLOW_AVATAR;

          setUser(user);
          setIsLoggedIn(true);
          analyticsUserIdRef.current = user.uid;
          setUserName(resolvedName);
          setAvatarUrl(resolvedAvatar);

          localStorage.setItem(`user_name_${user.uid}`, resolvedName);
          localStorage.setItem(`user_avatar_${user.uid}`, resolvedAvatar);

          // Pre-launch: skip the profile doc for non-admin sessions
          // (the auth listener signs them out right after).
          if (isPrelaunchAdminUser(user)) {
            await setDoc(doc(db, 'users', user.uid), {
              email: user.email || '',
              firstName: resolvedName,
              avatarUrl: resolvedAvatar,
              createdAt: serverTimestamp()
            }, { merge: true });
          }

          // Auto-claim pending reward if returning from iOS redirect
          try {
            const rawPending = localStorage.getItem('pending_reward_claim');
            if (rawPending) {
              localStorage.removeItem('pending_reward_claim');
              const { ruleId, exhibitionId, previousAnonUid } = JSON.parse(rawPending);
              const claimFn = httpsCallable(functions, 'claimReward');
              const claimRes: any = await claimFn({
                ruleId,
                exhibitionId,
                previousAnonUid
              });
              if (claimRes.data) {
                setClaimedRewardDoc(claimRes.data);
                setShowRewardUnlock(true);
              }
            }
          } catch (claimErr) {
            console.warn('[Auth] Error auto-claiming pending reward:', claimErr);
          }

          setScreen('home');
        } else {
          // No redirect result — do NOT sign in anonymously here.
          // onAuthStateChanged is the single source of truth for auth state
          // and will fire with the restored user (or null) shortly after.
          localStorage.removeItem('oauth_in_flight');
        }
      })
      .catch((err) => {
        localStorage.removeItem('oauth_in_flight');
        console.warn('[Auth] Redirect result error:', err);
        // Do NOT fall back to anonymous here — let onAuthStateChanged handle it
      });
  }, []);

  const anonSignInTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      // Clear any pending deferred anonymous sign-in timer on auth state update
      if (anonSignInTimerRef.current) {
        clearTimeout(anonSignInTimerRef.current);
        anonSignInTimerRef.current = null;
      }
      // First fire (any state) unblocks the splash entry gate
      setAuthReady(true);

      setUser(currentUser);
      // ── Pre-launch gate ────────────────────────────────────────────────
      // Until the public Visitor launch (~2027) only allowlisted admins may
      // hold an authenticated session. Non-admin sessions are signed out
      // here immediately (stray/pre-gate accounts included) and routed to
      // the newsletter screen. Firestore rules remain the data backstop.
      // Anonymous guests and QR/deep-link test entries are untouched.
      if (currentUser && !currentUser.isAnonymous && !isPrelaunchAdminUser(currentUser)) {
        try { await auth.signOut(); } catch {}
        if (!isQrEntry()) {
          setScreen('prelaunch');
        } else {
          try { sessionStorage.setItem('comenius_guest_entry', '1'); } catch {}
        }
        return;
      }
      if (currentUser?.uid) {
        // Sync user_progress from Firestore
        try {
          const progRef = doc(db, 'user_progress', currentUser.uid);
          getDoc(progRef).then(snap => {
            if (snap.exists()) {
              const cloudProgress = snap.data()?.experiences || {};
              setExperienceProgress(prev => {
                const merged = { ...cloudProgress, ...prev };
                Object.keys(cloudProgress).forEach(expId => {
                  const cloudItem = cloudProgress[expId];
                  const localItem = prev[expId];
                  if (cloudItem && localItem) {
                    const mergedStops = Array.from(new Set([
                      ...(cloudItem.completedStops || []),
                      ...(localItem.completedStops || [])
                    ])).sort((a: number, b: number) => a - b);

                    merged[expId] = {
                      currentItem: Math.max(cloudItem.currentItem || 1, localItem.currentItem || 1),
                      totalItems: cloudItem.totalItems || localItem.totalItems || 1,
                      completed: cloudItem.completed || localItem.completed || false,
                      completedStops: mergedStops
                    };
                  }
                });
                try {
                  localStorage.setItem('experience_progress', JSON.stringify(merged));
                } catch {}
                return merged;
              });
            }
          }).catch(err => console.warn('[Auth] Error querying cloud progress:', err));
        } catch (err) {
          console.warn('[Auth] Cloud progress error:', err);
        }
      }

      if (currentUser && !currentUser.isAnonymous) {
        setIsLoggedIn(true);
        analyticsUserIdRef.current = currentUser.uid;
        
        // Immediate profile resolution from localStorage or extractFirstName
        const cachedName = localStorage.getItem(`user_name_${currentUser.uid}`);
        const cachedAvatar = localStorage.getItem(`user_avatar_${currentUser.uid}`);
        const fallbackName = extractFirstName(currentUser);
        const fallbackAvatar = currentUser.photoURL || DEFAULT_YELLOW_AVATAR;

        const initialName = cachedName || fallbackName;
        const initialAvatar = cachedAvatar || fallbackAvatar;

        setUserName(initialName);
        setAvatarUrl(initialAvatar);

        const pendingName = localStorage.getItem('pending_firstName');
        if (pendingName) {
          setUserName(pendingName);
          localStorage.removeItem('pending_firstName');
          setAvatarUrl(DEFAULT_YELLOW_AVATAR);
        } else {
          try {
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            if (userDoc.exists()) {
              const data = userDoc.data();
              const realName = (data.firstName && data.firstName !== 'Explorer' && data.firstName !== 'Guest') 
                ? data.firstName 
                : fallbackName;
              const realAvatar = data.avatarUrl || fallbackAvatar;

              setUserName(realName);
              setAvatarUrl(realAvatar);

              localStorage.setItem(`user_name_${currentUser.uid}`, realName);
              localStorage.setItem(`user_avatar_${currentUser.uid}`, realAvatar);
            }
          } catch (err) {
            console.warn('[Auth] Error fetching user doc:', err);
          }
        }
        
        // Auto-navigate away from the auth/prelaunch screen after sign-in.
        // Honors a pending gated tab (e.g. guest tapped PROFILE then signed in).
        setScreen(prev => {
          if (prev === 'auth' || prev === 'prelaunch') {
            const next = pendingTabRef.current || 'home';
            pendingTabRef.current = null;
            return next;
          }
          return prev;
        });
      } else if (currentUser && currentUser.isAnonymous) {
        // User already has an active anonymous account — preserve session UID, do NOT call signInAnonymously again
        setIsLoggedIn(false);
        setUserName('Guest');
        setAvatarUrl(DEFAULT_YELLOW_AVATAR);
        analyticsUserIdRef.current = currentUser.uid;
      } else {
        // Curator host → never create marketplace anon (prevents curator → marketplace pollution)
        if (isCuratorHost()) {
          console.log('[Auth] Curator host detected — suppressing marketplace anonymous sign-in');
          return;
        }
        // Session is strictly null — check if OAuth redirect is in flight
        if (localStorage.getItem('oauth_in_flight') === '1') {
          console.log('[Auth] Deferring anonymous sign-in — OAuth redirect in flight');
          return;
        }

        setIsLoggedIn(false);
        setUserName('Guest');
        setAvatarUrl(DEFAULT_YELLOW_AVATAR);

        // Check if returning from AR (URL param or recent sessionStorage timestamp)
        const params = new URLSearchParams(window.location.search);
        const isArReturnParam = params.get('returnFromAR') === '1';
        let isArReturnSession = false;
        try {
          const raw = sessionStorage.getItem('comenius_ar_return');
          if (raw) {
            const state = JSON.parse(raw);
            if (Date.now() - (state.timestamp || 0) < 900_000) isArReturnSession = true;
          }
        } catch {}
        const isArReturn = isArReturnParam || isArReturnSession;

        // Check if device has a cached user identity from a previous real sign-in
        let hasRealUserCache = false;
        try {
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('user_name_')) {
              hasRealUserCache = true;
              break;
            }
          }
        } catch {}

        if (isArReturn) {
          // Returning from AR on iOS PWA: IndexedDB session restoration can be delayed.
          // Do NOT call signInAnonymously — it would clobber the stored Google credentials!
          console.log('[Auth] AR return detected — suppressing anonymous sign-in to preserve stored Google session');
          return;
        }

        if (hasRealUserCache) {
          // Returning visitor with cached real credentials: log status, do NOT sign in anonymously
          console.log('[Auth] Cached real user identity found — awaiting IndexedDB session restoration');
          return;
        }

        // Fresh guest visitor: keep session unauthenticated for browsing.
        // Anonymous Auth will only be created ON-DEMAND when starting an AR experience or interactive session.
        console.log('[Auth] Guest visitor session initialized — lazy anonymous auth active');
      }
    });
    return () => {
      unsubscribe();
      if (anonSignInTimerRef.current) {
        clearTimeout(anonSignInTimerRef.current);
        anonSignInTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    queryClient.fetchQuery({
      queryKey: ['collections'],
      queryFn: async () => {
        console.log("[Firebase] Fetching museums and exhibitions from Firestore...");
        
        let dbMuseums: any[] = [];
        try {
          const museumsSnap = await getDocs(query(collection(db, 'museums'), limit(50)));
          dbMuseums = museumsSnap.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              createdAt: data.createdAt || new Date().toISOString(),
              countryCode: data.countryCode || 'NL',
              lat: data.lat !== undefined ? data.lat : 52.3756,
              lon: data.lon !== undefined ? data.lon : 4.8878,
              ...data
            };
          });
          if (dbMuseums.length > 0) {
            console.log("[Firebase] Loaded museums:", dbMuseums);
            MUSEUMS = dbMuseums as any;
          }
        } catch (err) {
          console.warn("[Firebase] Error loading museums collection (expected if permissions are denied):", err);
        }

        try {
          const expsSnap = await getDocs(query(collection(db, 'exhibitions'), limit(50)));
          const dbExps = expsSnap.docs.map(doc => {
            const data = doc.data();
            
            let type = data.type;
            if (!type) {
              const lowerId = doc.id.toLowerCase();
              const lowerTitle = (data.title || "").toLowerCase();
              if (lowerId.includes('hermetic') || lowerTitle.includes('hermetic')) {
                type = 'immersive';
              } else if (lowerId.includes('source') || lowerTitle.includes('source')) {
                type = 'route';
              } else if (lowerId.includes('voices') || lowerTitle.includes('voices')) {
                type = 'audio';
              } else {
                type = 'immersive';
              }
            }

            let museumId = data.museumId;
            if (!museumId) {
              if (data.museum === 'EFM') {
                museumId = 'embassy-of-the-free-mind';
              } else if (data.museum === 'UGLE') {
                museumId = 'united-grand-lodge';
              } else {
                museumId = 'embassy-of-the-free-mind';
              }
            }

            return {
              id: doc.id,
              rating: data.rating || 4.5,
              ...data,
              type,
              museumId,
              isDemo: data.isDemo || false,
              status: data.status || "draft"
            };
          });

          if (dbExps.length > 0) {
            console.log("[Firebase] Loaded exhibitions:", dbExps);
            EXPERIENCES = dbExps as any;
            
            const grouped: Record<string, any[]> = {};
            const defaultMuseumId = MUSEUMS.length > 0 ? MUSEUMS[0].id : 'embassy-of-the-free-mind';
            dbExps.forEach(exp => {
              const mId = (exp as any).museumId || defaultMuseumId;
              if (!grouped[mId]) {
                grouped[mId] = [];
              }
              grouped[mId].push(exp);
            });
            MUSEUM_EXPERIENCES = grouped;
          }
        } catch (err) {
          console.error("[Firebase] Error loading exhibitions collection:", err);
        }

        MUSEUMS = MUSEUMS.map(m => {
          const exps = MUSEUM_EXPERIENCES[m.id] || [];
          // Publication gating: only publicly visible exhibitions count (live, or demo unless unlisted)
          const visible = exps.filter((e: any) => e.status === 'live' || (e.isDemo === true && e.status !== 'unlisted'));
          return {
            ...m,
            counts: {
              ar: visible.filter((e: any) => e.type === 'immersive' || e.type === 'ar').length,
              route: visible.filter((e: any) => e.type === 'route').length,
              audio: visible.filter((e: any) => e.type === 'audio').length
            }
          };
        });

        return { MUSEUMS, EXPERIENCES, MUSEUM_EXPERIENCES };
      },
      staleTime: 5 * 60 * 1000,
    })
    // setDataLoaded is in .then() — fires even when queryClient serves cached
    // data without re-running queryFn (e.g. PWA WebView suspend/resume).
    .then(() => setDataLoaded(true))
    .catch(err => {
      console.error("[Firebase] Collections fetch failed:", err);
      setDataLoaded(true); // ensure deep-link effect still fires even on error
    });
  }, []);

  // Safety-net: force dataLoaded after 6 s so the spinner never gets stuck
  useEffect(() => {
    if (dataLoaded) return;
    const t = setTimeout(() => setDataLoaded(true), 6000);
    return () => clearTimeout(t);
  }, [dataLoaded]);

  // ── Deep link: parse /marketplace?experience=&museum= on load ──────────
  useEffect(() => {
    if (!dataLoaded) return;
    // Skip deep-link navigation if returning from OAuth redirect — let auth flow land on home
    if (localStorage.getItem('oauth_in_flight') === '1') {
      console.log('[DeepLink] Skipping — OAuth redirect in flight');
      return;
    }
    try {
      const params = new URLSearchParams(window.location.search);
      // Also accept exhibitionId (used by viewer QR fallback URL)
      let expId         = params.get('experience') || params.get('exhibitionId');
      let requestedScreen = params.get('screen');
      let targetMuseum  = params.get('museum') || params.get('mid');
      const tourCompleted = params.get('tourCompleted') === '1';
      const dwellSeconds = parseInt(params.get('comenius_dwell') || '', 10);
      const dwellStop = parseInt(params.get('comenius_stop') || '', 10);
      const dwellItem = params.get('comenius_item') || '';

      // ── sessionStorage fallback (reliable in iOS PWA) ────────────────
      if (!expId) {
        const raw = sessionStorage.getItem('comenius_ar_return');
        if (raw) {
          try {
            const state = JSON.parse(raw);
            // Extend window from 30s to 15 minutes (900_000 ms) for long AR sessions
            if (Date.now() - (state.timestamp || 0) < 900_000) {
              expId           = state.experience;
              requestedScreen = state.screen;
              targetMuseum    = state.museum;
            }
          } catch {}
          sessionStorage.removeItem('comenius_ar_return');
        }
      } else {
        // URL params found — clear any stale sessionStorage key
        sessionStorage.removeItem('comenius_ar_return');
      }

      // AR return state already handled synchronously by getInitialArReturnState

      if (!expId) return;
      const exp = EXPERIENCES.find(e => e.id === expId);
      const resolvedMuseum = (exp as any)?.museumId || targetMuseum || selectedMuseumId || MUSEUMS[0]?.id || 'embassy-of-the-free-mind';
      setSelectedMuseumId(resolvedMuseum);
      setSelectedExperienceId(expId);

      // Trigger tour completion whenever tourCompleted=1 is present in the return URL
      if (tourCompleted) {
        completeTour(expId, resolvedMuseum);
      }

      // Support return-to-screen from AR viewer via ?screen=itinerary|tour_object|experience|audio_player
      const validScreens: Screen[] = ['audio_player', 'experience', 'itinerary', 'tour_object'];
      // Always open itinerary directly from deep-links (skip the redundant 'experience' detail screen).
      // Audio type is the only exception.
      let target: Screen = exp?.type === 'audio' ? 'audio_player' : 'itinerary';
      if (requestedScreen && validScreens.includes(requestedScreen as Screen)) {
        target = requestedScreen as Screen;
      }

      if (!tourCompleted && !experienceProgressRef.current[expId]?.completed) {
        setToursInProgress(prev => new Set(prev).add(expId));
      }

      setDirection(1);
      setScreen(target);
      if (!Number.isNaN(dwellSeconds) && dwellSeconds > 0) {
        logAnalyticsEvent('stop_view', {
          stopNumber: Number.isNaN(dwellStop) || dwellStop < 1 ? 1 : dwellStop,
          itemName: dwellItem || exp.title || '',
          durationSeconds: dwellSeconds,
          source: 'ar',
        }, expId, resolvedMuseum);
      }
      window.history.replaceState({}, '', window.location.pathname);
      console.log(`[Deep link] Opened experience ${expId} on screen ${target}`);
    } catch (err) {

      console.warn('[Deep link] Failed to parse URL params:', err);
    }
  }, [dataLoaded]);

  // ── Portrait lock (mobile-only) ────────────────────────────────────────
  useEffect(() => {
    // Only apply portrait lock on actual mobile devices — desktop browsers
    // (wider viewport, no touch API) should never see the Rotate to Portrait overlay
    const isMobileDevice = () => {
      const ua = navigator.userAgent || '';
      if (/Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return true;
      if (navigator.userAgentData && typeof navigator.userAgentData.mobile === 'boolean') {
        return navigator.userAgentData.mobile;
      }
      if (/Windows NT|Macintosh|X11|Linux x86_64/i.test(ua)) return false;
      return window.innerWidth < 768;
    };
    if (!isMobileDevice()) return; // skip on desktop entirely

    if (screen.orientation?.lock) {
      screen.orientation.lock('portrait-primary').catch(() => {});
    }
    const mql = window.matchMedia('(orientation: landscape)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsLandscape(e.matches);
    };
    handler(mql);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!selectedExperienceId || !dataLoaded) {
      if (!selectedExperienceId) setIsItineraryLoading(false);
      return;
    }
    setIsItineraryLoading(true);
    setLinkedArSceneId(null);

    // 3-second safety net: ensure isItineraryLoading is NEVER permanently stuck in true state
    const safetyTimer = setTimeout(() => {
      setIsItineraryLoading(false);
    }, 3000);

    queryClient.fetchQuery({
      queryKey: ['storyboard', selectedExperienceId],
      queryFn: async () => {
        console.log("[Firebase] Fetching storyboard for exhibitionId:", selectedExperienceId);
        
        // 1. Try querying scenes where exhibitionId == selectedExperienceId and type == 'route'
        const q = query(
          collection(db, 'scenes'),
          where('exhibitionId', '==', selectedExperienceId),
          where('type', '==', 'route')
        );
        const querySnap = await getDocs(q);
        
        let storyboardDocData: any = null;
        
        // Find first route storyboard that is not DRAFT, fallback to any route (including DRAFT) in beta env
        const routes = querySnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        let activeRoute: any = routes.find((r: any) => r.status !== 'DRAFT');
        if (!activeRoute && routes.length > 0) {
          activeRoute = routes[0]; // fallback to draft in beta environment
        }
        
        if (activeRoute) {
          storyboardDocData = activeRoute;
          console.log("[Firebase] Found storyboard by exhibitionId query:", activeRoute.id, "(status:", activeRoute.status, ")");
        } else {
          // Fallback: try direct document ID fetch
          const docRef = doc(db, 'scenes', selectedExperienceId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            storyboardDocData = data; // Allow draft fallback for direct fetches too
            console.log("[Firebase] Found storyboard by direct document ID:", selectedExperienceId, "(status:", data?.status, ")");
          }
        }

        if (storyboardDocData && storyboardDocData.nodes && Array.isArray(storyboardDocData.nodes) && storyboardDocData.nodes.length > 0) {
          console.log("[Firebase] Loaded storyboard nodes:", storyboardDocData.nodes.length);
          const ordered = getOrderedNodes(storyboardDocData.nodes, storyboardDocData.connections || []);
          
          // Collect unique linkedExhibitIds for batch fetch (N+1 fix)
          const linkedIds = Array.from(new Set(
            ordered.map((n: any) => n.linkedExhibitId).filter((id: string | null): id is string => Boolean(id))
          ));

          // Batch fetch all linked scenes in ONE query
          const linkedScenesMap = new Map<string, any>();
          if (linkedIds.length > 0) {
            try {
              const batchQuery = query(
                collection(db, 'scenes'),
                where(documentId(), 'in', linkedIds)
              );
              const batchSnap = await getDocs(batchQuery);
              batchSnap.docs.forEach(d => {
                linkedScenesMap.set(d.id, { id: d.id, ...d.data() });
              });
              console.log(`[Firebase] Batch-fetched ${linkedScenesMap.size}/${linkedIds.length} linked scenes in ONE query`);
            } catch (err) {
              console.error(`[Firebase] Batch fetch linked scenes failed:`, err);
            }
          }

          // Build itinerary using pre-fetched map (no more per-node getDoc)
          const itineraryPromises = ordered.map(async (node: any, idx: number) => {
            let linkedScene: any = null;
            if (node.linkedExhibitId) {
              linkedScene = linkedScenesMap.get(node.linkedExhibitId) || null;
              if (!linkedScene) {
                console.warn(`[Firebase] Linked scene doc ${node.linkedExhibitId} not found in batch`);
              }
            }
            const linkedEx = EXPERIENCES.find(e => e.id === node.linkedExhibitId);
            
            const title = linkedScene?.title || node.title || 'Untitled Stop';
            const desc = linkedScene?.desc || linkedScene?.description || node.description || '';
            const img = linkedScene?.trackerImage || linkedScene?.image || node.mediaUrl || (linkedEx?.image) || 'https://upload.wikimedia.org/wikipedia/commons/1/1a/William_Blake_-_Isaac_Newton_-_WGA02217.jpg';
            const audioText = node.description || desc || '';

            console.log(`[Firebase] Resolved stop ${idx + 1}:`, { title, desc, img, audioText });

            return {
              id: idx + 1,
              title,
              desc,
              img,
              itemNo: (node.linkedExhibitId || node.id).substring(0, 8).toUpperCase(),
              audioText,
              audioUrl: node.audioUrl || null,
              type: node.type,
              linkedExhibitId: node.linkedExhibitId || null,
              aiGuide: linkedScene?.aiGuide || storyboardDocData?.aiGuide || null
            };
          });

          const itinerary = await Promise.all(itineraryPromises);
          return { itinerary, linkedArSceneId: null };
        } else {
          // No route storyboard found — single AR artefact
          // Look up the actual AR scene document linked to this exhibition
          console.log("[Firebase] No storyboard nodes found — looking up linked AR scene for exhibition:", selectedExperienceId);
          try {
            // 1. Check direct doc ID first (most authoritative)
            const directDocSnap = await getDoc(doc(db, 'scenes', selectedExperienceId));
            if (directDocSnap.exists()) {
              console.log("[Firebase] Found linked AR scene by direct doc ID:", selectedExperienceId);
              return { itinerary: [], linkedArSceneId: selectedExperienceId };
            }

            // 2. Query scenes by exhibitionId
            const arSceneQuery = query(
              collection(db, 'scenes'),
              where('exhibitionId', '==', selectedExperienceId)
            );
            const arSceneSnap = await getDocs(arSceneQuery);
            const arScenes = arSceneSnap.docs
              .map(d => ({ id: d.id, ...d.data() as any }))
              .filter((s: any) => s.type !== 'route');

            const activeArScene = arScenes.find((s: any) => s.status === 'PUBLISHED' || s.status === 'ACTIVE' || s.status === 'active')
              || arScenes.find((s: any) => s.status !== 'DRAFT')
              || arScenes[0];

            if (activeArScene) {
              console.log("[Firebase] Found linked AR scene by exhibitionId query:", activeArScene.id);
              return { itinerary: [], linkedArSceneId: activeArScene.id };
            } else {
              console.warn("[Firebase] Using selectedExperienceId as fallback scene ID:", selectedExperienceId);
              return { itinerary: [], linkedArSceneId: selectedExperienceId };
            }
          } catch (e) {
            console.error("[Firebase] Error looking up linked AR scene:", e);
            return { itinerary: [], linkedArSceneId: selectedExperienceId };
          }
        }
      }
    }).then((result) => {
      clearTimeout(safetyTimer);
      setTourItinerary(result.itinerary);
      setLinkedArSceneId(result.linkedArSceneId);
      setIsItineraryLoading(false);
    }).catch((err) => {
      clearTimeout(safetyTimer);
      console.error("[Firebase] Error fetching storyboard:", err);
      setTourItinerary([]);
      setLinkedArSceneId(selectedExperienceId);
      setIsItineraryLoading(false);
    });

    return () => clearTimeout(safetyTimer);
  }, [selectedExperienceId, dataLoaded]);

  const toggleFavorite = (id: string) => {
    setFavorites(prev => 
      prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]
    );
  };

  const toggleMyListExhibition = (id: string) => {
    setMyListExhibitions(prev => 
      prev.includes(id) ? prev.filter(eid => eid !== id) : [...prev, id]
    );
  };

  const updateAvatar = async (newUrl: string) => {
    setAvatarUrl(newUrl);
    
    try {
      localStorage.setItem('comenius_avatar_fallback', newUrl);
    } catch (e) {}

    if (auth.currentUser) {
      // Save to user-scoped localStorage
      try {
        localStorage.setItem(`user_avatar_${auth.currentUser.uid}`, newUrl);
      } catch (e) {
        console.warn("Could not save avatar to localStorage (quota exceeded):", e);
      }
      
      // Save to Firestore users collection
      try {
        await setDoc(doc(db, 'users', auth.currentUser.uid), {
          avatarUrl: newUrl
        }, { merge: true });
        console.log("[Firestore] Avatar saved successfully to database!");
      } catch (err) {
        console.error("[Firestore] Error saving avatar to database:", err);
      }
    }
  };

  const updateUserName = (newName: string) => {
    setUserName(newName);
    if (auth.currentUser) {
      try {
        localStorage.setItem(`user_name_${auth.currentUser.uid}`, newName);
      } catch (e) {
        console.warn("Could not save username to localStorage:", e);
      }
    }
  };

  const navigateTo = (newScreen: Screen, dir: number = 1) => {
    setDirection(dir);
    setScreen(newScreen);
    try {
      if (newScreen !== 'splash') {
        sessionStorage.setItem('comenius_current_screen', newScreen);
      }
    } catch (e) {}
  };

  // ── Entry/tab membership gate ──────────────────────────────────────────
  // Tabs other than Home require a real (non-anonymous) member. Guests are
  // sent to the Auth screen; after login they land on the requested tab.
  const GATED_TABS: Screen[] = ['discover', 'mylist', 'profile'];
  const pendingTabRef = useRef<Screen | null>(null);

  const isMemberUser = (u: any) => !!u && !u.isAnonymous;

  const navigateToTab = (s: Screen) => {
    if (s !== 'home' && GATED_TABS.includes(s) && !isMemberUser(auth.currentUser)) {
      pendingTabRef.current = s;
      setScreen('prelaunch');
      return;
    }
    navigateTo(s, 1);
  };

  const handleGoogleSuccess = async (user: any) => {
    if (!user) return;
    // Pre-launch: only allowlisted admins may sign in. Others are signed out
    // immediately (the auth listener enforces this too) without a profile doc.
    if (!isPrelaunchAdminUser(user)) {
      try { await auth.signOut(); } catch {}
      if (!isQrEntry()) setScreen('prelaunch');
      return;
    }
    const resolvedName = extractFirstName(user);
    const resolvedAvatar = user.photoURL || DEFAULT_YELLOW_AVATAR;
    setUser(user);
    setIsLoggedIn(true);
    analyticsUserIdRef.current = user.uid;
    setUserName(resolvedName);
    setAvatarUrl(resolvedAvatar);
    try {
      localStorage.setItem(`user_name_${user.uid}`, resolvedName);
      localStorage.setItem(`user_avatar_${user.uid}`, resolvedAvatar);
    } catch (e) {
      console.warn('[Auth] Could not save user to localStorage:', e);
    }
    try {
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email || '',
        firstName: resolvedName,
        avatarUrl: resolvedAvatar,
        createdAt: serverTimestamp()
      }, { merge: true });
    } catch (firestoreErr) {
      console.warn('[Auth] Failed to write user profile document:', firestoreErr);
    }
    const next = pendingTabRef.current || 'home';
    pendingTabRef.current = null;
    setScreen(next);
  };

  useEffect(() => {
    // No page scroll needed — overflow:hidden handles layout locking
  }, [screen]);

  const isDesktopVisitor = (() => {
    const isVisitor = window.location.hostname === 'visitor.c0menius.app' || new URLSearchParams(window.location.search).get('preview') === 'desktop';
    const ua = (navigator.userAgent || '').toLowerCase();
    if (ua.indexOf('android') !== -1 || ua.indexOf('mobi') !== -1 || ua.indexOf('iphone') !== -1 || ua.indexOf('ipad') !== -1 || ua.indexOf('ipod') !== -1) return false;
    if (navigator.userAgentData && typeof (navigator as any).userAgentData.mobile === 'boolean') {
      if ((navigator as any).userAgentData.mobile) return false;
    }
    if (ua.indexOf('windows nt') !== -1 || ua.indexOf('macintosh') !== -1 || ua.indexOf('x11') !== -1 || ua.indexOf('linux x86_64') !== -1) {
      return isVisitor;
    }
    return isVisitor && window.innerWidth >= 768;
  })();

  if (isDesktopVisitor) {
    return <VisitorDesktopLanding />;
  }

  const renderScreen = () => {
    switch (screen) {
      case 'auth':
        return <AuthScreen onGoogleSuccess={handleGoogleSuccess} />;
      case 'prelaunch':
        return <PrelaunchScreen />;
      case 'home':
        return (
          <HomeScreen 
            onSelectMuseum={(id) => { setSelectedMuseumId(id); navigateTo('museum_detail', 1); }} 
            favorites={favorites} 
            toggleFavorite={toggleFavorite} 
            onProfileClick={() => navigateToTab('profile')} 
            avatarUrl={avatarUrl} 
            userName={userName}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            userCoords={userCoords}
            setUserCoords={setUserCoords}
            userCountry={userCountry}
            setUserCountry={setUserCountry}
            isLocating={isLocating}
            setIsLocating={setIsLocating}
            locationError={locationError}
            setLocationError={setLocationError}
            triggerGeolocation={triggerGeolocation}
            userRank={userRank}
          />
        );
      case 'discover':
        return <DiscoverScreen 
          onSelectExperience={(mId, expId) => {
            setSelectedMuseumId(mId);
            setSelectedExperienceId(expId);
            const exp = EXPERIENCES.find(e => e.id === expId);
            navigateTo(exp && exp.status !== 'live' ? 'experience' : 'itinerary', 1);
          }} 
          myListExhibitions={myListExhibitions} 
          toggleMyListExhibition={toggleMyListExhibition} 
          userCoords={userCoords}
          userCountry={userCountry}
          isLocating={isLocating}
          triggerGeolocation={triggerGeolocation}
        />;
      case 'museum_detail':
        return <MuseumDetailScreen 
          museumId={selectedMuseumId} 
          onBack={() => navigateTo('home', -1)} 
          onSelectExperience={(s, expId) => {
            if (expId) setSelectedExperienceId(expId);
            navigateTo(s, 1);
          }} 
        />;
      case 'experience': {
        const activeEx = EXPERIENCES.find(e => e.id === selectedExperienceId);
        const activeMuseum = MUSEUMS.find(m => m.id === selectedMuseumId);
        const expProg = experienceProgress[selectedExperienceId];
        const expIsCompleted = expProg?.completed === true;
        const expIsInProgress = !expIsCompleted && (toursInProgress.has(selectedExperienceId) || (expProg && !expProg.completed));
        return <ExperienceDetail
          experience={activeEx}
          museumName={activeMuseum?.name || ''}
          onBack={() => navigateTo('museum_detail', -1)}
          isCompleted={expIsCompleted}
          isInProgress={expIsInProgress}
          onStart={() => {
            const t = activeEx?.type || 'immersive';
            if (t === 'audio') {
              navigateTo('audio_player', 1);
            } else {
              setSelectedExperienceId(selectedExperienceId);
              navigateTo('itinerary', 1);
            }
          }}
        />;
      }
      case 'audio_player': {
        const activeEx = EXPERIENCES.find(e => e.id === selectedExperienceId);
        return <AudioPlayerScreen
          experience={activeEx}
          onBack={() => navigateTo('experience', -1)}
          onComplete={() => {
            // completeTour is async — it checks for active reward rules first
            // and shows the reward modal if eligible, or falls through to feedback.
            logAnalyticsEvent('route_complete', {}, selectedExperienceId, selectedMuseumId);
            completeTour(selectedExperienceId, selectedMuseumId);
          }}
        />;
      }
      case 'itinerary': {
        const activeEx = EXPERIENCES.find(e => e.id === selectedExperienceId);
        const expProgress = experienceProgress[selectedExperienceId];
        const isCompleted = expProgress?.completed === true;
        // `completed` is authoritative — a completed experience is never "Continue",
        // even if a stale toursInProgress entry survived hydration.
        const inProgress = !isCompleted && (toursInProgress.has(selectedExperienceId) || (expProgress && !expProgress.completed));
        const activeExType = activeEx?.type || 'immersive';
        const isRouteType = activeExType === 'route';
        const detailOverride = activeEx ? {
          title: activeEx.title,
          description: activeEx.description || activeEx.desc || '',
          image: activeEx.image,
          minutes: isRouteType && tourItinerary.length > 0
            ? `${tourItinerary.length * 5} MINS`
            : isRouteType
              ? '... MINS'
              : activeExType === 'audio'
                ? '15 MINS'
                : '5 MINS',
          objectsCount: activeExType === 'ar' || activeExType === 'immersive'
            ? '1 Artefact'
            : activeExType === 'audio'
              ? 'Audio Guide'
              : '',
          about: activeEx.desc || activeEx.description || '',
          itinerary: []
        } : undefined;
        return <ItineraryScreen 
          museumId={selectedMuseumId} 
          experienceId={selectedExperienceId}
          dynamicItinerary={tourItinerary}
          aboutText={activeEx?.desc || activeEx?.description}
          detailOverride={detailOverride}
          linkedArSceneId={linkedArSceneId}
          progressInfo={expProgress}
          isGuest={!user || user.isAnonymous}
          onRequireSignIn={() => navigateTo('prelaunch', 1)}
          onBack={() => navigateTo('museum_detail', -1)} 
          onStartTour={async () => {
            if (isItineraryLoading) return;
            // Pressing START on a previously completed tour resets it so the
            // visitor begins again from Scene 1.
            const prevProg = experienceProgress[selectedExperienceId];
            if (prevProg?.completed) {
              handleProgressUpdate(selectedExperienceId, selectedMuseumId, 1, prevProg.totalItems || 1, false);
            }
            setToursInProgress(prev => new Set(prev).add(selectedExperienceId));
            logAnalyticsEvent('route_start', {}, selectedExperienceId, selectedMuseumId);
            // If tourItinerary is empty (single AR artefact with no storyboard), go directly to AR engine
            if (tourItinerary.length === 0) {
              // Resolve the correct AR scene document ID for this exhibition.
              // We NEVER fall back to the bare exhibition ID as a scene ID because
              // scenes/<exhibitionId> almost certainly doesn't exist and produces a guaranteed 404.
              let sceneId = linkedArSceneId || '';

              // Validate: verify the resolved ID actually exists in Firestore
              if (sceneId) {
                try {
                  const checkRef = doc(db, 'scenes', sceneId);
                  const checkSnap = await getDoc(checkRef);
                  if (!checkSnap.exists()) {
                    console.warn(`[Marketplace] linkedArSceneId ${sceneId} missing in Firestore — re-querying active child scene...`);
                    sceneId = '';
                  }
                } catch (err) {
                  console.warn('[Marketplace] Error verifying scene document existence:', err);
                  sceneId = '';
                }
              }

              // Last-resort: query child AR scenes by exhibitionId
              if (!sceneId) {
                try {
                  const arSceneQuery = query(
                    collection(db, 'scenes'),
                    where('exhibitionId', '==', selectedExperienceId)
                  );
                  const arSceneSnap = await getDocs(arSceneQuery);
                  const arScenes = arSceneSnap.docs
                    .map(d => ({ id: d.id, ...d.data() as any }))
                    .filter((s: any) => s.type !== 'route');
                  const activeArScene =
                    arScenes.find((s: any) => s.status === 'PUBLISHED' || s.status === 'ACTIVE' || s.status === 'active') ||
                    arScenes.find((s: any) => s.status !== 'DRAFT') ||
                    arScenes[0];
                  if (activeArScene) {
                    sceneId = activeArScene.id;
                    console.log('[Marketplace] Resolved active child AR scene by exhibitionId:', sceneId);
                  }
                } catch (err) {
                  console.warn('[Marketplace] Error querying child AR scenes:', err);
                }
              }

              // If we still have no valid scene, abort with a clear message
              if (!sceneId) {
                alert('Bu deneyimin bağlı bir AR sahnesi bulunamadı. Lütfen Curator Editör\'den sahneyi kaydedip tekrar deneyin.');
                return;
              }
              const returnTo = encodeURIComponent(`/marketplace?experience=${selectedExperienceId}&museum=${selectedMuseumId}&screen=itinerary&returnFromAR=1`);
              // Record genuine AR interaction for this tour (direct itinerary launch = stop 1)
              {
                const arSet = arVisitedStopsRef.current[selectedExperienceId] || (arVisitedStopsRef.current[selectedExperienceId] = new Set());
                arSet.add(1);
              }
              // Save to sessionStorage as a PWA-reliable fallback
              try {
                sessionStorage.setItem('comenius_ar_return', JSON.stringify({
                  experience: selectedExperienceId,
                  museum: selectedMuseumId,
                  screen: 'itinerary',
                  timestamp: Date.now()
                }));
              } catch {}
              window.location.href = `/viewer?scene=${sceneId}&storyboard=${selectedExperienceId}&origin=marketplace&mid=${selectedMuseumId}&stop=1&item=${encodeURIComponent(activeEx?.title || '')}&returnTo=${returnTo}`;
            } else {
              navigateTo('tour_object', 1);
            }
          }}
          toggleMyList={() => toggleMyListExhibition(selectedExperienceId)}
          isInMyList={myListExhibitions.includes(selectedExperienceId)}
          isTourInProgress={!!inProgress}
          isCompleted={isCompleted}
          isLoading={isItineraryLoading}
          onRewardClick={(reward) => { setSelectedReward(reward); navigateTo('reward_redeem', 1); }}
        />;
      }
      case 'tour_object':
        return <TourObjectScreen 
          museumId={selectedMuseumId} 
          experienceId={selectedExperienceId}
          dynamicItinerary={tourItinerary}
          onBack={() => navigateTo('itinerary', -1)} 
          onClose={() => setShowFeedback(true)} 
          onStartAR={async (linkedId, mode, stopNumber, title) => {
            // Record genuine AR interaction for this tour (feeds route_complete.hadArInteraction)
            if (typeof stopNumber === 'number' && stopNumber >= 1) {
              const set = arVisitedStopsRef.current[selectedExperienceId] || (arVisitedStopsRef.current[selectedExperienceId] = new Set());
              set.add(stopNumber);
            }
            let sceneId = linkedId || '';

            // 1. Verify whether linkedId exists in Firestore
            if (linkedId) {
              try {
                const checkRef = doc(db, 'scenes', linkedId);
                const checkSnap = await getDoc(checkRef);
                if (!checkSnap.exists()) {
                  console.warn(`[Marketplace tour_object] linkedId ${linkedId} does not exist in Firestore! Resolving fallback for ${selectedExperienceId}...`);
                  sceneId = '';
                }
              } catch (e) {
                console.warn('[Marketplace tour_object] Error checking linkedId:', e);
              }
            }

            // 2. If linkedId is missing or dead, resolve active AR scene for this exhibition
            if (!sceneId) {
              try {
                const directDocSnap = await getDoc(doc(db, 'scenes', selectedExperienceId));
                if (directDocSnap.exists() && directDocSnap.data()?.type !== 'route') {
                  sceneId = selectedExperienceId;
                } else {
                  const arSceneQuery = query(
                    collection(db, 'scenes'),
                    where('exhibitionId', '==', selectedExperienceId)
                  );
                  const arSceneSnap = await getDocs(arSceneQuery);
                  const arScenes = arSceneSnap.docs
                    .map(d => ({ id: d.id, ...d.data() as any }))
                    .filter((s: any) => s.type !== 'route');
                  
                  const activeArScene = arScenes.find((s: any) => s.status === 'PUBLISHED' || s.status === 'ACTIVE' || s.status === 'active')
                    || arScenes.find((s: any) => s.status !== 'DRAFT')
                    || arScenes[0];
                    
                  if (activeArScene) {
                    sceneId = activeArScene.id;
                  } else {
                    sceneId = selectedExperienceId;
                  }
                }
              } catch (err) {
                sceneId = selectedExperienceId;
              }
            }

            const returnTo = encodeURIComponent(`/marketplace?experience=${selectedExperienceId}&museum=${selectedMuseumId}&screen=tour_object&returnFromAR=1`);
            // Save to sessionStorage as a PWA-reliable fallback
            try {
              sessionStorage.setItem('comenius_ar_return', JSON.stringify({
                experience: selectedExperienceId,
                museum: selectedMuseumId,
                screen: 'tour_object',
                timestamp: Date.now()
              }));
            } catch {}
            window.location.href = `/viewer?scene=${sceneId}&storyboard=${selectedExperienceId}&origin=marketplace&mid=${selectedMuseumId}${mode ? `&mode=${mode}` : ''}&stop=${stopNumber || 1}&item=${encodeURIComponent(title || '')}&returnTo=${returnTo}`;
          }}
          onOpenAIGuide={(exhibit) => {
            // AI Guide open also counts as immersive interaction for this tour
            const stopNo = exhibit && typeof exhibit.id === 'number' ? exhibit.id : NaN;
            if (stopNo >= 1) {
              const set = arVisitedStopsRef.current[selectedExperienceId] || (arVisitedStopsRef.current[selectedExperienceId] = new Set());
              set.add(stopNo);
            }
            setAiGuideExhibit(exhibit);
            navigateTo('ai_guide', 1);
          }}
          onProgressUpdate={(current, total, completed) => handleProgressUpdate(selectedExperienceId, selectedMuseumId, current, total, completed)}
          initialItem={experienceProgress[selectedExperienceId]?.currentItem || 1}
          onStopView={(stopNumber, title) =>
            logAnalyticsEvent('stop_view', { stopNumber, itemName: title, source: 'stop' })
          }
          onStopAction={(actionType, stopNumber, title) =>
            logAnalyticsEvent('stop_action', { interactionType: actionType, stopNumber, itemName: title })
          }
          onDwellTime={(stopNumber, title, durationSeconds) =>
            logAnalyticsEvent('stop_view', { stopNumber, itemName: title, durationSeconds, source: 'stop' })
          }
          isMember={!!user && !user?.isAnonymous}
          onRequireSignIn={() => setShowAudioMemberGate(true)}
        />;
      case 'ai_guide':
        return <AIGuideScreen
          experienceId={selectedExperienceId}
          exhibitTitle={aiGuideExhibit?.title}
          exhibitDescription={aiGuideExhibit?.desc || aiGuideExhibit?.audioText || ''}
          onBack={() => navigateTo('tour_object', -1)}
        />;
      case 'scanner':
        return <ScannerScreen
          onBack={() => navigateTo('tour_object', -1)}
          onScanSuccess={(itemId, itemName) =>
            logAnalyticsEvent('direct_scan', { itemName, itemId }, selectedExperienceId, selectedMuseumId)
          }
        />;
      case 'profile':
        return <ProfileScreen onLogout={async () => { await auth.signOut(); try { navigateTo(window.location.pathname.startsWith('/admin') ? 'auth' : 'prelaunch', -1); } catch { navigateTo('prelaunch', -1); } }} onRewardClick={(reward) => { setSelectedReward(reward); navigateTo('reward_redeem', 1); }} avatarUrl={avatarUrl} setAvatarUrl={updateAvatar} userName={userName} setUserName={updateUserName} />;
      case 'reward_redeem':
        return <RewardRedeemScreen reward={selectedReward} onClose={() => navigateTo('profile', -1)} />;
      case 'mylist':
        const displayExhibitions = myListTab === 'saved' 
          ? EXPERIENCES.filter(e => myListExhibitions.includes(e.id))
          : myListTab === 'continue'
            ? EXPERIENCES.filter(e => {
                const progress = experienceProgress[e.id];
                return progress && !progress.completed;
              })
            : EXPERIENCES.filter(e => {
                const progress = experienceProgress[e.id];
                return progress && progress.completed;
              });

        return (
          <div className="h-full bg-[#13213b] flex flex-col overflow-y-auto no-scrollbar pb-32">
            {/* Hero Section */}
            <div className="relative h-[280px] w-full shrink-0">
              <div 
                className="absolute inset-0 bg-cover bg-center" 
                style={{ backgroundImage: 'url("https://upload.wikimedia.org/wikipedia/commons/thumb/a/a1/Hermes_Trismegistus_Sienna_Cathedral_Mosaic.jpg/1280px-Hermes_Trismegistus_Sienna_Cathedral_Mosaic.jpg")' }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-[#13213b] via-[#13213b]/45 to-[#13213b]/90"></div>
              </div>
              
              <div className="absolute inset-0 flex flex-col justify-end px-8 pb-10">
                <motion.h1 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-3xl font-serif font-bold text-white mb-4"
                >
                  My List
                </motion.h1>
                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-white/80 text-lg max-w-[280px] leading-relaxed"
                >
                  Your saved journeys, in-progress explorations, and completed badges.
                </motion.p>
              </div>
            </div>

            {/* Content Section */}
            <div className="px-6 -mt-4 relative z-10">
              {/* Tabs */}
              <div className="flex border-b border-white/10 mb-8 relative">
                <button 
                  onClick={() => setMyListTab('continue')}
                  className={`relative flex-1 pb-1 text-xs font-bold uppercase tracking-[0.2em] flex flex-col items-center leading-tight transition-colors duration-300 ${myListTab === 'continue' ? 'text-primary font-bold' : 'text-slate-500 hover:text-slate-300'}`}
                >
                  <span>Continue</span>
                  <span>&nbsp;</span>
                  {myListTab === 'continue' && (
                    <motion.div 
                      layoutId="activeMyListTabUnderline"
                      className="absolute bottom-0 left-0 right-0 h-[3px] bg-primary rounded-t-full z-10"
                      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                    />
                  )}
                </button>
                <button 
                  onClick={() => setMyListTab('saved')}
                  className={`relative flex-1 pb-1 text-xs font-bold uppercase tracking-[0.2em] flex flex-col items-center leading-tight transition-colors duration-300 ${myListTab === 'saved' ? 'text-primary font-bold' : 'text-slate-500 hover:text-slate-300'}`}
                >
                  <span>Saved</span>
                  <span>&nbsp;</span>
                  {myListTab === 'saved' && (
                    <motion.div 
                      layoutId="activeMyListTabUnderline"
                      className="absolute bottom-0 left-0 right-0 h-[3px] bg-primary rounded-t-full z-10"
                      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                    />
                  )}
                </button>
                <button 
                  onClick={() => setMyListTab('completed')}
                  className={`relative flex-1 pb-1 text-xs font-bold uppercase tracking-[0.2em] flex flex-col items-center leading-tight transition-colors duration-300 ${myListTab === 'completed' ? 'text-primary font-bold' : 'text-slate-500 hover:text-slate-300'}`}
                >
                  <span>Completed</span>
                  <span>&nbsp;</span>
                  {myListTab === 'completed' && (
                    <motion.div 
                      layoutId="activeMyListTabUnderline"
                      className="absolute bottom-0 left-0 right-0 h-[3px] bg-primary rounded-t-full z-10"
                      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                    />
                  )}
                </button>
              </div>

              <div className="space-y-6">
                {displayExhibitions.length > 0 ? (
                  displayExhibitions.map(exp => {
                    const progressInfo = experienceProgress[exp.id];
                    const pct = myListTab === 'completed' ? 100 : (progressInfo ? Math.round((progressInfo.currentItem / progressInfo.totalItems) * 100) : 0);
                    return (
                      <div key={exp.id} className="bg-surface-dark/40 border border-white/5 rounded-2xl overflow-hidden shadow-2xl cursor-pointer hover:bg-surface-dark/60 transition-colors" onClick={() => {
                        setSelectedMuseumId(exp.museumId || '');
                        setSelectedExperienceId(exp.id);
                        navigateTo(exp.status !== 'live' ? 'experience' : 'itinerary', 1);
                      }}>
                        <img src={exp.image} className="w-full aspect-video object-cover" alt={exp.title} referrerPolicy="no-referrer" loading="lazy" />
                        <div className="p-5">
                          <h4 className="text-lg font-serif font-bold mb-1">{exp.title}</h4>
                          <div className="w-full h-1.5 bg-white/5 rounded-full mt-4 overflow-hidden">
                            <div className="h-full bg-primary" style={{ width: `${pct}%` }}></div>
                          </div>
                          <div className="flex justify-between mt-2">
                            <span className="text-[10px] font-bold text-slate-500 uppercase">
                              {myListTab === 'completed' ? 'Experience Completed' : 'Experience Progress'}
                            </span>
                            <span className="text-[10px] font-bold text-primary">
                              {pct}%
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-20">
                    <Heart size={48} className="mx-auto text-slate-700 mb-4" />
                    <p className="text-slate-500 font-medium">
                      {myListTab === 'saved' ? 'Your saved list is empty' : myListTab === 'continue' ? 'No experiences in progress' : 'No completed experiences'}
                    </p>
                    <button onClick={() => navigateTo('discover', 1)} className="text-primary text-sm font-bold uppercase tracking-widest mt-4">Explore Museums</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      default:
        return <div className="flex items-center justify-center h-full">Screen: {screen}</div>;
    }
  };

  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        x: { type: "spring", stiffness: 300, damping: 30 },
        opacity: { duration: 0.2 }
      }
    },
    exit: (dir: number) => ({
      x: dir < 0 ? '100%' : '-100%',
      opacity: 0,
      transition: {
        x: { type: "spring", stiffness: 300, damping: 30 },
        opacity: { duration: 0.2 }
      }
    })
  };

  return (
    <Sentry.ErrorBoundary fallback={SentryErrorFallback}>
    <QueryClientProvider client={queryClient}>
    <div className="max-w-md mx-auto bg-bg-dark h-full relative shadow-2xl overflow-hidden" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      {screen === 'splash' ? (
        <SplashScreen onFinish={() => {
          // ── Entry gate ──────────────────────────────────────────────
          // QR entries (experience/museum/screen params or AR return) keep the
          // anonymous guest flow untouched — deep-link handles the transition.
          // Direct entries without membership land on the Auth screen (soft
          // gate: "Continue as Guest" sets a tab-session flag).
          const isQrEntry = () => {
            try {
              const params = new URLSearchParams(window.location.search);
              if (params.get('experience') || params.get('exhibitionId')) return true;
              if (params.get('museum') || params.get('mid') || params.get('screen')) return true;
              const raw = sessionStorage.getItem('comenius_ar_return');
              if (raw) {
                const state = JSON.parse(raw);
                if (Date.now() - (state.timestamp || 0) < 900_000) return true;
              }
            } catch {}
            return false;
          };

          const decideEntry = () => {
            try {
              // Hidden admin entry: /admin renders the Auth screen directly,
              // bypassing the prelaunch gate. No in-app UI links here —
              // real enforcement stays the email allowlist + Firestore rules.
              try {
                if (window.location.pathname === '/admin' || window.location.pathname.startsWith('/admin/')) {
                  setScreen('auth');
                  return;
                }
              } catch {}
              const params = new URLSearchParams(window.location.search);
              const urlExp = params.get('experience') || params.get('exhibitionId');
              if (urlExp) return; // Deep-link useEffect will handle screen transition

              const member = isMemberUser(auth.currentUser);
              let guest = false;
              try { guest = sessionStorage.getItem('comenius_guest_entry') === '1'; } catch {}

              if (!member && !guest && !isQrEntry()) {
                setScreen('prelaunch');
                return;
              }

              const storedScreen = sessionStorage.getItem('comenius_current_screen') as Screen | null;
              const storedMuseum = sessionStorage.getItem('comenius_selected_museum_id');
              if (storedScreen === 'museum_detail' && storedMuseum) {
                setSelectedMuseumIdState(storedMuseum);
                setScreen('museum_detail');
                return;
              }
              if (storedScreen && ['home', 'museum_detail', 'mylist', 'profile', 'discover'].includes(storedScreen)) {
                // Gated tabs need membership even when restored from session
                if (!member && storedScreen !== 'home' && storedScreen !== 'museum_detail') {
                  setScreen('prelaunch');
                  return;
                }
                setScreen(storedScreen);
                return;
              }
            } catch (e) {}
            setScreen('home');
          };

          if (authReady) { decideEntry(); return; }
          // Firebase Auth restore can lag splash timing — wait briefly, then
          // decide conservatively (unknown membership → gate).
          let tries = 0;
          const timer = setInterval(() => {
            tries += 1;
            if (authReady || tries >= 6) {
              clearInterval(timer);
              decideEntry();
            }
          }, 400);
        }} />
      ) : (
        <AnimatePresence mode="wait" initial={false} custom={direction}>
          <motion.div
            key={screen}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            className="h-full w-full overflow-hidden"
          >
            <React.Suspense fallback={<div className="py-20 text-center text-slate-500 text-xs font-bold uppercase tracking-wider">Loading…</div>}>
            {renderScreen()}
            </React.Suspense>
          </motion.div>
        </AnimatePresence>
      )}
      
      <AnimatePresence>
        {!['splash', 'auth', 'prelaunch', 'itinerary', 'experience', 'audio_player', 'tour_object', 'scanner', 'ai_guide', 'reward_redeem'].includes(screen) && (
          <Navbar key="navbar" current={screen} setScreen={(s) => navigateToTab(s)} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showRewardUnlock && pendingRewardRule && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-sm bg-surface-dark/95 border border-primary/30 rounded-3xl p-6 shadow-2xl shadow-black/80 flex flex-col items-center text-center relative overflow-hidden backdrop-blur-xl"
            >
              {/* Close button */}
              <button
                onClick={() => {
                  setShowRewardUnlock(false);
                  setShowFeedback(true);
                }}
                className="absolute top-4 right-4 text-white/50 hover:text-white p-1.5 rounded-full bg-white/10"
              >
                <X size={18} />
              </button>

              {!claimedRewardDoc ? (
                <>
                  {/* Header Badge */}
                  <div className="w-16 h-16 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mb-3 text-primary animate-pulse">
                    <Sparkles size={32} />
                  </div>

                  <span className="text-[10px] font-black uppercase tracking-widest text-primary bg-primary/10 px-3 py-1 rounded-full border border-primary/20 mb-2">
                    REWARD UNLOCKED!
                  </span>

                  <h2 className="text-xl font-serif font-bold text-white mb-1">
                    {pendingRewardRule.title || 'Museum Discount Reward'}
                  </h2>

                  <p className="text-xs text-white/70 mb-4 px-2">
                    {pendingRewardRule.description || "You've earned an exclusive reward for completing this experience!"}
                  </p>

                  <div className="w-full bg-white/5 border border-white/10 rounded-2xl p-3 mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-left">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                        <Zap size={20} />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-white">{pendingRewardRule.targetName || 'All Items'}</p>
                        <p className="text-[10px] text-white/50">Museum Shop / Cafe</p>
                      </div>
                    </div>
                    <span className="text-lg font-extrabold text-primary font-mono">
                      {pendingRewardRule.discountPercentage || 20}% OFF
                    </span>
                  </div>

                  {claimError && (
                    <p className="text-rose-400 text-xs mb-3 font-mono bg-rose-500/10 p-2 rounded-xl border border-rose-500/20 w-full">
                      {claimError}
                    </p>
                  )}

                  {auth.currentUser && !auth.currentUser.isAnonymous ? (
                    <button
                      onClick={async () => {
                        setIsClaiming(true);
                        setClaimError(null);
                        try {
                          const prevAnonUid = localStorage.getItem('analytics_user_id') || undefined;
                          const claimFn = httpsCallable(functions, 'claimReward');
                          const res: any = await claimFn({
                            ruleId: pendingRewardRule.id,
                            exhibitionId: selectedExperienceId,
                            previousAnonUid: prevAnonUid
                          });
                          setClaimedRewardDoc(res.data);
                        } catch (err: any) {
                          console.error('Claim error:', err);
                          setClaimError(err.message || 'Error claiming reward.');
                        } finally {
                          setIsClaiming(false);
                        }
                      }}
                      disabled={isClaiming}
                      className="w-full py-3.5 bg-primary text-bg-dark font-black uppercase tracking-widest text-xs rounded-xl hover:brightness-110 transition-all flex items-center justify-center gap-2 shadow-lg shadow-primary/20"
                    >
                      {isClaiming ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
                      <span>ADD REWARD TO MY PROFILE</span>
                    </button>
                  ) : (
                    <div className="w-full space-y-2">
                      <p className="text-[11px] text-white/60 mb-2">
                        Sign up to save this reward to your profile and redeem it at checkout:
                      </p>

                      <button
                        onClick={async () => {
                          setIsClaiming(true);
                          setClaimError(null);
                          localStorage.setItem('oauth_in_flight', '1');
                          try {
                            const provider = new GoogleAuthProvider();
                            let user = auth.currentUser;
                            const prevAnonUid = user?.isAnonymous ? user.uid : localStorage.getItem('analytics_user_id') || undefined;

                            // Always save pending claim to localStorage (survives iOS Safari suspend)
                            localStorage.setItem('pending_reward_claim', JSON.stringify({
                              ruleId: pendingRewardRule.id,
                              exhibitionId: selectedExperienceId,
                              previousAnonUid: prevAnonUid
                            }));

                            try {
                              // Popup-primary on all platforms
                              if (user && user.isAnonymous) {
                                const res = await linkWithPopup(user, provider);
                                user = res.user;
                              } else {
                                const res = await signInWithPopup(auth, provider);
                                user = res.user;
                              }
                            } catch (popupErr: any) {
                              if (popupErr.code === 'auth/credential-already-in-use') {
                                const res = await signInWithPopup(auth, provider);
                                user = res.user;
                              } else if (popupErr.code === 'auth/popup-blocked' || popupErr.code === 'auth/cancelled-popup-request') {
                                // Redirect fallback — pending_reward_claim already saved to localStorage
                                await signInWithRedirect(auth, provider);
                                return;
                              } else {
                                throw popupErr;
                              }
                            }

                            if (user) {
                              const resolvedName = extractFirstName(user);
                              const resolvedAvatar = user.photoURL || DEFAULT_YELLOW_AVATAR;

                              setUser(user);
                              setIsLoggedIn(true);
                              analyticsUserIdRef.current = user.uid;
                              setUserName(resolvedName);
                              setAvatarUrl(resolvedAvatar);

                              localStorage.setItem(`user_name_${user.uid}`, resolvedName);
                              localStorage.setItem(`user_avatar_${user.uid}`, resolvedAvatar);

                              await setDoc(doc(db, 'users', user.uid), {
                                email: user.email || '',
                                firstName: resolvedName,
                                avatarUrl: resolvedAvatar,
                                createdAt: serverTimestamp()
                              }, { merge: true });
                            }

                            // Claim the reward (popup path — redirect path handled in getRedirectResult)
                            localStorage.removeItem('pending_reward_claim');
                            const claimFn = httpsCallable(functions, 'claimReward');
                            const claimRes: any = await claimFn({
                              ruleId: pendingRewardRule.id,
                              exhibitionId: selectedExperienceId,
                              previousAnonUid: prevAnonUid
                            });
                            setClaimedRewardDoc(claimRes.data);
                          } catch (err: any) {
                            console.error('Google sign-in / claim error:', err);
                            setClaimError(err.message || 'Error signing in with Google.');
                          } finally {
                            setTimeout(() => localStorage.removeItem('oauth_in_flight'), 3000);
                            setIsClaiming(false);
                          }
                        }}
                        disabled={isClaiming}
                        className="w-full py-3 bg-white text-slate-900 font-bold text-xs rounded-xl hover:bg-slate-100 transition-all flex items-center justify-center gap-2 shadow-md"
                      >
                        {isClaiming ? <Loader2 size={16} className="animate-spin" /> : (
                          <svg className="w-4 h-4" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
                          </svg>
                        )}
                        <span>1-Tap Sign Up with Google</span>
                      </button>

                      <div className="relative my-3">
                        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
                        <div className="relative flex justify-center text-[9px] uppercase"><span className="bg-surface-dark px-2 text-white/40 font-mono">OR VIA EMAIL</span></div>
                      </div>

                      {!magicLinkSent ? (
                        <div className="flex gap-2">
                          <input
                            type="email"
                            placeholder="your@email.com"
                            value={emailForClaim}
                            onChange={e => setEmailForClaim(e.target.value)}
                            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-primary/50"
                          />
                          <button
                            onClick={async () => {
                              if (!emailForClaim || !emailForClaim.includes('@')) {
                                setClaimError('Please enter a valid email address.');
                                return;
                              }
                              setIsClaiming(true);
                              setClaimError(null);
                              try {
                                const actionCodeSettings = {
                                  url: window.location.origin + '/marketplace',
                                  handleCodeInApp: true
                                };
                                await sendSignInLinkToEmail(auth, emailForClaim, actionCodeSettings);
                                window.localStorage.setItem('emailForSignIn', emailForClaim);
                                setMagicLinkSent(true);
                              } catch (err: any) {
                                console.error('Magic link error:', err);
                                setClaimError(err.message || 'Failed to send email link.');
                              } finally {
                                setIsClaiming(false);
                              }
                            }}
                            disabled={isClaiming}
                            className="px-4 py-2 bg-primary/20 text-primary border border-primary/30 rounded-xl font-bold text-xs hover:bg-primary/30 transition-all shrink-0"
                          >
                            {isClaiming ? <Loader2 size={14} className="animate-spin" /> : 'Send Link'}
                          </button>
                        </div>
                      ) : (
                        <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-400 text-xs">
                          ✓ Verification link sent to your email! Please check your inbox.
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full flex flex-col items-center">
                  <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mb-3 text-emerald-400">
                    <CheckCircle2 size={28} />
                  </div>

                  <h2 className="text-lg font-serif font-bold text-white mb-1">
                    Reward Saved to Your Account!
                  </h2>

                  <p className="text-xs text-white/60 mb-4">
                    Show this QR code or verification code at checkout to claim your {pendingRewardRule.discountPercentage}% discount:
                  </p>

                  <div className="bg-white p-4 rounded-2xl shadow-xl mb-4 flex flex-col items-center border-4 border-primary/30">
                    <div className="w-36 h-36 bg-black/5 rounded-xl flex flex-col items-center justify-center p-2 text-center">
                      <Scan size={64} className="text-black mb-1" />
                      <span className="text-[8px] font-mono text-black/60 break-all">
                        COMENIUS-REDEEM:{auth.currentUser?.uid?.slice(0, 8)}:{pendingRewardRule.id?.slice(0, 8)}
                      </span>
                    </div>
                  </div>

                  <p className="text-[11px] text-primary font-mono mb-5">
                    Accessible anytime under Profile &gt; Your Rewards.
                  </p>

                  <button
                    onClick={() => {
                      setShowRewardUnlock(false);
                      setClaimedRewardDoc(null);
                      setPendingRewardRule(null);
                      setShowFeedback(true);
                    }}
                    className="w-full py-3 bg-primary text-bg-dark font-black uppercase tracking-widest text-xs rounded-xl hover:brightness-110 transition-all shadow-lg shadow-primary/20"
                  >
                    Done & Share Feedback
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAudioMemberGate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowAudioMemberGate(false)}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm bg-surface-dark/95 border border-primary/30 rounded-3xl p-6 shadow-2xl shadow-black/80 flex flex-col items-center text-center relative overflow-hidden backdrop-blur-xl"
            >
              {/* Close button */}
              <button
                onClick={() => setShowAudioMemberGate(false)}
                className="absolute top-4 right-4 text-white/50 hover:text-white p-1.5 rounded-full bg-white/10"
              >
                <X size={18} />
              </button>

              {/* Header Badge */}
              <div className="w-16 h-16 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mb-3 text-primary">
                <Headphones size={32} />
              </div>

              <span className="text-[10px] font-black uppercase tracking-widest text-primary bg-primary/10 px-3 py-1 rounded-full border border-primary/20 mb-2">
                FREE MEMBER FEATURE
              </span>

              <h2 className="text-xl font-serif font-bold text-white mb-1">
                Audio Guide
              </h2>

              <p className="text-xs text-white/70 mb-4 px-2">
                Sign up free with your Google account to listen to this exhibition's audio guide.
              </p>

              {audioMemberSignInError && (
                <p className="text-rose-400 text-xs mb-3 font-mono bg-rose-500/10 p-2 rounded-xl border border-rose-500/20 w-full">
                  {audioMemberSignInError}
                </p>
              )}

              <button
                onClick={async () => {
                  setIsAudioMemberSigningIn(true);
                  setAudioMemberSignInError(null);
                  localStorage.setItem('oauth_in_flight', '1');
                  try {
                    const provider = new GoogleAuthProvider();
                    let user = auth.currentUser;

                    try {
                      if (user && user.isAnonymous) {
                        const res = await linkWithPopup(user, provider);
                        user = res.user;
                      } else {
                        const res = await signInWithPopup(auth, provider);
                        user = res.user;
                      }
                    } catch (popupErr: any) {
                      if (popupErr.code === 'auth/credential-already-in-use') {
                        const res = await signInWithPopup(auth, provider);
                        user = res.user;
                      } else if (popupErr.code === 'auth/popup-blocked' || popupErr.code === 'auth/cancelled-popup-request') {
                        await signInWithRedirect(auth, provider);
                        return;
                      } else {
                        throw popupErr;
                      }
                    }

                    if (user) {
                      const resolvedName = extractFirstName(user);
                      const resolvedAvatar = user.photoURL || DEFAULT_YELLOW_AVATAR;

                      setUser(user);
                      setIsLoggedIn(true);
                      analyticsUserIdRef.current = user.uid;
                      setUserName(resolvedName);
                      setAvatarUrl(resolvedAvatar);

                      localStorage.setItem(`user_name_${user.uid}`, resolvedName);
                      localStorage.setItem(`user_avatar_${user.uid}`, resolvedAvatar);

                      await setDoc(doc(db, 'users', user.uid), {
                        email: user.email || '',
                        firstName: resolvedName,
                        avatarUrl: resolvedAvatar,
                        createdAt: serverTimestamp()
                      }, { merge: true });
                    }

                    setShowAudioMemberGate(false);
                  } catch (err: any) {
                    console.error('Google sign-in error (audio gate):', err);
                    setAudioMemberSignInError(err.message || 'Error signing in with Google.');
                  } finally {
                    setTimeout(() => localStorage.removeItem('oauth_in_flight'), 3000);
                    setIsAudioMemberSigningIn(false);
                  }
                }}
                disabled={isAudioMemberSigningIn}
                className="w-full py-3.5 bg-primary text-bg-dark font-black uppercase tracking-widest text-xs rounded-xl hover:brightness-110 transition-all flex items-center justify-center gap-2 shadow-lg shadow-primary/20"
              >
                {isAudioMemberSigningIn ? <Loader2 size={16} className="animate-spin" /> : <Headphones size={16} />}
                <span>SIGN UP FREE</span>
              </button>

              <p className="text-[10px] text-white/40 mt-3">
                Free membership — just sign in with Google. No credit card needed.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showMemberPerksModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-sm bg-surface-dark/95 border border-primary/30 rounded-3xl p-6 shadow-2xl shadow-black/80 flex flex-col items-center text-center relative overflow-hidden backdrop-blur-xl"
            >
              {/* Close button */}
              <button
                onClick={() => {
                  setShowMemberPerksModal(false);
                  setShowFeedback(true);
                }}
                className="absolute top-4 right-4 text-white/50 hover:text-white p-1.5 rounded-full bg-white/10"
              >
                <X size={18} />
              </button>

              {/* Header Badge */}
              <div className="w-16 h-16 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mb-3 text-primary">
                <Sparkles size={32} />
              </div>

              <span className="text-[10px] font-black uppercase tracking-widest text-primary bg-primary/10 px-3 py-1 rounded-full border border-primary/20 mb-2">
                JOIN COMENIUS CLUB
              </span>

              <h2 className="text-xl font-serif font-bold text-white mb-2">
                Unlock Member Perks
              </h2>

              <p className="text-xs text-white/70 mb-5 px-2 leading-relaxed">
                Create a free account to track your museum journey, collect rewards, and access exclusive perks across all partner venues.
              </p>

              <div className="w-full space-y-3">
                <button
onClick={async () => {
                      setIsClaiming(true);
                      localStorage.setItem('oauth_in_flight', '1');
                      try {
                        const provider = new GoogleAuthProvider();
                        let user = auth.currentUser;

                        try {
                          // Popup-primary on all platforms
                          if (user && user.isAnonymous) {
                            const res = await linkWithPopup(user, provider);
                            user = res.user;
                          } else {
                            const res = await signInWithPopup(auth, provider);
                            user = res.user;
                          }
                        } catch (popupErr: any) {
                          if (popupErr.code === 'auth/credential-already-in-use') {
                            const res = await signInWithPopup(auth, provider);
                            user = res.user;
                          } else if (popupErr.code === 'auth/popup-blocked' || popupErr.code === 'auth/cancelled-popup-request') {
                            await signInWithRedirect(auth, provider);
                            return;
                          } else {
                            throw popupErr;
                          }
                        }

                        if (user) {
                          const resolvedName = extractFirstName(user);
                          const resolvedAvatar = user.photoURL || DEFAULT_YELLOW_AVATAR;

                          setUser(user);
                          setIsLoggedIn(true);
                          analyticsUserIdRef.current = user.uid;
                          setUserName(resolvedName);
                          setAvatarUrl(resolvedAvatar);

                          localStorage.setItem(`user_name_${user.uid}`, resolvedName);
                          localStorage.setItem(`user_avatar_${user.uid}`, resolvedAvatar);

                          await setDoc(doc(db, 'users', user.uid), {
                            email: user.email || '',
                            firstName: resolvedName,
                            avatarUrl: resolvedAvatar,
                            createdAt: serverTimestamp()
                          }, { merge: true });
                        }
                        setShowMemberPerksModal(false);
                        setShowFeedback(true);
                      } catch (err) {
                        console.error('Google sign in error:', err);
                      } finally {
                        setTimeout(() => localStorage.removeItem('oauth_in_flight'), 3000);
                        setIsClaiming(false);
                      }
                    }}
                  disabled={isClaiming}
                  className="w-full py-3 bg-white text-slate-900 font-bold text-xs rounded-xl hover:bg-slate-100 transition-all flex items-center justify-center gap-2 shadow-md"
                >
                  {isClaiming ? <Loader2 size={16} className="animate-spin" /> : (
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
                    </svg>
                  )}
                  <span>1-Tap Sign Up with Google</span>
                </button>

                <button
                  onClick={() => {
                    setShowMemberPerksModal(false);
                    setShowFeedback(true);
                  }}
                  className="w-full py-3 bg-white/5 hover:bg-white/10 text-white/60 font-semibold text-xs rounded-xl transition-all border border-white/10"
                >
                  Maybe Later — Continue to Feedback
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showFeedback && (
          <FeedbackPopup
            onClose={() => {
              setShowFeedback(false);
              setToursInProgress(prev => {
                const next = new Set(prev);
                next.delete(selectedExperienceId);
                return next;
              });
              navigateTo('museum_detail', -1);
            }}
            onSubmit={(feedbackData) =>
              logAnalyticsEvent('feedback_submit', feedbackData)
            }
          />
        )}
      </AnimatePresence>

      {arSceneId && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25 }}
          className="absolute inset-0 z-50 bg-black flex flex-col">
          <div className="absolute right-4 z-50" style={{ top: 'calc(1rem + env(safe-area-inset-top, 0px))' }}>
            <button 
              onClick={() => {
                setArSceneId(null);
                setArMode(null);
              }} 
              className="w-10 h-10 rounded-full bg-black/60 border border-white/20 text-white flex items-center justify-center active:scale-90 transition-transform"
            >
              <X size={20} />
            </button>
          </div>
          <iframe
            src={`/viewer?scene=${arSceneId}&storyboard=${selectedExperienceId}&origin=marketplace&mid=${selectedMuseumId}${arMode ? `&mode=${arMode}` : ''}`}
            className="w-full h-full border-none"
            allow="camera *; microphone *; xr-spatial-tracking *; gyroscope *; accelerometer *; autoplay *"
          />
        </motion.div>
      )}
      {isLandscape && (
        <div className="fixed inset-0 z-[100] bg-[#0a1628] flex flex-col items-center justify-center text-white">
          <Smartphone size={64} className="rotate-90 mb-6 text-primary" />
          <h2 className="text-xl font-semibold mb-2">Rotate to Portrait</h2>
          <p className="text-sm text-on-surface-variant">Please rotate your device for the best experience</p>
        </div>
      )}
    </div>
    </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}
